import { ALL_WHERE_FILTER_OPS, ANONYMOUS_USER_ID, ANONYMOUS_USER_IDS, CANONICAL_TO_REST, DEFAULT_DATA_SOURCE_KEY, DEFAULT_LIST_LIMIT, DEFAULT_TENANT_BYPASS_ROLES, EntityReference, EntityRelation, JUNCTION_PIVOT_KEY, LIST_OPS, MAX_INCLUDE_DEPTH, NULL_OPS, REST_TO_CANONICAL, RLS_IS_ANONYMOUS_SQL, RLS_JWT_SQL, RLS_ROLES_SQL, RLS_UID_SQL, RebaseApiError, getDataSourceCapabilities, getDeclaredSubcollections, isAnonymousUid, isManyToMany, isPostgresCollectionConfig, isRelationAggregateSort, isRelationalCollectionConfig, isTenantClaimSource, isUnsupported, policy, resolveResourceRefs, rewriteLegacyRlsFunctions, sortKeyToString, toCanonicalOp, unsupportedMethod } from "@rebasepro/types";
import { deepClone, firstFreeKey, generateForeignKeyName, getIn, getPolicyNamesForRules, getPolicyOperations, isDefaultFieldConfigId, mergeDeep, prettifyIdentifier, randomString, removeFunctions, toSnakeCase, toWireKey } from "@rebasepro/utils";
import jsonLogic from "json-logic-js";
import { deepEqual } from "fast-equals";
//#region src/util/common.ts
var DEFAULT_ONE_OF_TYPE = "type";
var DEFAULT_ONE_OF_VALUE = "value";
//#endregion
//#region src/util/entities.ts
function isPropertyBuilder(property) {
	return typeof property?.dynamicProps === "function";
}
/**
* What a form opens with: a value for every property it can write.
*
* `excludeFromApi` columns are left out, and that is the whole of the rule —
* they are not part of the API surface in either direction, so there is nothing
* for a form to open showing and nothing it may send back. Including them was
* not cosmetic: the baseline is what gets submitted, so a new record carried
* `passwordHash: null` and `emailVerificationToken: null` into the create, and
* the server refused the whole write with "these columns are the server's to
* set" — the users collection could not be added to from the panel at all. The
* fields were invisible on screen (`admin.disabled.hidden`), which is what made
* the error read as being about the roles the operator *had* just edited.
*
* Server-side defaulting does not come through here: `applyDefaultValuesOnCreate`
* asks each property for its own default, so an excluded column with a declared
* `defaultValue` is still filled in on an in-process write.
*/
function getDefaultValuesFor(properties) {
	if (!properties) return {};
	return Object.entries(properties).map(([key, property]) => {
		if (!property) return {};
		if (property.excludeFromApi) return {};
		const value = getDefaultValueFor(property);
		return value === void 0 ? {} : { [key]: value };
	}).reduce((a, b) => ({
		...a,
		...b
	}), {});
}
function getDefaultValueFor(property) {
	if (!property) return void 0;
	if (isPropertyBuilder(property)) return void 0;
	if (property.defaultValue !== void 0) return property.defaultValue;
	else if (property.type === "map" && property.properties) {
		const defaultValuesFor = getDefaultValuesFor(property.properties);
		if (Object.keys(defaultValuesFor).length === 0) return void 0;
		return defaultValuesFor;
	} else return getDefaultValueFortype(property.type);
}
function getDefaultValueFortype(type) {
	if (type === "string") return null;
	else if (type === "number") return null;
	else if (type === "boolean") return false;
	else if (type === "date") return null;
	else if (type === "array") return [];
	else if (type === "map") return {};
	else if (type === "vector") return null;
	else if (type === "binary") return null;
	else return null;
}
/**
* Update the automatic values in a entity before save
* @group Driver
*/
function updateDateAutoValues({ inputValues, properties, status, timestampNowValue }) {
	return traverseValuesProperties(inputValues, properties, (inputValue, property) => {
		if (property.type === "date") if (status === "existing" && property.autoValue === "on_update") return timestampNowValue;
		else if ((status === "new" || status === "copy") && (property.autoValue === "on_update" || property.autoValue === "on_create")) return timestampNowValue;
		else return inputValue;
		else return inputValue;
	}) ?? {};
}
/**
* Stamp the acting user's uid into the `user_on_create` / `user_on_update`
* columns a collection declares.
*
* A deliberate sibling of {@link updateDateAutoValues} rather than another
* branch inside it. The two share a shape and nothing else: one takes an
* instant the server generates and the other takes an identity the request
* carries, so overloading the timestamp function would have meant threading a
* second, unrelated argument through every one of its callers and letting a
* `date` property and a `string` property compete for the same `autoValue`
* union. Called side by side in the driver.
*
* The stamped value overwrites whatever arrived in the body. A caller who can
* set `createdBy` is a caller who can attribute their write to somebody else,
* which is the one thing an audit column must not allow.
*
* `uid` is `undefined` for an anonymous request, a service token or an
* in-process write; the column is set to an explicit `null` there. Explicit
* matters on an update: leaving the key absent would keep whatever uid the
* column already held, so an anonymous edit would be recorded as the previous
* editor's. Refusing that write outright is `required`'s job, not this
* function's — see `assertWriteValuesValid`.
*
* Top-level properties only, deliberately, unlike {@link updateDateAutoValues}.
* `traverseValuesProperties` cannot express "set this key to null" — a `null`
* from its operation means "leave the key out" — and an audit column nested
* inside a `map` is not a column at all, so there is nothing down there to
* stamp.
*
* @group Driver
*/
function updateUserAutoValues({ inputValues, properties, status, uid }) {
	const result = { ...inputValues ?? {} };
	for (const [key, property] of Object.entries(properties ?? {})) {
		const prop = property;
		if (!prop || prop.type !== "string") continue;
		const autoValue = prop.autoValue;
		if (autoValue !== "user_on_create" && autoValue !== "user_on_update") continue;
		if (status === "existing" && autoValue === "user_on_create") continue;
		result[key] = uid ?? null;
	}
	return result;
}
/**
* Fill in the `defaultValue`s a create left unset.
*
* `defaultValue` was read by exactly one thing: the Studio's form, which uses it
* to prefill inputs. Every other way into the same collection — the REST create,
* the SDK, the socket, an import — stored whatever arrived and nothing where the
* key was absent. So `active: { type: "boolean", defaultValue: true }` produced
* rows with `active` unset through the API and `true` through the panel, from
* one declaration that reads like a promise about the data.
*
* Only genuinely absent keys are filled. An explicit `null` is a caller saying
* "no value", which is a different statement from not mentioning the field, and
* overwriting it would make the default impossible to opt out of.
*
* `getDefaultValuesFor` also invents a per-type default for properties with no
* `defaultValue` at all (`false` for a boolean, `[]` for an array, `null` for
* the rest) — right for a form, which must render *something* in every input,
* and wrong here, where an absent key must stay absent so the column's own
* DEFAULT applies. Only declared defaults are taken.
*
* @param values the caller's payload
* @param properties the collection's declared properties
* @group Driver
*/
function applyDefaultValuesOnCreate(values, properties) {
	if (!properties) return values ?? {};
	const result = { ...values ?? {} };
	for (const [key, property] of Object.entries(properties)) {
		if (!property) continue;
		if (!declaresDefault(property)) continue;
		const defaultValue = getDefaultValueFor(property);
		if (result[key] !== void 0) {
			if (property.type === "map" && property.defaultValue === void 0 && isPlainObject(result[key])) result[key] = {
				...defaultValue ?? {},
				...result[key]
			};
			continue;
		}
		if (defaultValue !== void 0) result[key] = defaultValue;
	}
	return result;
}
/** Does this property, or something nested under it, state a `defaultValue`? */
function declaresDefault(property) {
	if (isPropertyBuilder(property)) return false;
	if (property.defaultValue !== void 0) return true;
	if (property.type === "map" && property.properties) return Object.values(property.properties).some((child) => child && declaresDefault(child));
	return false;
}
function isPlainObject(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
* Add missing required fields, expected in the collection, to the values of a entity
* @param values
* @param properties
* @group Driver
*/
function sanitizeData(values, properties) {
	const result = values;
	Object.entries(properties).forEach(([key, property]) => {
		if (values && values[key] !== void 0) result[key] = values[key];
		else if (property.validation?.required) result[key] = null;
	});
	return result;
}
function getReferenceFrom(entity) {
	if (typeof entity.id !== "string") throw new Error("Only string IDs are supported in references");
	return new EntityReference({
		id: entity.id,
		path: entity.path,
		driver: entity.driver,
		databaseId: entity.databaseId
	});
}
function getRelationFrom(entity) {
	return new EntityRelation(entity.id, entity.path, entity);
}
/**
* Normalize a value into a proper EntityRelation instance.
* Handles EntityRelation class instances, and plain objects
* with `__type === "relation"` or an `isEntityRelation()` method.
*
* When `propertyType` is `"relation"`, also accepts plain objects that
* have `id` and `path` fields — these are relation-shaped objects from
* edge cases in the data pipeline (REST fallback, stale cache, custom data source).
*
* When `targetPath` is given, also accepts a bare id. A relation column is a
* foreign key, and the REST layer returns it as the scalar it is; only some
* fetch paths hydrate it into an object. Which form a caller sees therefore
* depends on how the row was loaded, and a caller that only accepted objects
* reported half of its own data as a type error. The declared target is the
* missing half: with it, an id is a relation that has not been fetched yet.
*
* Returns null if the value cannot be coerced.
*/
function normalizeToEntityRelation(value, propertyType, targetPath) {
	if (value instanceof EntityRelation) return value;
	if (targetPath && (typeof value === "string" || typeof value === "number")) {
		if (value === "") return null;
		return new EntityRelation(value, targetPath);
	}
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	const obj = value;
	if (!(obj.__type === "relation" || obj.__type === "reference" || typeof obj.isEntityRelation === "function" && obj.isEntityRelation() || typeof obj.isEntityReference === "function" && obj.isEntityReference() || propertyType === "relation" && typeof obj.id !== "undefined" && typeof obj.path === "string")) return null;
	return new EntityRelation(obj.id, obj.path, obj.data);
}
function traverseValuesProperties(inputValues, properties, operation) {
	const safeInputValues = inputValues ?? {};
	const result = mergeDeep(safeInputValues, Object.entries(properties).map(([key, property]) => {
		const updatedValue = traverseValueProperty(safeInputValues && safeInputValues[key], property, operation);
		if (updatedValue === null) return null;
		if (updatedValue === void 0) return void 0;
		return { [key]: updatedValue };
	}).reduce((a, b) => ({
		...a,
		...b
	}), {}));
	if (!result || Object.keys(result).length === 0) return void 0;
	return result;
}
function traverseValueProperty(inputValue, property, operation) {
	let value;
	if (property.type === "map" && property.properties) value = traverseValuesProperties(inputValue, property.properties, operation);
	else if (property.type === "array") {
		const of = property.of;
		if (of && Array.isArray(inputValue) && !Array.isArray(of)) value = inputValue.map((e) => traverseValueProperty(e, of, operation));
		else if (of && Array.isArray(inputValue) && Array.isArray(of)) value = inputValue.map((e, i) => {
			if (i < of.length) return traverseValueProperty(e, of[i], operation);
			return null;
		}).filter(Boolean);
		else if (property.oneOf && Array.isArray(inputValue)) {
			const typeField = property.oneOf?.typeField ?? "type";
			const valueField = property.oneOf?.valueField ?? "value";
			value = inputValue.map((e) => {
				if (e === null) return null;
				if (typeof e !== "object") return e;
				const rec = e;
				const type = rec[typeField];
				const childProperty = property.oneOf?.properties[type];
				if (!type || !childProperty) return e;
				return {
					[typeField]: type,
					[valueField]: traverseValueProperty(rec[valueField], childProperty, operation)
				};
			});
		} else value = inputValue;
	} else value = operation(inputValue, property);
	return value;
}
/**
* Create a lightweight relation stub for admin views.
* Replaces inline `{ id, path, __type: "relation" }` object literals.
*/
function createRelationRef(id, path) {
	return {
		id,
		path,
		__type: "relation"
	};
}
/**
* Create a hydrated relation reference that includes the full entity data.
* Used when entity data has been pre-fetched (e.g., via batch loading or JOINs).
*/
function createRelationRefWithData(id, path, data) {
	return {
		id,
		path,
		__type: "relation",
		data
	};
}
//#endregion
//#region src/util/collections.ts
function sortProperties(properties, propertiesOrder) {
	try {
		const propertiesKeys = Object.keys(properties);
		if (!propertiesOrder || propertiesOrder.length === 0) return propertiesKeys.map((key) => {
			const property = properties[key];
			if (!isPropertyBuilder(property) && property?.type === "map" && property.properties) return { [key]: {
				...property,
				properties: sortProperties(property.properties, property.propertiesOrder)
			} };
			else return { [key]: property };
		}).reduce((a, b) => ({
			...a,
			...b
		}), {});
		const validOrderKeys = propertiesOrder.filter((key) => {
			return !key.includes(".") && properties[key];
		});
		const processedKeys = new Set(validOrderKeys);
		const orderedResult = validOrderKeys.map((key) => {
			const property = properties[key];
			if (!isPropertyBuilder(property) && property?.type === "map" && property.properties) return { [key]: {
				...property,
				properties: sortProperties(property.properties, property.propertiesOrder)
			} };
			else return { [key]: property };
		}).reduce((a, b) => ({
			...a,
			...b
		}), {});
		const missingProperties = propertiesKeys.filter((key) => !processedKeys.has(key)).map((key) => {
			const property = properties[key];
			if (!isPropertyBuilder(property) && property?.type === "map" && property.properties) return { [key]: {
				...property,
				properties: sortProperties(property.properties, property.propertiesOrder)
			} };
			else return { [key]: property };
		}).reduce((a, b) => ({
			...a,
			...b
		}), {});
		return {
			...orderedResult,
			...missingProperties
		};
	} catch (e) {
		console.error("Error sorting properties", e);
		return properties;
	}
}
/**
* A copy of `collections` ordered by slug.
*
* Every generator that turns collections into a file is order-dependent, and
* every one of them is compared against its own output — `rebase doctor`
* regenerates in memory and diffs, `generate-sdk && git diff --exit-code` gates
* CI. While only the *writers* sorted, a project whose `readdirSync` order
* differed from its slug order was reported permanently out of date, and the
* fix the message printed rewrote the file in the order it was already in. The
* generators sort themselves now, so no caller can get this wrong.
*
* A slug-less collection is left to the generator's own validation, which names
* the offending collection; sorting must not throw first.
*/
function sortCollectionsBySlug(collections) {
	return [...collections].sort((a, b) => (a.slug ?? "").localeCompare(b.slug ?? ""));
}
function getPrimaryKeys(collection) {
	const properties = collection.properties;
	if (!properties) return ["id"];
	const ids = Object.entries(properties).filter(([key, prop]) => typeof prop === "object" && prop !== null && "isId" in prop && Boolean(prop.isId)).map(([key]) => key);
	if (ids.length > 0) return ids;
	return ["id"];
}
//#endregion
//#region src/util/identity.ts
/** Separator between the parts of a composite address. */
var COMPOSITE_ID_SEPARATOR = ":::";
/** The eight-four-four-four-twelve shape of a UUID, any version. */
var UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Whether one address part can be a value of the column it addresses. */
function partIsAddressable(part, pk) {
	if (pk.isUUID) return UUID_PATTERN.test(String(part));
	if (pk.type === "number") return typeof part === "number" ? Number.isFinite(part) : !isNaN(parseInt(String(part), 10));
	return true;
}
/**
* Whether an address could name a row at all, before asking the database.
*
* A `uuid` column cannot hold `"new"`, and an `integer` column cannot hold
* `"abc"` — so the answer to "which row is this" is "none", and that is a 404,
* not a failure. Postgres cannot say so politely: the comparison never runs, it
* raises `22P02` and aborts the enclosing transaction, after which every
* further statement returns the far less helpful `25P02`.
*
* `isUUID` must come from the column, not from `isId: "uuid"` in a config: the
* config is a claim about a key, and a `text` column that holds ids of some
* other shape is a working app this must not start rejecting.
*/
function isAddressableId(idValue, primaryKeys) {
	if (primaryKeys.length === 0) return false;
	if (primaryKeys.length === 1) return partIsAddressable(idValue, primaryKeys[0]);
	const parts = String(idValue).split(":::");
	if (parts.length !== primaryKeys.length) return false;
	return parts.every((part, i) => partIsAddressable(part, primaryKeys[i]));
}
/**
* Derive a row's address from its key columns.
*
* Single key → the value as a string. Composite → each part joined by
* {@link COMPOSITE_ID_SEPARATOR}, in primary-key order, which is what
* {@link parseIdValues} expects to invert.
*/
function buildCompositeId(values, primaryKeys) {
	if (primaryKeys.length === 0) return "";
	if (primaryKeys.length === 1) return String(values[primaryKeys[0].fieldName] ?? "");
	return primaryKeys.map((pk) => String(values[pk.fieldName] ?? "")).join(":::");
}
/**
* Invert {@link buildCompositeId}: turn an address back into key columns, each
* coerced to the type its column actually round-trips as.
*
* This is the boundary where a URL segment becomes a query parameter, so a
* malformed address must throw rather than silently produce a query that
* matches the wrong row (or none).
*/
function parseIdValues(idValue, primaryKeys) {
	const result = {};
	if (primaryKeys.length === 0) return result;
	if (primaryKeys.length === 1) {
		const pk = primaryKeys[0];
		if (pk.type === "number" && !pk.isUUID) {
			const parsed = typeof idValue === "number" ? idValue : parseInt(String(idValue), 10);
			if (isNaN(parsed)) throw new Error(`Invalid numeric ID: ${idValue}`);
			result[pk.fieldName] = parsed;
		} else result[pk.fieldName] = String(idValue);
		return result;
	}
	const parts = String(idValue).split(":::");
	if (parts.length !== primaryKeys.length) throw new Error(`Composite ID parts mismatch. Expected ${primaryKeys.length}, got ${parts.length} for ID: ${idValue}`);
	for (let i = 0; i < primaryKeys.length; i++) {
		const pk = primaryKeys[i];
		const val = parts[i];
		if (pk.type === "number" && !pk.isUUID) {
			const parsed = parseInt(val, 10);
			if (isNaN(parsed)) throw new Error(`Invalid numeric ID component: ${val}`);
			result[pk.fieldName] = parsed;
		} else result[pk.fieldName] = val;
	}
	return result;
}
/**
* The primary keys of a collection, as declared by its properties.
*
* This is the only tier both sides can read, because it is the only one written
* in the config: the postgres driver can also infer keys from the Drizzle
* schema, which the browser never sees and is never sent — the admin compiles
* the collection files into its own bundle rather than being served them. A key
* that lives only in the Drizzle schema is therefore invisible here, and the
* server says so at boot (`warnOnKeysTheAdminCannotResolve`) naming the `isId`
* to add.
*
* Returns an empty array when a collection declares none, which callers must
* treat as "not addressable" rather than defaulting to `id`: guessing a key
* that is not the real one produces confidently wrong addresses.
*/
function getDeclaredPrimaryKeys(collection) {
	const properties = collection.properties;
	if (!properties) return [];
	const keys = [];
	for (const [fieldName, propRaw] of Object.entries(properties)) {
		const prop = propRaw;
		if (!prop || typeof prop !== "object") continue;
		if (!("isId" in prop) || !prop.isId) continue;
		keys.push({
			fieldName,
			type: prop.type === "number" ? "number" : "string",
			isUUID: prop.isId === "uuid"
		});
	}
	return keys;
}
/**
* The keys to address a collection's rows with, resolved the way the driver
* resolves them — minus the tier the browser cannot reach.
*
* The postgres driver tries, in order: properties marked `isId`; the primary
* keys of the Drizzle schema; and finally a column literally named `id`. Only
* the first and last are visible in a `CollectionConfig`, which is what both
* sides share.
*
* So the two agree except on a collection that declares no `isId` and whose key
* is known only to Drizzle. There, the driver reads the real key, and this
* either resolves nothing (reported to the console by the caller) or — if the
* table happens to have an unrelated `id` property — resolves `id`, which is
* the wrong key and cannot be detected from here: the addresses look right and
* route wrong. Only the config can settle it, so the server names both cases
* at boot (`warnOnKeysTheAdminCannotResolve`) with the `isId` to add.
*/
function resolvePrimaryKeys(collection) {
	const declared = getDeclaredPrimaryKeys(collection);
	if (declared.length > 0) return declared;
	const idProp = collection.properties?.id;
	if (idProp && typeof idProp === "object") return [{
		fieldName: "id",
		type: idProp.type === "number" ? "number" : "string"
	}];
	return [];
}
//#endregion
//#region src/util/email.ts
/**
* Email normalization — one implementation, because the database enforces it.
*
* `ensureAuthTablesExist` puts a `UNIQUE INDEX ON users (lower(email))` on the
* auth table. That index decides what "the same address" means, and it does not
* trim: to Postgres, `' foo@bar.com'` and `'foo@bar.com'` are two addresses and
* both may exist. So every write that reaches the column has to agree with
* every read, exactly, or the two disagree in the one direction that matters —
* a row that exists and cannot be found.
*
* That is not hypothetical. The lookup path trimmed and the admin create paths
* did not, so a user created through `POST /api/data/users` or
* `POST /api/auth/admin/users` with a stray space was stored untrimmed,
* survived the unique index alongside the real address, and was unreachable by
* login forever after. The HTTP auth routes were unaffected only because Zod's
* `.email()` happens to reject surrounding whitespace — a guard on a different
* layer, for a different reason, that the admin paths do not sit behind.
*
* It lives in `common` because `server`, `server-postgres` and `server-mongo`
* all write this column and must agree exactly, and `common` is the only
* package all three already depend on.
*/
/**
* Canonical form of an email address: trimmed, lower-cased.
*
* Non-strings pass through untouched, so this is safe to apply to a value out
* of a partial update payload whose type is not known yet.
*/
function normalizeEmail(email) {
	return typeof email === "string" ? email.trim().toLowerCase() : email;
}
//#endregion
//#region src/util/enums.ts
function enumToObjectEntries(enumValues) {
	if (Array.isArray(enumValues)) return enumValues;
	else return Object.entries(enumValues).map(([id, value]) => {
		if (typeof value === "string") return {
			id,
			label: value
		};
		else return {
			...value,
			id
		};
	});
}
function getLabelOrConfigFrom(enumValues, key) {
	if (key === null || key === void 0) return void 0;
	return enumValues.find((entry) => String(entry.id) === String(key));
}
//#endregion
//#region src/util/paths.ts
var COLLECTION_PATH_SEPARATOR = "::";
/**
* Remove the entity ids from a given path
* `products/B44RG6APH/locales` => `products::locales`
* @param path
*/
function stripCollectionPath(path) {
	return segmentsToStrippedPath(fullPathToCollectionSegments(path));
}
function segmentsToStrippedPath(paths) {
	if (paths.length === 1) return paths[0];
	return paths.reduce((a, b) => `${a}::${b}`);
}
/**
* Extract the collection path routes
* `products/B44RG6APH/locales` => [`products`, `locales`]
* @param path
*/
function fullPathToCollectionSegments(path) {
	return path.split("/").filter((e, i) => i % 2 === 0);
}
//#endregion
//#region src/util/resolve-relation.ts
/**
* Fill in a relation's defaults.
*
* This replaces `sanitizeRelation`, which had to work out *which kind of link
* you meant* from whichever optional fields happened to be set — 194 lines of
* it, including a pass that inspected the target collection's own relations to
* decide whether a `many`/`inverse` pair was a one-to-many or the far side of a
* many-to-many, wrapped in a `try/catch` that fell through to the wrong answer
* when it could not tell. Two consumers running that logic at different moments
* could reach different conclusions about the same relation.
*
* With the kind declared there is nothing to work out. What remains is
* defaulting — a table name, a column name — which is deterministic, depends
* only on the relation and its two endpoints, and cannot fail. That is why this
* function returns rather than throws, and why it needs no cache to be
* consistent.
*/
function resolveRelation(relation, sourceCollection, propertyKey) {
	const target = relation.target;
	if (typeof target !== "function") throw new Error(`Relation${relation.relationName ? ` '${relation.relationName}'` : ""} on '${sourceCollection.slug}' has no \`target\`. Give it a thunk: \`target: () => otherCollection\`.`);
	const targetCollection = callTarget(relation, sourceCollection, propertyKey, target);
	const relationName = relation.relationName ?? propertyKey ?? toSnakeCase(targetCollection.slug);
	const shared = {
		relationName,
		target: () => unwrapModuleNamespace(target()),
		targetSlug: targetCollection.slug,
		onUpdate: relation.onUpdate,
		onDelete: relation.onDelete,
		overrides: relation.overrides
	};
	const sourceName = toSnakeCase(sourceCollection.slug ?? sourceCollection.name);
	switch (relation.kind) {
		case "belongsTo": return {
			...shared,
			kind: "belongsTo",
			cardinality: "one",
			writable: true,
			shared: false,
			localKey: relation.localKey ?? generateForeignKeyName(relationName)
		};
		case "hasOne": return {
			...shared,
			kind: "hasOne",
			cardinality: "one",
			writable: true,
			shared: false,
			foreignKeyOnTarget: relation.foreignKeyOnTarget ?? generateForeignKeyName(sourceName),
			sourceKey: relation.sourceKey
		};
		case "hasMany": return {
			...shared,
			kind: "hasMany",
			cardinality: "many",
			writable: true,
			shared: false,
			foreignKeyOnTarget: relation.foreignKeyOnTarget ?? generateForeignKeyName(sourceName),
			sourceKey: relation.sourceKey
		};
		case "manyToMany": {
			const sourceTable = getTableName(sourceCollection);
			const targetTable = getTableName(targetCollection);
			return {
				...shared,
				kind: "manyToMany",
				cardinality: "many",
				writable: true,
				shared: true,
				through: {
					table: relation.through?.table ?? [sourceTable, targetTable].sort().join("_"),
					sourceColumn: relation.through?.sourceColumn ?? generateForeignKeyName(sourceName),
					targetColumn: relation.through?.targetColumn ?? generateForeignKeyName(relationName),
					properties: relation.through?.properties ?? {}
				}
			};
		}
		case "via": return {
			...shared,
			kind: "via",
			cardinality: relation.cardinality,
			writable: false,
			shared: true,
			joinPath: relation.joinPath
		};
		default: throw new Error(`Unknown relation kind: ${JSON.stringify(relation)}`);
	}
}
/** How this relation is addressed in an error message, before it has a resolved name. */
function describe(relation, sourceCollection, propertyKey) {
	const name = relation.relationName ?? propertyKey;
	return `Relation${name ? ` '${name}'` : ""} on '${sourceCollection.slug}'`;
}
/**
* A module namespace, unwrapped to the collection it exports.
*
* A cycle transpiled to CommonJS does not hand the importing module the
* *default export* — it hands it the module object, `{ __esModule: true,
* default: … }`, captured before the exporting module finished evaluating. The
* `default` slot fills in later, so by the time a lazy `target` thunk runs the
* collection is sitting right there, one level down. Returning the namespace is
* never a thing a thunk means to do, and there is exactly one reading of it.
*
* Only unwrapped when the inner value is itself a collection: a `default` that
* is not one is a genuinely wrong thunk, and it should reach the error below
* rather than be quietly swapped in.
*/
function unwrapModuleNamespace(value) {
	if (!value || typeof value !== "object") return value;
	if (value.slug) return value;
	const inner = value.default;
	return inner && typeof inner === "object" && inner.slug ? inner : value;
}
/**
* Call the `target` thunk, and translate the ways an import cycle breaks it into
* an error that names the cause — or, where the value is recoverable, into the
* collection the thunk meant.
*
* The thunk exists to defer the reference until every module has finished
* evaluating, and for a cycle that closes at import time it does. Two cycles
* leave the binding permanently unusable:
*
*  - **ESM/TDZ.** `const` and `class` bindings in a not-yet-evaluated module are
*    in the temporal dead zone, so reading one throws `ReferenceError: x is not
*    defined`. The stack points at the thunk — a one-line arrow function that is
*    obviously fine — and says nothing about the cycle that made it throw.
*  - **CJS interop, unresolved.** The half-initialised module object has no
*    `default` yet, the import resolves to `undefined`, and the thunk returns it
*    without complaint. That one used to surface here as "did not resolve to a
*    collection", which is true and unhelpful.
*
* Both mean the same thing, and the fix for both is the same: break the cycle,
* or move the relation into the collection that does not close it.
*
* A third shape is *not* an error, and used to be reported as one. A loader that
* transpiles ESM to CJS — jiti, which is what `rebase generate-sdk` and
* `rebase build` load collections with — gives the module entered second in a
* cycle a namespace object rather than the default export, and never replaces it
* with a live binding. The thunk then returns `{ __esModule: true, default: … }`
* holding the fully-initialised collection. Native ESM resolves the same thunk
* to the collection directly, so this was a loader artefact reported as an
* authoring mistake, and the advice it gave — make the target a lazy thunk — was
* already satisfied by the code it was rejecting. Bidirectional relations make
* these cycles unavoidable, and the lazy thunk is this framework's own answer to
* them, so {@link unwrapModuleNamespace} takes the collection and moves on.
*/
function callTarget(relation, sourceCollection, propertyKey, target) {
	let targetCollection;
	try {
		targetCollection = unwrapModuleNamespace(target());
	} catch (error) {
		if (error instanceof ReferenceError) throw new Error(`${describe(relation, sourceCollection, propertyKey)} targets a collection that is not initialized yet — almost always an import cycle between the two collection files. Break the cycle (move the shared piece into a third module, or import the target lazily) so the target's module finishes evaluating before the registry is built.`, { cause: error });
		throw error;
	}
	if (!targetCollection?.slug) throw new Error(`${describe(relation, sourceCollection, propertyKey)} has a \`target\` that resolved to ${targetCollection === void 0 ? "`undefined`" : "something that is not a collection"}. ` + (targetCollection === void 0 ? "Under CommonJS interop an import cycle resolves the default import to `undefined`, so check whether this collection and its target import each other. Otherwise the thunk is returning the wrong value — it must return the collection itself, not a promise or a module." : typeof targetCollection.then === "function" ? "The thunk returned a promise — `target: () => import(\"./other\")` is asynchronous. Import the collection at the top of the file and return the binding: `target: () => otherCollection`." : "The thunk must return a collection config with a `slug`."));
	return targetCollection;
}
//#endregion
//#region src/util/relations.ts
/**
* Whether the target rows are shared with other parents — a many-to-many, or a
* multi-hop `via` chain.
*
* Decides what a write "through" the relation may touch: a shared target
* belongs to every parent that links it, so the parent owns the *link* and not
* the row. The backend enforces that (an unlink rather than a delete) and the
* admin renders it (remove-from-parent rather than delete).
*
* Now a field on the resolved relation rather than a re-derivation, so both
* sides read the same answer instead of each computing one.
*/
function isJunctionBackedRelation(relation) {
	return relation.shared;
}
/** WeakMap cache — same collection instance always yields the same relation map. */
var _resolvedRelationsCache = /* @__PURE__ */ new WeakMap();
/**
* Every relation a collection declares, keyed by the name it is addressed by.
*
* A relation reaches the map from either of two places — the collection's
* `relations` array, or a `relation` property that declares one inline — and is
* keyed by its resolved `relationName`, which is what a nested path segment,
* an `include` key and an admin tab all match against.
*
* Resolution no longer swallows failures. It used to wrap each relation in a
* `try/catch` that dropped anything it could not work out, so a
* mis-declared relation silently vanished instead of being reported; with the
* kind declared, the only remaining failure is a `target` that does not resolve,
* which is worth hearing about.
*/
function resolveCollectionRelations(collection) {
	const cached = _resolvedRelationsCache.get(collection);
	if (cached) return cached;
	if (!isRelationalCollectionConfig(collection)) return {};
	const relations = {};
	for (const relation of collection.relations ?? []) {
		const resolved = resolveRelation(relation, collection);
		relations[resolved.relationName] = resolved;
	}
	for (const [propertyKey, property] of Object.entries(collection.properties ?? {})) {
		if (property?.type !== "relation") continue;
		const declared = property.relation;
		if (!declared || relations[propertyKey]) continue;
		relations[propertyKey] = resolveRelation(declared, collection, propertyKey);
	}
	_resolvedRelationsCache.set(collection, relations);
	return relations;
}
/**
* The `type: "relation"` property that declares a link, or `undefined` for one
* that only exists in the collection's `relations` array.
*
* Both declaration sites end up in {@link resolveCollectionRelations}, and only
* one of them has a property to carry field-level facts — `name`, `admin`, and
* the one this exists for, `validation.required`.
*/
function relationDeclaringProperty(collection, relation) {
	const resolved = resolveCollectionRelations(collection);
	for (const [key, raw] of Object.entries(collection.properties ?? {})) {
		const prop = raw;
		if (prop?.type !== "relation") continue;
		if (resolved[key] === relation) return prop;
		const addressed = prop.relation?.relationName;
		if (addressed && findRelation(resolved, addressed) === relation) return prop;
	}
}
/**
* Must every row of this collection point at a target through this link?
*
* Read from the declaring property's `validation.required` — the same key every
* other field uses, and the only place it lives.
*
* `RelationBase` carried its own `validation.required` until 0.18, which made
* this two questions rather than one. They were answered by different readers:
* the Postgres DDL generator asked the property (so the foreign-key column was
* `NOT NULL`) and the SDK type generator asked the relation (so the generated
* `Insert` type made the field optional). A `create()` that left the relation
* out therefore typechecked and then failed at the database with a not-null
* violation, and the two `required`s had to be written twice, identically, for
* the pair to agree.
*
* A relation with no declaring property — an entry in `relations` nothing
* points at — is not required. There is no field to fill in.
*/
function isRelationRequired(collection, relation) {
	return Boolean(relationDeclaringProperty(collection, relation)?.validation?.required);
}
/**
* The path of the collection a relation property points at, derived from the
* property alone.
*
* A preview holds a property and a value and no collection, so it cannot call
* `resolveRelationProperty`. It does not need to: both forms that carry a
* target — the stamped `resolvedRelation` and the inline `relation` — name it
* directly. Only the third form, a relation declared by name in the
* collection's `relations` array, is out of reach, and that one has no target
* to read without the collection anyway.
*
* This is what lets a preview render a relation column that arrived as a bare
* foreign key: the id says *which* row, the declared target says *which
* collection*, and `RelationPreview` fetches the rest. Without it a scalar id
* is indistinguishable from a value of the wrong type.
*/
function getRelationTargetPath(property) {
	const stamped = property.resolvedRelation?.targetSlug;
	if (stamped) return stamped;
	const target = property.relation?.target;
	if (typeof target !== "function") return void 0;
	try {
		return target()?.slug;
	} catch (_e) {
		return;
	}
}
/**
* The table a collection reads and writes.
*
* `table` when it is set, otherwise `toSnakeCase(slug)` — which is what made it
* safe to drop `table` from the required fields on the config type: the runtime
* had always derived it, and the type was demanding a value it did not need.
*
* The `||` chain is load-bearing. `toSnakeCase(undefined)` returns `""`, not
* `undefined`, so the previous `??` chain short-circuited on the empty string
* and the name fallback could never run — a safety net that read like one and
* caught nothing. It was unreachable while `slug` was required; it stops being
* unreachable the moment anything constructs a config without one.
*/
function getTableName(collection) {
	return (isRelationalCollectionConfig(collection) ? collection.table : void 0) || toSnakeCase(collection.slug) || toSnakeCase(collection.name);
}
/**
* A JavaScript identifier: what a generated `export const <name> =` needs.
*
* Deliberately the same shape the two schema generators already define
* privately — this is the third place that needed it, and the first two guard
* property keys and member accesses while nothing guarded the variable name
* itself.
*/
var JS_IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
/**
* The variable name a generated table is bound to.
*
* Camel-cases underscores, and then guarantees the result is a legal
* identifier. It did only the first, so a table name that is legal in Postgres
* and not in JavaScript produced a `schema.generated.ts` that does not parse:
*
*   `2024_archive`       → `export const 2024Archive = pgTable(…)`
*                          "An identifier or keyword cannot immediately follow
*                           a numeric literal"
*   `reporting.events`   → `export const reporting.events = pgTable(…)`
*                          "',' expected"
*
* That file is imported by the server, so the failure is not one broken
* collection — `rebase build` and `db push` fail at tsc for the whole
* directory. And it is reachable from a documented flow: `rebase init` against
* a database holding a table called `2024_archive` writes a collection file
* that parses and a schema file that does not.
*
* **A no-op for every name that already worked**, which is what makes changing
* a derived name safe here: the only inputs whose output changes are the ones
* that produced a syntax error, and nothing can be running against those.
* Separators become camel case rather than disappearing, so `reporting.events`
* and `reporting_events` do not collide into one variable.
*/
function getTableVarName(tableName) {
	const camel = tableName.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
	if (JS_IDENTIFIER.test(camel)) return camel;
	const sanitised = camel.replace(/[^A-Za-z0-9_$]+([A-Za-z0-9])?/g, (_, char) => char ? char.toUpperCase() : "").replace(/^([0-9])/, "t$1");
	return JS_IDENTIFIER.test(sanitised) ? sanitised : `t${sanitised}`;
}
function getEnumVarName(tableName, propName) {
	return `${getTableVarName(tableName)}${propName.charAt(0).toUpperCase() + propName.slice(1)}`;
}
function getColumnName(fullColumn) {
	return fullColumn.includes(".") ? fullColumn.split(".").pop() : fullColumn;
}
/**
* The field key a database column is served and addressed under.
*
* A column has two names and they are not the same name. `author_id` is what
* Postgres stores; `authorId` is the key on the JSON row, the key in the
* generated Drizzle table, and the key a caller writes in `where` and
* `orderBy`. Every place that starts from a column and has to reach a row, a
* Drizzle table or a payload goes through here, so there is one answer rather
* than one per call site — the two that disagreed put `displayName` and
* `author_id` on the same API.
*
* A declared property is the authority when there is one, because its key *is*
* the wire name and `columnName` is the only thing that ever renamed the
* column:
*
*  1. an explicit `columnName` equal to this column;
*  2. a property whose key is literally the column (an author who wrote
*     `author_id:` meant `author_id` on the wire, and gets it);
*  3. a property whose key snake-cases to the column, which is the default
*     mapping — `authorId` → `author_id`.
*
* With no property in the way — a foreign key derived from a relation, which
* usually has none — the name is derived: {@link toWireKey}.
*
* Note the fallback is *not* the column verbatim. That was the old behaviour
* and it is precisely the defect: a derived foreign key reached the wire under
* its column name while every hand-authored field beside it was camelCase.
*/
function fieldKeyForColumn(collection, column) {
	const properties = collection?.properties;
	if (properties) {
		for (const [key, prop] of Object.entries(properties)) {
			const columnName = prop?.columnName;
			if (typeof columnName === "string" && columnName === column) return key;
		}
		for (const key of Object.keys(properties)) {
			if (key === column) return key;
			if (toSnakeCase(key) === column) return key;
		}
	}
	return toWireKey(column);
}
/**
* Look up a relation by key with forgiving normalization.
*
* `resolveCollectionRelations` stores each relation under a single canonical
* key (no aliases). This helper tries the given key as-is, then falls back to
* slug form (underscores → hyphens) and snake_case form (hyphens → underscores)
* so that callers that receive a key from external input (URL path segments,
* user-provided config, etc.) can still find the right entry.
*/
function findRelation(resolvedRelations, key) {
	if (resolvedRelations[key]) return resolvedRelations[key];
	const slugKey = key.replace(/_/g, "-");
	if (slugKey !== key && resolvedRelations[slugKey]) return resolvedRelations[slugKey];
	const snakeKey = key.replace(/-/g, "_");
	if (snakeKey !== key && resolvedRelations[snakeKey]) return resolvedRelations[snakeKey];
}
//#endregion
//#region src/util/resolutions.ts
function resolveProperty(props) {
	const { property, ignoreMissingFields = false, ...rest } = props;
	let resultProperty;
	if (isPropertyBuilder(property)) {
		const path = rest.path;
		if (!path) resultProperty = property;
		else {
			const usedPropertyValue = rest.propertyKey ? getIn(rest.values, rest.propertyKey) : void 0;
			const dynamicProps = property.dynamicProps?.({
				...rest,
				path,
				propertyValue: usedPropertyValue,
				values: rest.values ?? {},
				previousValues: rest.previousValues ?? rest.values ?? {}
			});
			resultProperty = mergeDeep(property, dynamicProps ?? {});
		}
	} else resultProperty = property;
	if (resultProperty?.dynamicProps && rest.path) {
		const path = rest.path;
		const usedPropertyValue = rest.propertyKey ? getIn(rest.values, rest.propertyKey) : void 0;
		const dynamicPropsResult = resultProperty.dynamicProps({
			...rest,
			path,
			propertyValue: usedPropertyValue,
			values: rest.values ?? {},
			previousValues: rest.previousValues ?? rest.values ?? {}
		});
		if (dynamicPropsResult) resultProperty = mergeDeep(resultProperty, dynamicPropsResult);
	}
	let resolvedProperty;
	if (resultProperty?.type === "map" && resultProperty.properties) {
		const properties = resolveProperties({
			ignoreMissingFields,
			...rest,
			properties: resultProperty.properties
		});
		resolvedProperty = {
			...resultProperty,
			properties
		};
	} else if (resultProperty?.type === "array") resolvedProperty = resultProperty;
	else if ((resultProperty?.type === "string" || resultProperty?.type === "number") && resultProperty.enum) resolvedProperty = resolvePropertyEnum(resultProperty);
	else resolvedProperty = resultProperty;
	if (resolvedProperty?.propertyConfig && !isDefaultFieldConfigId(resolvedProperty.propertyConfig)) {
		const cmsFields = rest.propertyConfigs;
		if (!cmsFields && !ignoreMissingFields) throw Error(`Trying to resolve a property with key '${resolvedProperty.propertyConfig}' that inherits from a custom property config but no custom property configs were provided. Use the property 'propertyConfigs' in your app config to provide them`);
		const customField = cmsFields?.[resolvedProperty.propertyConfig];
		if (!customField) {
			console.warn(`Trying to resolve a property with key '${resolvedProperty.propertyConfig}' that inherits from a custom property config but no custom property config with that key was found. Check the 'propertyConfigs' in your app config`);
			return resolvedProperty;
		}
		if (customField.property) {
			const restConfigProperty = { ...customField.property };
			delete restConfigProperty.propertyConfig;
			const customFieldProperty = resolveProperty({
				property: {
					name: "",
					...restConfigProperty
				},
				ignoreMissingFields,
				...rest
			});
			if (customFieldProperty) resolvedProperty = mergeDeep(customFieldProperty, resolvedProperty);
		}
	}
	return resolvedProperty;
}
/**
* The resolved relation a relation property refers to.
*
* Normalization stamps `resolvedRelation` onto the property, so this is usually
* a field read. It falls back to resolving from the collection for properties
* that never went through the registry — a preview, or a form rendered straight
* from an authored config.
*/
function resolveRelationProperty(property, collection, propertyKey) {
	if (property.resolvedRelation) return property.resolvedRelation;
	if (property.relation) return resolveRelation(property.relation, collection, propertyKey);
	const name = propertyKey ?? "";
	const declared = resolveCollectionRelations(collection)[name];
	if (!declared) throw Error(`Relation property '${name || "(unnamed)"}' on '${collection.slug}' declares no \`relation\`, and the collection has no relation of that name.`);
	return declared;
}
/**
* Resolve enum aliases for a string or number property
* @param property
*/
function resolvePropertyEnum(property) {
	if (typeof property.enum === "object") return {
		...property,
		enum: enumToObjectEntries(property.enum)?.filter((value) => value && (value.id || value.id === 0) && value.label) ?? []
	};
	return property;
}
/**
* Resolve enums and arrays for properties
* @param properties
* @param value
*/
function resolveProperties({ propertyKey, properties, ignoreMissingFields, ...props }) {
	return Object.entries(properties).map(([key, property]) => {
		const childResolvedProperty = resolveProperty({
			propertyKey: propertyKey ? `${propertyKey}.${key}` : void 0,
			property,
			ignoreMissingFields,
			...props
		});
		if (!childResolvedProperty) return {};
		return { [key]: childResolvedProperty };
	}).filter((a) => a !== null).reduce((a, b) => ({
		...a,
		...b
	}), {});
}
function resolveArrayProperties({ propertyKey, property, ignoreMissingFields = false, ...props }) {
	const propertyValue = propertyKey ? getIn(props.values, propertyKey) : void 0;
	if (property.of) if (Array.isArray(property.of)) return property.of.map((p, index) => {
		return resolveProperty({
			propertyKey: `${propertyKey}.${index}`,
			property: p,
			ignoreMissingFields,
			...props,
			index
		});
	});
	else {
		const of = property.of;
		const resolvedProperties = getArrayResolvedProperties({
			propertyValue,
			propertyKey,
			property,
			ignoreMissingFields,
			...props
		});
		const { values, previousValues, ...rest } = props;
		if (!resolveProperty({
			property: of,
			ignoreMissingFields,
			...rest
		}) && !ignoreMissingFields) throw Error("When using a property builder as the 'of' prop of an ArrayProperty, you must return a valid child property");
		return resolvedProperties;
	}
	else if (property.oneOf) {
		const typeField = property.oneOf?.typeField ?? "type";
		return Array.isArray(propertyValue) ? propertyValue.map((v, index) => {
			const type = v && v[typeField];
			const childProperty = property.oneOf?.properties[type];
			if (!type || !childProperty) return null;
			return resolveProperty({
				propertyKey: `${propertyKey}.${index}`,
				property: childProperty,
				ignoreMissingFields,
				...props
			});
		}).filter((e) => Boolean(e)) : [];
	} else if (!property.columnType) throw Error(`The array property (${propertyKey}) needs to declare an 'of' or a 'oneOf' property, or a \`columnType\` such as "text[]"`);
	else return [];
}
function getArrayResolvedProperties({ propertyKey, propertyValue, property, ...props }) {
	const of = property.of;
	if (!of) throw Error(`Trying to resolve an array property (${propertyKey}) without providing an 'of' property`);
	return Array.isArray(propertyValue) ? propertyValue.map((v, index) => {
		return resolveProperty({
			propertyKey: `${propertyKey}.${index}`,
			property: Array.isArray(of) ? of[index] : of,
			...props,
			index
		});
	}).filter((e) => Boolean(e)) : [];
}
function resolveEnumValues(input) {
	if (typeof input === "object") return Object.entries(input).map(([id, value]) => typeof value === "string" ? {
		id,
		label: value
	} : value);
	else if (Array.isArray(input)) return input;
	else return;
}
/**
* The lists rendered inside an entity view of `collection` — its tabs.
*
* The single derivation. There used to be two that disagreed: this one, and a
* copy in `CollectionRegistry.normalizeCollection` that stamped each child with
* the *target collection's* slug instead of the relation key. Since the
* registry ran first and cached its answer onto `childCollections`, its version
* was the one that won, and the frontend addressed child listings by a segment
* the backend could not resolve.
*
* Order of precedence:
*   1. `childCollections` — the explicit escape hatch for custom drivers.
*   2. `subcollections` on an engine that has real containment (Firestore).
*   3. many-relations on an engine that has relations (SQL).
*/
function getEntityChildViews(collection) {
	const asSubcollections = (collections) => collections.filter(Boolean).map((child) => ({
		key: child.slug,
		collection: child,
		source: { kind: "subcollection" }
	}));
	if (collection.childCollections) return asSubcollections(collection.childCollections() ?? []);
	const capabilities = getDataSourceCapabilities(collection.engine);
	const declaredSubcollections = getDeclaredSubcollections(collection);
	if (capabilities.supportsSubcollections && declaredSubcollections) return asSubcollections(declaredSubcollections() ?? []);
	if (!capabilities.supportsRelations) return [];
	const resolvedRelations = resolveCollectionRelations(collection);
	const views = [];
	const seen = /* @__PURE__ */ new Set();
	for (const [relationKey, relation] of Object.entries(resolvedRelations)) {
		if (relation.cardinality !== "many") continue;
		const identity = relation.relationName ?? relationKey;
		if (seen.has(identity)) continue;
		let target;
		try {
			target = relation.target();
		} catch {
			continue;
		}
		if (!target) continue;
		seen.add(identity);
		const customName = Object.entries(collection.properties ?? {}).find(([propKey, p]) => p.type === "relation" && (p.relation?.relationName ?? propKey) === identity)?.[1]?.name;
		const base = {
			...target,
			slug: relationKey,
			...customName ? {
				name: customName,
				singularName: customName
			} : {}
		};
		views.push({
			key: relationKey,
			collection: relation.overrides ? mergeDeep(base, relation.overrides) : base,
			source: {
				kind: "relation",
				relationKey,
				mode: isJunctionBackedRelation(relation) ? "linked" : "owned",
				targetSlug: target.slug
			}
		});
	}
	return views;
}
/**
* Each of `collection`'s tabs paired with the property that declared it, when a
* property declared it: child view key → property key.
*
* A many-relation can only be declared as a property — that is the documented
* and only mechanism — and {@link getEntityChildViews} promotes it to a tab. So
* one declaration reaches the panel twice, and neither surface knew about the
* other. The form rendered a relation picker beside the tab, and the collection
* table rendered *two* columns under one heading: the relation's own column,
* showing the child rows, and a jump-to-tab button carrying the same name.
*
* The pairing is what lets each surface decide which half is redundant, and it
* has to be a pairing rather than two sets because the two keys differ whenever
* a relation is named. The match is on the resolved `relationName` — the
* identity `getEntityChildViews` itself dedupes on — so a relation declared in
* `relations` and pointed at by a differently-named property is recognised too.
*
* A relation with no property of its own is absent here, which is the point: it
* has exactly one surface already, and nothing to weigh it against.
*
* Only top-level properties: a relation nested inside a `map` gets no tab.
*/
function getChildViewDeclaringProperties(collection) {
	const pairs = /* @__PURE__ */ new Map();
	const relationProperties = Object.entries(collection.properties ?? {}).filter(([, property]) => property?.type === "relation");
	if (relationProperties.length === 0) return pairs;
	const relationViews = getEntityChildViews(collection).filter((view) => view.source.kind === "relation");
	if (relationViews.length === 0) return pairs;
	const resolvedRelations = resolveCollectionRelations(collection);
	const identityOf = (relationKey) => resolvedRelations[relationKey]?.relationName ?? relationKey;
	const declaringPropertyByIdentity = /* @__PURE__ */ new Map();
	for (const [propertyKey, property] of relationProperties) {
		const relation = property.resolvedRelation ?? resolvedRelations[propertyKey];
		if (relation?.cardinality !== "many") continue;
		const identity = relation.relationName ?? propertyKey;
		if (!declaringPropertyByIdentity.has(identity)) declaringPropertyByIdentity.set(identity, propertyKey);
	}
	for (const view of relationViews) {
		const propertyKey = declaringPropertyByIdentity.get(identityOf(view.source.relationKey));
		if (propertyKey) pairs.set(view.key, propertyKey);
	}
	return pairs;
}
/**
* The property keys of `collection` whose relation is already one of its tabs.
*
* What a form asks: the tab is the treatment for a list of child rows, so the
* picker beside it is the redundant half. See
* {@link getChildViewDeclaringProperties}.
*/
function getChildViewRelationPropertyKeys(collection) {
	return new Set(getChildViewDeclaringProperties(collection).values());
}
/**
* The child views of `collection` as bare collections.
*
* The flattened view of {@link getEntityChildViews}, for navigation code that
* only needs to match a path segment against a slug. Anything that cares *what
* kind* of list it is showing — chiefly the admin, which must not offer a
* global delete on a shared row — should read the views instead.
*/
function getSubcollections(collection) {
	return getEntityChildViews(collection).map((view) => view.collection);
}
//#endregion
//#region src/util/policy/sqlToPolicy.ts
/**
* A tiny, regex-based SQL "parser" for security rules.
*
* This is NOT a full SQL parser. It is designed to handle the subset of SQL
* commonly used in `USING` and `WITH CHECK` clauses, enough to drive the
* optimistic client-side UI decision.
*
* It handles:
* - `field = 'literal'`
* - `field != 'literal'`
* - `field = current_setting('app.uid')` (or the legacy `app.user_id`)
* - `A AND B`, `A OR B` — only where the keyword is at the top level
* - `true`
* - `IN (...)` (as optimistic true)
*
* For anything it doesn't understand, it returns a `raw` expression, which
* the evaluator treats as "unknown" (and usually optimistic true).
*
* **This output also round-trips back into DDL** via `policyToPostgres` (the
* schema/policy generators), so decomposing a clause the parser only partly
* understands is not a cosmetic mistake — it emits invalid SQL. When in doubt,
* prefer `raw`: it is reproduced verbatim.
*/
/** True when `keyword` starts at `i` as a standalone word. */
function isKeywordAt(upper, i, keyword) {
	if (!upper.startsWith(keyword, i)) return false;
	const before = i === 0 ? " " : upper[i - 1];
	const after = upper[i + keyword.length] ?? " ";
	return /[\s()]/.test(before) && /[\s()]/.test(after);
}
/**
* Split `sql` on a boolean keyword, but only where it sits at paren depth 0 and
* outside a string literal. Returns null when it never does, so the caller
* leaves the clause alone.
*
* This used to be `sql.split(/ AND /i)`, which tore subqueries in half: the
* `AND` inside
*   `EXISTS (SELECT 1 FROM organization_members m WHERE m.org = t.org AND m.user_id = rebase.uid())`
* split the expression, and re-emitting the halves produced
*   `(EXISTS (...) AND m.user_id = rebase.uid())`
* where `m` is no longer in scope — SQL that Postgres rejects outright with
* "missing FROM-clause entry for table". Returning null instead keeps such a
* clause as a `raw` expression, which round-trips verbatim.
*/
function splitTopLevel(sql, keyword) {
	const upper = sql.toUpperCase();
	const parts = [];
	let depth = 0;
	let inString = false;
	let start = 0;
	for (let i = 0; i < sql.length; i++) {
		const ch = sql[i];
		if (inString) {
			if (ch === "'") if (sql[i + 1] === "'") i++;
			else inString = false;
			continue;
		}
		if (ch === "'") {
			inString = true;
			continue;
		}
		if (ch === "(") {
			depth++;
			continue;
		}
		if (ch === ")") {
			depth--;
			continue;
		}
		if (depth === 0 && isKeywordAt(upper, i, keyword)) {
			parts.push(sql.slice(start, i));
			i += keyword.length - 1;
			start = i + 1;
		}
	}
	if (parts.length === 0) return null;
	parts.push(sql.slice(start));
	const trimmedParts = parts.map((p) => p.trim()).filter((p) => p.length > 0);
	return trimmedParts.length > 1 ? trimmedParts : null;
}
/** Drop redundant wrapping parens (`(a AND b)` → `a AND b`), never `(a) AND (b)`. */
function stripOuterParens(sql) {
	let s = sql.trim();
	for (;;) {
		if (!s.startsWith("(") || !s.endsWith(")")) return s;
		let depth = 0;
		let inString = false;
		let wraps = true;
		for (let i = 0; i < s.length; i++) {
			const ch = s[i];
			if (inString) {
				if (ch === "'") if (s[i + 1] === "'") i++;
				else inString = false;
				continue;
			}
			if (ch === "'") {
				inString = true;
				continue;
			}
			if (ch === "(") depth++;
			else if (ch === ")") {
				depth--;
				if (depth === 0 && i < s.length - 1) {
					wraps = false;
					break;
				}
			}
		}
		if (!wraps) return s;
		s = s.slice(1, -1).trim();
	}
}
function sqlToPolicy(sql) {
	const trimmed = stripOuterParens(rewriteLegacyRlsFunctions(sql).trim());
	if (trimmed.toLowerCase() === "true") return policy.true();
	if (trimmed.toLowerCase() === "false") return policy.false();
	const overlapMatch = trimmed.match(/^string_to_array\s*\(\s*rebase\.roles\(\)\s*,\s*','\s*\)\s*&&\s*ARRAY\s*\[(.+)\]$/i);
	if (overlapMatch) {
		const roles = overlapMatch[1].split(",").map((s) => s.trim().replace(/^'|'$/g, ""));
		return policy.rolesOverlap(roles);
	}
	const containMatch = trimmed.match(/^string_to_array\s*\(\s*rebase\.roles\(\)\s*,\s*','\s*\)\s*@>\s*ARRAY\s*\[(.+)\]$/i);
	if (containMatch) {
		const roles = containMatch[1].split(",").map((s) => s.trim().replace(/^'|'$/g, ""));
		return policy.rolesContain(roles);
	}
	const orParts = splitTopLevel(trimmed, "OR");
	if (orParts) return policy.or(...orParts.map(sqlToPolicy));
	const andParts = splitTopLevel(trimmed, "AND");
	if (andParts) return policy.and(...andParts.map(sqlToPolicy));
	const match = trimmed.match(/^(.+?)\s*(!?=)\s*(.+)$/);
	if (match) {
		const [, leftStr, op, rightStr] = match;
		const left = parseOperand(leftStr.trim());
		const right = parseOperand(rightStr.trim());
		if (left && right) return policy.compare(left, op === "=" ? "eq" : "neq", right);
	}
	return policy.raw(trimmed);
}
/**
* Literals from other BaaS platforms that people compare `rebase.uid()` against
* out of habit. Mirrors the driver's `FOREIGN_CONVENTION_ROLES` guard on
* `pgRoles`, one surface over: the same muscle memory inside a `using:` string
* is the more dangerous spelling, because it inverts a rule instead of
* emptying a table.
*/
/**
* A `Map`, not an object literal.
*
* As `Record<string, string>` this was indexed with a literal taken straight
* out of a policy, so every key on `Object.prototype` answered: a rule
* comparing `rebase.uid()` to `"valueOf"`, `"toString"`, `"constructor"` or
* `"hasOwnProperty"` found a truthy "platform" and reported an anonymous-grant
* risk that does not exist — with the matched function interpolated into the
* explanation as the platform's name. A security warning that fires on
* innocent input is worse than none: it is what teaches people to skip the
* warnings that are real.
*
* Same shape as the prototype-pollution class swept out of `setIn`, `getIn`,
* `mergeDeep` and `unflattenObject` — a data-derived key reaching a plain
* object. Found by a property test, on the input `"valueOf"`.
*/
var FOREIGN_CONVENTION_UIDS = /* @__PURE__ */ new Map([
	["anon", "Supabase"],
	["authenticated", "Supabase"],
	["service_role", "Supabase"]
]);
/**
* The same foreign literals, as a pattern for SQL that could not be parsed
* back into structure.
*/
var FOREIGN_UID_LITERAL_SQL = new RegExp(String.raw`rebase\.uid\(\)\s*=\s*'(${[...FOREIGN_CONVENTION_UIDS.keys()].join("|")})'`, "i");
/**
* `rebase.uid() IS NOT NULL` in raw SQL, the clause that is always true.
*
* Both schema spellings, because this runs over policy bodies read back from a
* database, and one migrated by a pre-1.0 release still holds `auth.uid()`.
* A security check that stops recognising a dangerous clause because the
* framework renamed a function is a check that silently turns off.
*/
var UID_NOT_NULL = /\b(?:rebase|auth)\.uid\(\)\s+IS\s+NOT\s+NULL/i;
/**
* Find clauses that read as "signed-in users only" but admit anonymous callers.
*
* Both spellings come from the same place — Supabase, where its own `auth.uid()`
* really is NULL for an anonymous request. Rebase substitutes
* {@link ANONYMOUS_USER_ID} instead (a blank id would read back as NULL, which
* is how the trusted *server* context is recognised), so:
*
*  - `rebase.uid() IS NOT NULL` is a tautology on the user path, and
*  - `rebase.uid() != 'anon'` excludes one spelling of anonymous and admits the
*    other. This one is not hypothetical and was not only a foreign habit:
*    rebase's own request path reported `'anon'` while everything that compiled
*    or checked a policy used `'anonymous'`, so whichever literal an author
*    picked, half the anonymous callers walked through. See
*    {@link ANONYMOUS_USER_IDS}.
*
* Either one turns a lockdown into a full grant, and neither looks wrong. No
* real user id is ever one of these literals, and a user-context request is
* never NULL, so a match is always a mistake rather than a deliberate check.
*
* Structured expressions are checked too, not just parsed SQL: `policy.compare`
* can spell the same mistake.
*/
function findAnonymousGrants(expr) {
	const found = [];
	const visit = (e) => {
		switch (e.kind) {
			case "and":
			case "or":
				e.operands.forEach(visit);
				return;
			case "not":
				visit(e.operand);
				return;
			case "existsIn":
				visit(e.where);
				return;
			case "raw": {
				if (UID_NOT_NULL.test(e.sql)) found.push({
					pattern: "uid-not-null",
					detail: e.sql,
					explanation: `\`rebase.uid() IS NOT NULL\` is true for every request that came from a client, including anonymous ones — they carry '${ANONYMOUS_USER_ID}', not NULL. Use \`condition: policy.authenticated()\` to mean "signed in".`
				});
				const foreign = FOREIGN_UID_LITERAL_SQL.exec(e.sql);
				if (foreign) {
					const literal = foreign[1];
					found.push({
						pattern: "foreign-uid-literal",
						detail: literal,
						explanation: `'${literal}' is a ${FOREIGN_CONVENTION_UIDS.get(literal)} convention. Rebase reports an anonymous request as '${ANONYMOUS_USER_ID}', so comparing against '${literal}' passes for every caller. Use \`condition: policy.authenticated()\` to mean "signed in".`
					});
				}
				return;
			}
			case "compare": {
				const literal = [e.left, e.right].find((o) => o.kind === "literal");
				if (!(e.left.kind === "authUid" || e.right.kind === "authUid") || typeof literal?.value !== "string") return;
				const platform = FOREIGN_CONVENTION_UIDS.get(literal.value);
				if (!platform) return;
				found.push({
					pattern: "foreign-uid-literal",
					detail: literal.value,
					explanation: `'${literal.value}' is a ${platform} convention. Rebase reports an anonymous request as '${ANONYMOUS_USER_ID}', so comparing against '${literal.value}' passes for every caller. Use \`condition: policy.authenticated()\` to mean "signed in" — it compiles to NOT IN (${ANONYMOUS_USER_IDS.map((v) => `'${v}'`).join(", ")}), covering every spelling rebase has reported rather than whichever one you remember.`
				});
				return;
			}
			default: return;
		}
	};
	visit(expr);
	return found;
}
function parseOperand(str) {
	if (/^current_setting\s*\(\s*'app\.(uid|user_id)'\s*\)$/i.test(str) || /^rebase\.uid\(\)$/i.test(str)) return policy.authUid();
	const literal = parseSingleQuoted(str);
	if (literal !== null) return policy.literal(literal);
	if (/^-?\d+$/.test(str)) return policy.literal(Number(str));
	if (/^-?\d*\.\d+$/.test(str)) return policy.literal(Number(str));
	if (/^true$/i.test(str)) return policy.literal(true);
	if (/^false$/i.test(str)) return policy.literal(false);
	if (/^null$/i.test(str)) return policy.literal(null);
	if (/^\w+$/.test(str) && toSnakeCase(str) !== "") return policy.field(str);
	return null;
}
/**
* Decode a single-quoted SQL literal, or null when `str` is not exactly one.
*
* Rejecting is as important as decoding: `'a' = 'b'` is two literals and an
* operator, not one literal whose body contains a quote, and a regex anchored
* on the outer quotes would happily read it as the latter. Every interior quote
* must therefore be part of a `''` pair.
*/
function parseSingleQuoted(str) {
	if (str.length < 2 || !str.startsWith("'") || !str.endsWith("'")) return null;
	const body = str.slice(1, -1);
	let out = "";
	for (let i = 0; i < body.length; i++) {
		if (body[i] !== "'") {
			out += body[i];
			continue;
		}
		if (body[i + 1] === "'") {
			out += "'";
			i++;
			continue;
		}
		return null;
	}
	return out;
}
//#endregion
//#region src/util/policy/securityRuleToConditions.ts
/**
* Desugars a {@link SecurityRule} — its `access`/`ownerField`/`roles` shortcuts,
* structured `condition`/`check`, and raw `using`/`withCheck` — into a single
* normalized {@link PolicyExpression} pair.
*
* **This is the linchpin against drift:** both the Postgres DDL generators and
* the client-side evaluator consume this one function, so there is exactly one
* definition of what a rule means. In particular, application `roles` are folded
* into the expression here (AND'd with the base condition, matching how Postgres
* generates the clause) rather than being handled separately by each consumer.
*/
function securityRuleToConditions(rule) {
	return {
		usingExpr: withRoles(baseUsing(rule), rule),
		withCheckExpr: withRoles(baseWithCheck(rule), rule)
	};
}
function baseUsing(rule) {
	if (rule.condition) return rule.condition;
	if (rule.using != null) return sqlToPolicy(rule.using);
	if (rule.access === "public") return policy.true();
	if (rule.ownerField) return policy.compare(policy.field(rule.ownerField), "eq", policy.authUid());
	return null;
}
function baseWithCheck(rule) {
	if (rule.check) return rule.check;
	if (rule.withCheck != null) return sqlToPolicy(rule.withCheck);
	return baseUsing(rule);
}
/**
* AND the base condition with an application-role check, or produce a roles-only
* condition when there is no base. Mirrors the Postgres generator so that a
* role-scoped restrictive rule denies exactly the same set of users on both
* sides.
*/
function withRoles(base, rule) {
	if (!rule.roles || rule.roles.length === 0) return base;
	const rolesExpr = policy.rolesOverlap(rule.roles);
	if (rule.mode === "restrictive") return base ? policy.or(policy.not(rolesExpr), base) : policy.not(rolesExpr);
	return base ? policy.and(base, rolesExpr) : rolesExpr;
}
//#endregion
//#region src/util/policy/policyToPostgres.ts
/**
* Compiles a {@link PolicyExpression} to a PostgreSQL boolean SQL string,
* suitable for a `USING (...)` / `WITH CHECK (...)` clause.
*
* This is one of the two consumers of the shared policy model (the other being
* {@link evaluatePolicy}); the Postgres schema generators call it so that DDL
* and the admin UI derive from the exact same expression.
*/
function policyToPostgres(expr, collection, options) {
	return compile(expr, {
		fieldCollection: collection,
		fieldPrefix: "",
		outerCollection: collection,
		outerPrefix: "",
		resolveCollection: options?.resolveCollection,
		alias: { n: 0 }
	});
}
function compile(expr, scope) {
	switch (expr.kind) {
		case "true": return "true";
		case "false": return "false";
		case "and": return expr.operands.length === 0 ? "true" : expr.operands.map((o) => `(${compile(o, scope)})`).join(" AND ");
		case "or": return expr.operands.length === 0 ? "false" : expr.operands.map((o) => `(${compile(o, scope)})`).join(" OR ");
		case "not": return `NOT (${compile(expr.operand, scope)})`;
		case "compare": {
			const castForAuthUid = (operand, sqlText, other) => other.kind === "authUid" && (operand.kind === "field" || operand.kind === "outerField") ? `(${sqlText})::text` : sqlText;
			const claimSql = (operand, other) => operand.kind === "authClaim" ? authClaimSql(operand.name, claimCastType(other, scope)) : void 0;
			const leftSql = claimSql(expr.left, expr.right) ?? castForAuthUid(expr.left, operandToSql(expr.left, scope), expr.right);
			const rightSql = claimSql(expr.right, expr.left) ?? castForAuthUid(expr.right, operandToSql(expr.right, scope), expr.left);
			return `${leftSql} ${COMPARE_SQL[expr.op]} ${rightSql}`;
		}
		case "rolesOverlap": return `string_to_array(${RLS_ROLES_SQL}, ',') && ${rolesArraySql(expr.roles)}`;
		case "rolesContain": return `string_to_array(${RLS_ROLES_SQL}, ',') @> ${rolesArraySql(expr.roles)}`;
		case "authenticated": return `${RLS_UID_SQL} IS NOT NULL AND ${RLS_UID_SQL} NOT IN (${ANONYMOUS_USER_IDS.map(quoteLiteral).join(", ")})`;
		case "registered": return `${RLS_UID_SQL} IS NOT NULL AND ${RLS_UID_SQL} NOT IN (${ANONYMOUS_USER_IDS.map(quoteLiteral).join(", ")}) AND NOT ${RLS_IS_ANONYMOUS_SQL}`;
		case "serverContext": return `${RLS_UID_SQL} IS NULL`;
		case "existsIn": return compileExistsIn(expr, scope);
		case "raw": return rewriteLegacyRlsFunctions(expr.sql).replace(/\{(\w+)\}/g, (_, col) => `${outerQualifier(scope)}${resolveColumnName(col, scope.outerCollection)}`);
	}
}
/**
* Compiles `existsIn` to a correlated `EXISTS (SELECT 1 FROM <join> WHERE ...)`.
* Inside the subquery, `field` operands bind to the aliased join table and
* `outerField` operands bind to the (table-qualified) outer RLS row.
*/
function compileExistsIn(expr, scope) {
	const join = scope.resolveCollection?.(expr.collection);
	const joinTable = join ? getTableName(join) : toSnakeCase(expr.collection);
	const joinSchema = schemaOf(join) ?? schemaOf(scope.outerCollection) ?? "public";
	const alias = `_ex${scope.alias.n++}`;
	const outerPrefix = outerQualifier(scope);
	const innerScope = {
		fieldCollection: join,
		fieldPrefix: `"${alias}".`,
		outerCollection: scope.outerCollection,
		outerPrefix,
		resolveCollection: scope.resolveCollection,
		alias: scope.alias
	};
	return `EXISTS (SELECT 1 FROM "${joinSchema}"."${joinTable}" "${alias}" WHERE ${compile(expr.where, innerScope)})`;
}
var COMPARE_SQL = {
	eq: "=",
	neq: "!=",
	lt: "<",
	lte: "<=",
	gt: ">",
	gte: ">="
};
function operandToSql(operand, scope) {
	switch (operand.kind) {
		case "field": return `${scope.fieldPrefix}${resolveColumnName(operand.name, scope.fieldCollection)}`;
		case "outerField": return `${scope.outerPrefix}${resolveColumnName(operand.name, scope.outerCollection)}`;
		case "literal": return quoteLiteral(operand.value);
		case "authUid": return RLS_UID_SQL;
		case "authRoles": return `string_to_array(${RLS_ROLES_SQL}, ',')`;
		case "authClaim": return authClaimSql(operand.name, "text");
	}
}
/**
* The Postgres type a claim has to be cast to, to be compared with `operand`.
*
* `"text"` means "no cast": a claim already is text, and a `text` / `varchar`
* column compares with it directly and keeps using its index.
*
* Resolved from the *property*, and through a relation's target when the column
* is a foreign key — a `belongsTo` tenant field is the ordinary shape, and its
* column's type is the target collection's primary key type rather than
* anything visible on the property itself. Unknown resolves to `"text"`, which
* is the safe direction: a redundant `text` comparison costs nothing, while a
* missing `uuid` cast is a `CREATE POLICY` that fails and leaves a table with
* RLS enabled and no policy — which denies every row.
*/
function claimCastType(operand, scope) {
	if (operand.kind !== "field" && operand.kind !== "outerField") return "text";
	const collection = operand.kind === "field" ? scope.fieldCollection : scope.outerCollection;
	return propertyClaimCastType(operand.name, collection, scope.resolveCollection);
}
/**
* What `name` on `collection` compares against a text claim as.
*
* `depth` stops a `reference` cycle — two collections whose keys point at each
* other — from recursing forever. Two hops is more than any real declaration
* needs.
*/
function propertyClaimCastType(name, collection, resolveCollection, depth = 0) {
	const prop = collection?.properties?.[name];
	if (!prop || depth > 2) return "text";
	switch (prop.type) {
		case "string": {
			const sp = prop;
			if (sp.enum) return "text";
			return sp.isId === "uuid" || sp.columnType === "uuid" ? "uuid" : "text";
		}
		case "number": {
			const np = prop;
			if (np.columnType === "numeric") return "numeric";
			if (np.columnType || np.validation?.integer || np.isId) return "bigint";
			return "numeric";
		}
		case "reference": return primaryKeyClaimCastType(resolveTargetCollection(prop.path, resolveCollection), resolveCollection, depth);
		case "relation": return primaryKeyClaimCastType(relationTarget(name, prop, collection, resolveCollection), resolveCollection, depth);
		default: return "text";
	}
}
/** The cast a column pointing at `target`'s primary key needs. */
function primaryKeyClaimCastType(target, resolveCollection, depth) {
	if (!target) return "text";
	for (const [key, property] of Object.entries(target.properties ?? {})) {
		if (!property?.isId) continue;
		return propertyClaimCastType(key, target, resolveCollection, depth + 1);
	}
	return "text";
}
/**
* A relation's target slug, whatever form the declaration took.
*
* `target` is a slug on a plain object and a thunk on a builder — the two
* shapes `resolveRelation` normalises — and this runs on the raw property,
* before that resolution.
*/
function relationTargetSlug(relation) {
	const target = relation?.target;
	if (typeof target === "string") return target;
	if (typeof target !== "function") return void 0;
	try {
		const slug = target()?.slug;
		return typeof slug === "string" ? slug : void 0;
	} catch {
		return;
	}
}
function resolveTargetCollection(slug, resolveCollection) {
	if (!slug || !resolveCollection) return void 0;
	return resolveCollection(slug) ?? resolveCollection(slug.split("/").pop());
}
/**
* `NULLIF(rebase.jwt() ->> 'name', '')`, cast to the column's type.
*
* Two things here are load-bearing beyond the cast:
*
* - **`NULLIF(…, '')`.** An absent claim already reads as NULL, but one set to
*   the empty string does not, and `''::uuid` raises rather than denying. Both
*   spellings of "this caller has no tenant" have to reach the comparison as
*   NULL, which is never true and therefore never a grant.
* - **The guard.** The cast sits inside a `CASE` that first checks the text is
*   well-formed, because `'nonsense'::uuid` raises `invalid input syntax` — and
*   a policy that raises does not deny a row, it fails the whole statement. A
*   caller holding a malformed claim would get a 500 on every read of the table
*   instead of an empty list. `CASE` rather than an `AND` guard because only
*   `CASE` is guaranteed not to evaluate its arms out of order.
*
* The whole expression is STABLE (`rebase.jwt()` is), so Postgres evaluates it
* once per query and can still use a btree index on the column it is compared
* against — which is the entire reason the cast is on this side.
*/
function authClaimSql(name, cast) {
	const claim = `NULLIF(${RLS_JWT_SQL} ->> ${quoteLiteral(name)}, '')`;
	if (cast === "text") return claim;
	return `CASE WHEN ${claim} ~ '${CLAIM_CAST_GUARDS[cast]}' THEN (${claim})::${cast} END`;
}
/**
* The text a claim must match before it is cast, per target type.
*
* The `bigint` guard caps the digit run at 18 rather than matching any run of
* digits: `'99999999999999999999'::bigint` is a range error, which fails the
* statement exactly as the syntax error would have.
*/
var CLAIM_CAST_GUARDS = {
	uuid: "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$",
	bigint: "^-?[0-9]{1,18}$",
	numeric: "^-?[0-9]+(\\.[0-9]+)?$"
};
/**
* SQL prefix that qualifies a column of the outer RLS row (`"schema"."table".`),
* or `""` when the collection is unknown.
*/
function outerQualifier(scope) {
	const table = scope.outerCollection ? getTableName(scope.outerCollection) : void 0;
	if (!table) return "";
	return `"${schemaOf(scope.outerCollection) ?? "public"}"."${table}".`;
}
function schemaOf(collection) {
	return collection?.schema || void 0;
}
function resolveColumnName(propName, collection) {
	const prop = collection?.properties?.[propName];
	if (prop && "columnName" in prop && typeof prop.columnName === "string") return quoteColumnIdentifier(prop.columnName);
	const relationColumn = belongsToColumn(propName, prop, collection);
	if (relationColumn) return quoteColumnIdentifier(relationColumn);
	return quoteColumnIdentifier(toSnakeCase(propName));
}
/**
* The foreign key column a `relation` property addresses, when it has one.
*
* A `belongsTo` property is a *link*, and its column is the relation's
* `localKey` — `org` addresses `org_id`. `toSnakeCase` cannot know that, so a
* rule naming a relation (`ownerField: "org"`, a tenancy declaration over a
* `belongsTo`) compiled to a comparison against a column called `org`, which
* does not exist. `CREATE POLICY` then fails, and a table with RLS enabled and
* no policy denies every row — the loudest possible symptom for the quietest
* possible cause, since the rule reads exactly right.
*
* Only `belongsTo`. Every other kind puts its column on the target table, in a
* junction row, or nowhere at all, and there is no column here to name.
*/
function belongsToColumn(propName, prop, collection) {
	if (prop?.type !== "relation") return void 0;
	const relation = resolvePropertyRelation(propName, collection);
	return relation?.kind === "belongsTo" ? relation.localKey : void 0;
}
/**
* The resolved relation a `relation` property stands for, by the only name
* both declarations share: the property's own key.
*
* A property may carry the link inline (`relation: { kind, target }`) or name
* an entry in the collection's `relations` array — and the second form is
* matched by the property key, so there is nothing on the property to read.
* `resolveCollectionRelations` normalises both under that key, which is why
* this asks it rather than reading the property.
*/
function resolvePropertyRelation(propName, collection) {
	if (!collection) return void 0;
	try {
		return findRelation(resolveCollectionRelations(collection), propName);
	} catch {
		return;
	}
}
/**
* The collection a `relation` property points at.
*
* Preferred over the property's raw `target` because only the resolved
* relation covers both declarations, and because a resolved `target()` hands
* back the collection itself — no registry needed. The slug path stays as the
* fallback for a `target` written as a plain string, which cannot become a
* collection without one.
*/
function relationTarget(propName, prop, collection, resolveCollection) {
	const relation = resolvePropertyRelation(propName, collection);
	if (relation) try {
		const target = relation.target();
		if (target) return target;
	} catch {}
	return resolveTargetCollection(relationTargetSlug(prop.relation), resolveCollection);
}
/**
* Every PostgreSQL keyword that cannot stand as a bare column reference.
* Appendix C's two reserved categories — plain "reserved", and "reserved (can
* be function or type name)" — since neither may name a column unquoted.
*/
var RESERVED_SQL_WORDS = /* @__PURE__ */ new Set([
	"all",
	"analyse",
	"analyze",
	"and",
	"any",
	"array",
	"as",
	"asc",
	"asymmetric",
	"authorization",
	"binary",
	"both",
	"case",
	"cast",
	"check",
	"collate",
	"collation",
	"column",
	"concurrently",
	"constraint",
	"create",
	"cross",
	"current_catalog",
	"current_date",
	"current_role",
	"current_schema",
	"current_time",
	"current_timestamp",
	"current_user",
	"default",
	"deferrable",
	"desc",
	"distinct",
	"do",
	"else",
	"end",
	"except",
	"false",
	"fetch",
	"for",
	"foreign",
	"freeze",
	"from",
	"full",
	"grant",
	"group",
	"having",
	"ilike",
	"in",
	"initially",
	"inner",
	"intersect",
	"into",
	"is",
	"isnull",
	"join",
	"lateral",
	"leading",
	"left",
	"like",
	"limit",
	"localtime",
	"localtimestamp",
	"natural",
	"not",
	"notnull",
	"null",
	"offset",
	"on",
	"only",
	"or",
	"order",
	"outer",
	"overlaps",
	"placing",
	"primary",
	"references",
	"returning",
	"right",
	"select",
	"session_user",
	"similar",
	"some",
	"symmetric",
	"system_user",
	"table",
	"tablesample",
	"then",
	"to",
	"trailing",
	"true",
	"union",
	"unique",
	"user",
	"using",
	"variadic",
	"verbose",
	"when",
	"where",
	"window",
	"with"
]);
/** An identifier Postgres reads back unchanged without quotes. */
var BARE_IDENTIFIER = /^[a-z_][a-z0-9_$]*$/;
/**
* Quote a column reference when Postgres would not read the bare name as that
* column — and only then.
*
* Three ways a bare name goes wrong, in ascending order of how long it takes to
* notice:
*
* - **Case.** `columnName` is used verbatim, and `rebase schema introspect`
*   populates it from a live database, so a legacy `"createdAt"` column arrives
*   spelled exactly that way. Unquoted, Postgres folds it to `createdat` and
*   `CREATE POLICY` fails with "column does not exist" — the collection keeps
*   RLS enabled with no policy, which denies every row.
* - **Syntax.** A column named `order` or `default` is a syntax error mid-clause.
* - **Silent rebinding.** `user`, `current_user`, `session_user`, `current_date`
*   and friends are *valid bare expressions*, so the policy compiles, applies,
*   and is reported as a success — while comparing against the connected role
*   or the wall clock instead of the column. Under RLS every request runs as the
*   same `rebase_user` role, so `USING (user = rebase.uid())` is a constant: it
*   denies everything, and its negation admits everything.
*
* Only the names that need it are quoted, so an ordinary snake_case policy body
* is emitted byte-for-byte as before. That keeps generated artifacts and the
* policies already stored in shipped databases stable — this fix reaches the
* clauses that were broken and no others.
*/
function quoteColumnIdentifier(name) {
	if (BARE_IDENTIFIER.test(name) && !RESERVED_SQL_WORDS.has(name)) return name;
	return `"${name.replace(/"/g, "\"\"")}"`;
}
function quoteLiteral(value) {
	if (value === null) return "NULL";
	if (typeof value === "boolean") return value ? "true" : "false";
	if (typeof value === "number") return String(value);
	return `'${value.replace(/'/g, "''")}'`;
}
/** Sorted, single-quoted `ARRAY['a','b']` — matches the generators' output. */
function rolesArraySql(roles) {
	return `ARRAY[${[...roles].sort().map((r) => `'${r}'`).join(",")}]`;
}
//#endregion
//#region src/util/policy/evaluatePolicy.ts
/**
* Evaluates a {@link PolicyExpression} against a user + row, using three-valued
* (Kleene) logic so that `"unknown"` sub-results propagate soundly.
*
* This is the JavaScript twin of {@link policyToPostgres}: both derive from the
* same expression, so the admin UI matches database enforcement by construction
* for every non-raw rule.
*/
function evaluatePolicy(expr, ctx) {
	switch (expr.kind) {
		case "true": return true;
		case "false": return false;
		case "and": return kleeneAnd$1(expr.operands.map((o) => evaluatePolicy(o, ctx)));
		case "or": return kleeneOr(expr.operands.map((o) => evaluatePolicy(o, ctx)));
		case "not": return kleeneNot(evaluatePolicy(expr.operand, ctx));
		case "compare": return evaluateCompare(expr.op, expr.left, expr.right, ctx);
		case "rolesOverlap": {
			const userRoles = ctx.roles ?? [];
			return expr.roles.some((r) => r === "public" || userRoles.includes(r));
		}
		case "rolesContain": {
			const userRoles = ctx.roles ?? [];
			return expr.roles.every((r) => r === "public" || userRoles.includes(r));
		}
		case "authenticated": return ctx.uid != null && !isAnonymousUid(ctx.uid);
		case "registered": return ctx.uid != null && !isAnonymousUid(ctx.uid) && ctx.isAnonymous !== true;
		case "serverContext": return false;
		case "existsIn": return "unknown";
		case "raw": return "unknown";
	}
}
function kleeneAnd$1(values) {
	if (values.some((v) => v === false)) return false;
	if (values.some((v) => v === "unknown")) return "unknown";
	return true;
}
function kleeneOr(values) {
	if (values.some((v) => v === true)) return true;
	if (values.some((v) => v === "unknown")) return "unknown";
	return false;
}
function kleeneNot(value) {
	if (value === "unknown") return "unknown";
	return !value;
}
function resolveOperand(operand, ctx) {
	switch (operand.kind) {
		case "literal": return {
			known: true,
			value: operand.value
		};
		case "authUid": return {
			known: true,
			value: ctx.uid ?? ANONYMOUS_USER_ID
		};
		case "authRoles": return {
			known: true,
			value: ctx.roles ?? []
		};
		case "field":
			if (!ctx.entity) return { known: false };
			return {
				known: true,
				value: ctx.entity.values[operand.name]
			};
		case "outerField": return { known: false };
		case "authClaim": return { known: false };
	}
}
function evaluateCompare(op, left, right, ctx) {
	const l = resolveOperand(left, ctx);
	const r = resolveOperand(right, ctx);
	if (!l.known || !r.known) return "unknown";
	const a = l.value;
	const b = r.value;
	if (a === null || b === null) return "unknown";
	if (op === "eq") return a === b;
	if (op === "neq") return a !== b;
	if (typeof a === "string" && typeof b === "string") {
		if (op === "lt") return a < b;
		if (op === "lte") return a <= b;
		if (op === "gt") return a > b;
		if (op === "gte") return a >= b;
	}
	if (typeof a === "number" && typeof b === "number") {
		if (op === "lt") return a < b;
		if (op === "lte") return a <= b;
		if (op === "gt") return a > b;
		if (op === "gte") return a >= b;
	}
	if (typeof a === "bigint" && typeof b === "bigint") {
		if (op === "lt") return a < b;
		if (op === "lte") return a <= b;
		if (op === "gt") return a > b;
		if (op === "gte") return a >= b;
	}
	return "unknown";
}
//#endregion
//#region src/util/permissions.ts
/** Combine clause results with AND under three-valued (Kleene) logic. */
function kleeneAnd(values) {
	if (values.some((v) => v === false)) return false;
	if (values.some((v) => v === "unknown")) return "unknown";
	return true;
}
/** The operations a rule covers, mirroring the Postgres generator's resolution. */
function ruleOperations(rule) {
	return rule.operations && rule.operations.length > 0 ? rule.operations : [rule.operation ?? "all"];
}
function ruleApplies(rule, targetOperation) {
	const ops = ruleOperations(rule);
	return ops.includes(targetOperation) || ops.includes("all");
}
/**
* Evaluate a single rule for one operation, returning a tri-state.
*
* A `null` clause (the rule contributes no condition for a required clause)
* denies — matching Postgres, which emits `USING (false)` / `WITH CHECK (false)`
* in that case. USING applies to SELECT/UPDATE/DELETE; WITH CHECK to
* INSERT/UPDATE; both must pass for UPDATE.
*/
function evaluateRuleForOperation(rule, ctx, targetOperation, clauses = "both") {
	const { usingExpr, withCheckExpr } = securityRuleToConditions(rule);
	const clause = (expr) => expr === null ? false : evaluatePolicy(expr, ctx);
	const needsUsing = targetOperation !== "insert" && clauses !== "withCheck";
	const needsWithCheck = (targetOperation === "insert" || targetOperation === "update") && clauses !== "using";
	const results = [];
	if (needsUsing) results.push(clause(usingExpr));
	if (needsWithCheck) results.push(clause(withCheckExpr));
	return kleeneAnd(results);
}
function resolveTriState(value, onUnknown) {
	if (value === "unknown") return onUnknown === "allow";
	return value;
}
/**
* Decide whether an operation is permitted for a user on a (possibly null) row,
* by evaluating the collection's security rules with the shared policy model —
* the same model compiled to Postgres RLS DDL, so the decision matches database
* enforcement for every non-raw rule.
*
* Engine-independent by design. `securityRules` are a declaration about the
* data, not about Postgres: the engine decides *who* enforces them (Postgres
* compiles them to RLS DDL, a document driver applies them in-process), never
* *whether* they hold. Gating this function on the engine's `supportsRLS`
* capability is what made every `{ onUnknown: "deny" }` call site in the Mongo
* driver return `true` before it evaluated anything — and it did so only for
* collections that spelled their engine out, so declaring `engine: "mongodb"`
* was what switched authorization off.
*
* @param options.onUnknown how to treat rules that cannot be decided
*   client-side (raw SQL, or row predicates with no row). Defaults to `"allow"`
*   for optimistic UI gating; enforcement callers should pass `"deny"`.
* @param options.clauses which half of each rule to evaluate. See
*   {@link PolicyClauses}; defaults to `"both"`.
*/
function checkOperation(collection, authContext, entity, targetOperation, options) {
	const onUnknown = options?.onUnknown ?? "allow";
	const clauses = options?.clauses ?? "both";
	const securityRules = collection.securityRules;
	if (!securityRules || securityRules.length === 0) return true;
	const applicableRules = securityRules.filter((r) => ruleApplies(r, targetOperation));
	if (applicableRules.length === 0) return false;
	const ctx = {
		uid: authContext.user?.uid,
		roles: authContext.user?.roles ?? [],
		isAnonymous: authContext.user?.isAnonymous === true,
		entity
	};
	let grantedByPermissive = false;
	let deniedByRestrictive = false;
	let hasPermissive = false;
	for (const rule of applicableRules) {
		const mode = rule.mode || "permissive";
		const passed = resolveTriState(evaluateRuleForOperation(rule, ctx, targetOperation, clauses), onUnknown);
		if (mode === "restrictive") {
			if (!passed) {
				deniedByRestrictive = true;
				break;
			}
		} else {
			hasPermissive = true;
			if (passed) grantedByPermissive = true;
		}
	}
	if (deniedByRestrictive) return false;
	return hasPermissive ? grantedByPermissive : false;
}
function canReadCollection(collection, authContext) {
	return checkOperation(collection, authContext, null, "select");
}
function canEditEntity(collection, authContext, path, entity) {
	return checkOperation(collection, authContext, entity, "update");
}
function canCreateEntity(collection, authContext, path, entity) {
	return checkOperation(collection, authContext, entity, "insert");
}
function canDeleteEntity(collection, authContext, path, entity) {
	return checkOperation(collection, authContext, entity, "delete");
}
//#endregion
//#region src/util/builders.ts
/**
* At runtime this is a plain identity function: a resource handle written where
* a key belongs — `dataSource: analytics` — becomes its key, so past this point
* a collection is plain data. The signature above is the rest of the point.
* @group Builder
*/
function defineCollection(collection) {
	return resolveResourceRefs(collection);
}
//#endregion
//#region src/util/storage.ts
/**
* Resolve the {@link StorageSource} to use for a property, given the key
* referenced by `StorageConfig.storageSource`.
*
* Resolution priority:
* 1. No `sourceKey` → the default source (backward compatible).
* 2. An explicit {@link StorageSourceRegistry} (e.g. `client.storageRegistry`).
* 3. A `sources` lookup map (e.g. the `StorageSourcesContext`).
* 4. Fall back to the default source.
*
* Shared by the upload hook, the markdown editor, and the read-only previews
* so the resolution logic lives in one place.
*
* @group Storage
*/
function resolveStorageSource(params) {
	const { sourceKey, sources, registry, defaultSource } = params;
	if (!sourceKey) return defaultSource;
	if (registry) return registry.getOrDefault(sourceKey);
	const fromSources = sources?.[sourceKey];
	if (fromSources) return fromSources;
	return defaultSource;
}
async function resolveStorageFilenameString({ input, storage, values, entityId, path, property, file, propertyKey }) {
	let result;
	if (typeof input === "function") {
		result = await input({
			path,
			entityId,
			values,
			property,
			file,
			storage,
			propertyKey
		});
		if (!result) console.warn("Storage callback returned empty result. Using default name value");
	} else result = replacePlaceholders({
		file,
		input,
		entityId,
		propertyKey,
		path
	});
	if (!result) result = randomString() + "_" + file.name;
	return result;
}
function resolveStoragePathString({ input, storage, values, entityId, path, property, file, propertyKey }) {
	let result;
	if (typeof input === "function") {
		result = input({
			path,
			entityId,
			values,
			property,
			file,
			storage,
			propertyKey
		});
		if (!result) console.warn("Storage callback returned empty result. Using default name value");
	} else result = replacePlaceholders({
		file,
		input,
		entityId,
		propertyKey,
		path
	});
	if (!result) result = randomString() + "_" + file.name;
	return result;
}
function replacePlaceholders({ file, input, entityId, propertyKey, path }) {
	const ext = file.name.split(".").pop();
	let result = input.replace("{propertyKey}", propertyKey).replace("{rand}", randomString()).replace("{file}", file.name).replace("{file.type}", file.type);
	if (entityId) result = result.replace("{entityId}", String(entityId));
	if (path) result = result.replace("{path}", path);
	if (ext) {
		result = result.replace("{file.ext}", ext);
		const name = file.name.replace(`.${ext}`, "");
		result = result.replace("{file.name}", name);
	}
	if (!result) result = randomString() + "_" + file.name;
	return result;
}
//#endregion
//#region src/util/callbacks.ts
/**
* Helper function to recursively check if there are any callbacks in the properties.
*/
function hasPropertyCallbacks(properties, callbackName) {
	if (!properties) return false;
	for (const property of Object.values(properties)) {
		if (property.callbacks?.[callbackName]) return true;
		if (property.type === "map" && property.properties) {
			if (hasPropertyCallbacks(property.properties, callbackName)) return true;
		} else if (property.type === "array" && property.of) {
			const ofs = Array.isArray(property.of) ? property.of : [property.of];
			for (const of of ofs) {
				if (of.callbacks?.[callbackName]) return true;
				if (of.type === "map" && of.properties && hasPropertyCallbacks(of.properties, callbackName)) return true;
			}
		}
	}
	return false;
}
/**
* Recursively process properties to apply field-level hooks.
*/
async function processProperties(properties, values, previousValues, propsContext, callbackName) {
	if (!values || typeof values !== "object") return values;
	const result = { ...values };
	for (const [key, property] of Object.entries(properties)) {
		if (result[key] === void 0) continue;
		let currentValue = result[key];
		const previousValue = previousValues?.[key];
		if (property.type === "array" && Array.isArray(currentValue)) {
			if (property.of && !Array.isArray(property.of)) currentValue = await Promise.all(currentValue.map(async (item, index) => {
				const prevItem = Array.isArray(previousValue) ? previousValue[index] : void 0;
				return (await processProperties({ "_tmp": property.of }, { "_tmp": item }, { "_tmp": prevItem }, propsContext, callbackName))["_tmp"];
			}));
		} else if (property.type === "map" && property.properties && typeof currentValue === "object") currentValue = await processProperties(property.properties, currentValue, previousValue ?? {}, propsContext, callbackName);
		if (property.callbacks?.[callbackName]) {
			const cbRes = await Promise.resolve(property.callbacks[callbackName]({
				...propsContext,
				value: currentValue,
				previousValue
			}));
			if (cbRes !== void 0) currentValue = cbRes;
		}
		result[key] = currentValue;
	}
	return result;
}
/**
* Helper function to extract field-level PropertyCallbacks from a properties schema
* and wrap them into an CollectionCallbacks object recursively.
*/
var buildPropertyCallbacks = (properties) => {
	if (!properties) return void 0;
	const propertyCallbacks = {};
	if (hasPropertyCallbacks(properties, "afterRead")) propertyCallbacks.afterRead = async (props) => {
		const row = props.row;
		const processedValues = await processProperties(properties, row, row, props, "afterRead");
		return {
			...props.row,
			...processedValues
		};
	};
	if (hasPropertyCallbacks(properties, "beforeSave")) propertyCallbacks.beforeSave = async (props) => {
		return await processProperties(properties, props.values, props.previousValues ?? {}, props, "beforeSave");
	};
	return Object.keys(propertyCallbacks).length > 0 ? propertyCallbacks : void 0;
};
//#endregion
//#region src/util/callback-errors.ts
/**
* The code a write carries when a collection callback rejected it and did not
* say how. Distinct from `INVALID_INPUT`, which the framework's own validation
* raises: this one means *your* rule refused, so the message is the author's.
*
* `details.stage` names which callback refused — `beforeSave`, `beforeDelete`,
* `afterSave` or `afterDelete`. An `after*` hook runs inside the write's
* transaction, so a throw there rolls the row back too; the caller is told the
* write did not happen and which hook decided that.
*/
var CALLBACK_REJECTED = "CALLBACK_REJECTED";
/**
* Turn whatever a user callback threw into something the API layer can answer
* with.
*
* ### Why a plain `throw` has to mean 400
*
* Both `docs/collections/callbacks.md` ("Throw an error to **block the save**")
* and `docs/backend/hooks.md` ("the operation is rejected with an HTTP 400
* error response") promised this, and neither delivered it: an `Error` thrown
* from `beforeSave` reached the client as
*
*     500 {"error":{"message":"Internal Server Error","code":"INTERNAL_ERROR"}}
*
* with the author's message visible only in the server log, because the error
* normalizer masks 5xx bodies — correctly, since a 500 is by definition
* something the caller must not be told about.
*
* But a callback is not the server failing. It is the application speaking, in
* code its author wrote, about a request its author judged invalid. The
* conservative reading — "an unrecognised throw might be a real bug, so 500" —
* costs every validation rule its message and makes the documented example
* wrong. A rule that wants a 500 can still raise one explicitly.
*
* ### Why `after*` comes through here too
*
* `afterSave` and `afterDelete` run inside the write's transaction and are
* awaited, so a throw in one aborts the transaction: the row is not there when
* the request ends. Left unconverted, the caller saw a 500 for a write that a
* rule deliberately undid, and had no way to tell that from a database outage.
* Converted, it is the same 400 `CALLBACK_REJECTED` a `before*` hook produces,
* with `stage` naming the hook that refused.
*
* ### What passes through untouched
*
* Anything that already carries a status: `RebaseApiError` from
* `@rebasepro/types` (the browser-safe class a `config/collections/*.ts` file
* can import — the collection file is bundled into the admin SPA, so it may not
* import the server package), and the server's own `ApiError`, recognised
* structurally rather than by `instanceof` because a monorepo can resolve two
* copies of a package and `instanceof` is false across them.
*
* @param error What the callback threw.
* @param stage The callback name, for the log line.
* @param path  The collection path, for the log line.
*/
function toCallbackError(error, stage, path) {
	if (error !== null && typeof error === "object") {
		const carried = error;
		if (typeof carried.statusCode === "number" || typeof carried.status === "number") return error;
	}
	return new RebaseApiError(error instanceof Error ? error.message : typeof error === "string" ? error : `${stage} rejected the write`, {
		status: 400,
		code: CALLBACK_REJECTED,
		details: {
			stage,
			path
		},
		cause: error
	});
}
/**
* The refusal a callback expresses by returning `false` rather than throwing.
*
* `beforeDelete` is typed `boolean | void` and documented as "return false or
* throw to block deletion". Returning `false` did stop the delete — and then the
* route answered `204 No Content`, which says the row is gone. The admin panel
* removed it from the list, a client that trusted the status dropped it from its
* cache, and the next reload brought it back. A veto that reports success is
* worse than no veto.
*
* 403, not the 400 a throw produces: a throw carries the author's message and
* reads as "this input is wrong", while `false` is a flat refusal with no
* explanation — the server understood the request and will not perform it. The
* code is the same either way, so a client can handle both in one branch.
*
* @param stage The callback name, for `details.stage`.
* @param path  The collection path, for `details.path`.
*/
function callbackRefusal(stage, path) {
	return new RebaseApiError(`${stage} refused the operation`, {
		status: 403,
		code: CALLBACK_REJECTED,
		details: {
			stage,
			path
		}
	});
}
/**
* The collection a callback tier is about to be handed — or a refusal.
*
* Every callback props type declares `collection: CollectionConfig`,
* non-optional, and the documented global-callback examples dereference it
* (`if (collection.slug === "audit_log") return;`). The driver resolves that
* value from the registry, which answers `undefined` for a path it does not
* know, so the tiers used to receive it through a cast that quietly dropped the
* `| undefined`. A global `beforeSave` reading `collection.slug` then threw a
* `TypeError` that `toCallbackError` reported as a 400 `CALLBACK_REJECTED` —
* the author's own rule blamed for a value the framework failed to supply.
*
* Skipping the tier instead is not available. `afterRead` is documented as the
* place for "security-critical redaction (PII masking, row filtering) — no read
* path bypasses it", and a tier that silently does not run on the paths the
* registry cannot resolve is precisely such a bypass.
*
* So the contract is the third option: **a callback tier never runs without a
* collection**, because a path that has none is refused before one can. That
* costs nothing, because it is already true — every read and every write
* reaches the database through `getCollectionByPath` in the driver's collection
* helpers, which raises this same "not found" for the same paths. Asking here
* only asks earlier, while the answer is still a 404 about the request instead
* of a `TypeError` attributed to the application's hook.
*
* @param collection The collection the driver resolved, if it resolved one.
* @param path       The collection path, for the message and `details`.
*/
function requireCallbackCollection(collection, path) {
	if (!collection) throw new RebaseApiError(`Collection not found: ${path}`, {
		status: 404,
		code: "NOT_FOUND",
		details: { path }
	});
	return collection;
}
/**
* The client a callback reads as `context.client` — or a refusal naming why
* there is none.
*
* A driver is constructed before the server client exists, and
* `initializeRebaseBackend` hands it the client afterwards. So a driver can
* run callbacks without one: constructed on its own, or missed by that
* injection, which is how a second database's callbacks once ran with
* `context.client === undefined` while the type said it was there.
*
* Called from a getter on the context rather than when the context is built,
* because most callbacks never touch `client` and must not fail for its
* absence. The one that does gets this sentence instead of "Cannot read
* properties of undefined". A 500, not the 400 `toCallbackError` makes of a
* plain throw: the callback is not at fault, the server's wiring is.
*/
function requireCallbackClient(client) {
	if (!client) throw new RebaseApiError("`context.client` is not available to this callback: the driver running it was never given the server client. `initializeRebaseBackend` attaches it to every data source's driver at boot, so this driver was constructed outside it or missed. Queries do not need it — they go through `context.data`.", {
		status: 500,
		code: "INTERNAL_ERROR"
	});
	return client;
}
//#endregion
//#region src/util/tenant.ts
/**
* The one reading of `collection.tenant`.
*
* Four things have to agree for a tenant-scoped collection to work — the
* column, the RLS policy, the value stamped on insert and the index — and
* before this they were four hand-written declarations that nothing compared.
* The three that are schema become a {@link SecurityRule} and a column effect
* derived here and in `planSchema`; the fourth, the write path, is
* {@link resolveTenantWrite}.
*
* Everything in this module is pure. The policy it builds is a `SecurityRule`
* like any other, which is what makes `db push`, the doctor, boot-ensure, the
* drift detector and the Studio treat the tenancy policy as what it is —
* generated, named, and recognisable — rather than as somebody's hand-written
* SQL that a push should offer to drop.
*/
/**
* The collection's tenancy declaration, or nothing.
*
* Read through this rather than off the object, so the one shape check —
* `tenant` is an object carrying a `field` and a `from` — is in one place. A
* config that is *wrong* is refused by `validateCollectionConfig` with a
* message; this is only asking whether there is one.
*/
function getTenantConfig(collection) {
	const tenant = collection?.tenant;
	if (!tenant || typeof tenant !== "object") return void 0;
	if (typeof tenant.field !== "string" || !tenant.field) return void 0;
	if (!tenant.from || typeof tenant.from !== "object") return void 0;
	return tenant;
}
/** The roles tenancy does not apply to, defaulted. */
function tenantBypassRoles(tenant) {
	return tenant.bypassRoles ?? DEFAULT_TENANT_BYPASS_ROLES;
}
/**
* The name of the policy a tenant declaration compiles to.
*
* Explicit — not a `getPolicyNameHash` of the rule — precisely because the
* rule's *body* is compiled with more information in some callers than in
* others (`planSchema` can resolve a relation's target collection and so knows
* the column's type; the Studio, asking only for names, cannot). A hashed name
* would then differ between the two, and the same policy would read as drift.
* A frozen identifier: see `contracts/derived-names.txt`.
*/
function tenantPolicyName(tableName) {
	return `${tableName}_tenant_scope`;
}
/** The `reason` on the index a tenant column gets. Rendered into `schema.sql`. */
var TENANT_INDEX_REASON = "tenant scope";
/**
* The condition a tenant declaration means, as a policy expression.
*
* `serverContext()` first, for the same reason every injected baseline rule
* carries it: the trusted plane runs migrations, the auth flows and the boot,
* and a restrictive policy that excluded it would not protect a tenant, it
* would stop the server from starting.
*
* Then the bypass roles, then the tenancy test itself — a claim comparison or a
* correlated `EXISTS` over the membership table, which are the two ways a
* deployment answers "which tenant is this caller in".
*/
function tenantScopeExpression(tenant) {
	const match = isTenantClaimSource(tenant.from) ? policy.compare(policy.field(tenant.field), "eq", policy.authClaim(tenant.from.claim)) : policy.existsIn({
		collection: tenant.from.membership.collection,
		where: policy.and(policy.compare(policy.field(tenant.from.membership.tenantField), "eq", policy.outerField(tenant.field)), policy.compare(policy.field(tenant.from.membership.userField), "eq", policy.authUid()))
	});
	const bypass = tenantBypassRoles(tenant);
	return bypass.length > 0 ? policy.or(policy.serverContext(), policy.rolesOverlap(bypass), match) : policy.or(policy.serverContext(), match);
}
/**
* The rule a tenant declaration compiles to, or nothing when there is none.
*
* **Restrictive**, and that is the whole design. A restrictive policy is ANDed
* with every other policy on the table, so tenancy narrows what the
* collection's own `securityRules` allow and can never widen it. A permissive
* one would OR with them, and a single `access: "public"` rule elsewhere in the
* file would take the entire tenancy boundary off without contradicting
* anything a reader could see.
*
* One rule with `operation: "all"` rather than four with `operations: [...]`:
* `FOR ALL` gives Postgres the USING clause for SELECT/UPDATE/DELETE and the
* WITH CHECK clause for INSERT/UPDATE, which is exactly the coverage wanted,
* as one policy with one name instead of four.
*/
function buildTenantSecurityRule(collection) {
	const tenant = getTenantConfig(collection);
	if (!tenant) return void 0;
	const expression = tenantScopeExpression(tenant);
	return {
		name: tenantPolicyName(getTableName(collection)),
		mode: "restrictive",
		operation: "all",
		condition: expression,
		check: expression
	};
}
/**
* An id, however it arrived.
*
* A tenant field may be a `belongsTo` relation or a `reference`, and those
* arrive over the wire as `{ id }` envelopes as often as bare ids. Comparing
* the envelope to a bare id would refuse every correct write with
* `TENANT_MISMATCH`, which is the most confusing possible failure — the caller
* sent exactly the tenant they belong to.
*/
function tenantIdOf(value) {
	if (value === null || value === void 0) return value;
	if (typeof value === "object") {
		const id = value.id;
		return id === void 0 ? value : id;
	}
	return value;
}
/**
* Compare two tenant ids as the database will.
*
* Stringified, because JSON has one number type and Postgres has several: a
* caller sending `"42"` for a `bigint` tenant column is writing the same row as
* one sending `42`, and Postgres agrees after the cast. Refusing one of them
* would be an API rule the database does not have.
*/
function sameTenant(a, b) {
	if (a === null || a === void 0 || b === null || b === void 0) return false;
	return String(tenantIdOf(a)) === String(tenantIdOf(b));
}
/**
* Stamp, or refuse, the tenant on a write.
*
* Three refusals, and each exists because the alternative lands somewhere
* worse:
*
* - **`TENANT_REQUIRED`** — the caller has no tenant, or belongs to several and
*   named none. Stamping a guess would put the row in the wrong tenant; letting
*   it through would write a NULL into a `NOT NULL` column and surface as a
*   23502 naming a column the caller never wrote.
* - **`TENANT_MISMATCH`** — the caller named a tenant that is not theirs. The
*   database refuses this too, through the policy's `WITH CHECK`, but as a
*   42501 "new row violates row-level security policy" with no mention of which
*   field or why. Refused here so the answer names the field.
* - **`TENANT_IMMUTABLE`** — an update that moves a row to another tenant. RLS
*   would allow it whenever the caller belongs to both, and it is almost never
*   what anybody meant: it takes the row out of one tenant's history and drops
*   it into another's, with no trace on either side. A deliberate move is a
*   `bypassRoles` operation.
*
* A bypass caller is exempt from all three: they are trusted across tenants by
* declaration, and stamping their write would silently confine a support
* operator's row to whichever tenant they happen to carry.
*/
function resolveTenantWrite(input) {
	const { tenant, values, status, callerTenants, bypass, previousValues, slug } = input;
	const field = tenant.field;
	const complete = input.callerTenantsComplete !== false;
	/** Is `value` one the caller may write? Unknown counts as yes — see `callerTenantsComplete`. */
	const callerHas = (value) => callerTenants.some((t) => sameTenant(t, value)) || !complete;
	if (bypass) return { values };
	const provided = values[field];
	if (!(status !== "existing")) {
		if (provided === void 0) return { values };
		const previous = previousValues?.[field];
		if (previous !== void 0 && !sameTenant(provided, previous)) return { refusal: {
			code: "TENANT_IMMUTABLE",
			field,
			message: `'${field}' is the tenant '${slug}' rows belong to, and a row cannot change tenant. This update would move it from '${String(tenantIdOf(previous))}' to '${String(tenantIdOf(provided))}'. Create the row in the other tenant and delete this one, or perform the move with a role listed in \`tenant.bypassRoles\`.`
		} };
		if (previous === void 0 && !callerHas(provided)) return { refusal: mismatch(field, slug, provided, callerTenants) };
		return { values };
	}
	if (provided === void 0 || provided === null || provided === "") {
		if (callerTenants.length === 1) return { values: {
			...values,
			[field]: tenantIdOf(callerTenants[0])
		} };
		return { refusal: {
			code: "TENANT_REQUIRED",
			field,
			message: callerTenants.length === 0 ? `'${slug}' is scoped to a tenant and this request carries none, so there is nothing to write into '${field}'. ` + sourceHint(tenant) : `'${slug}' is scoped to a tenant and this caller belongs to ${callerTenants.length} of them, so '${field}' cannot be inferred. Send it on the write — it must be one the caller belongs to.`
		} };
	}
	if (!callerHas(provided)) return { refusal: mismatch(field, slug, provided, callerTenants) };
	return { values };
}
function mismatch(field, slug, provided, callerTenants) {
	return {
		code: "TENANT_MISMATCH",
		field,
		message: `'${field}' names tenant '${String(tenantIdOf(provided))}', which this caller does not belong to, so the write to '${slug}' would be refused by the database as well. ` + (callerTenants.length === 0 ? "This request carries no tenant at all." : `The caller's ${callerTenants.length === 1 ? "tenant is" : "tenants are"} ` + callerTenants.map((t) => `'${String(tenantIdOf(t))}'`).join(", ") + ".")
	};
}
/** Where a caller's tenant was supposed to come from, for the 400. */
function sourceHint(tenant) {
	return isTenantClaimSource(tenant.from) ? `The tenant comes from the '${tenant.from.claim}' claim on the caller's token; this one has no such claim. Sign in, or add the claim in the custom-claims hook.` : `The tenant comes from rows of '${tenant.from.membership.collection}' whose '${tenant.from.membership.userField}' is the caller; this caller has none.`;
}
//#endregion
//#region src/util/auth-default-policies.ts
/**
* Default RLS policies injected by the schema generator.
*
* Rebase's enforcement model is unified: authenticated (user-context) requests
* run under the restricted `rebase_user` role, so Postgres RLS binds *every*
* statement — reads and writes. A collection's `securityRules` are the whole
* authorization model. The server context (auth flows, migrations, raw
* `rebase.sql`) runs as the owner and bypasses RLS.
*
* `rebase.dataAsAdmin` is **not** in that set, despite the name: it is scoped as
* `{ uid: "service", roles: ["admin"] }`, so it runs as `rebase_user` like any
* other caller and clears the baseline below through the *admin* arm, not the
* server arm. Which is why `disableDefaultPolicies` plus a lone
* `policy.serverContext()` rule locks it out too.
*
* Because RLS default-denies, every collection is **locked by default**: with
* no rules, only the server context and admins can touch it. The generator
* injects that safe baseline:
*
* **For every collection**
*  1. A permissive **server-or-admin SELECT** grant.
*  2. A permissive **server-or-admin write** grant (insert/update/delete).
*
* Author `securityRules` are permissive and OR together, so explicit rules only
* *broaden* access from this locked baseline (e.g. "users read/write their own
* rows").
*
* **For auth collections additionally**
*  3. A permissive **self SELECT** grant (`id = rebase.uid()`), so users can read
*     their own row (profile, session bootstrap) without every app re-declaring
*     it.
*  4. A **restrictive** admin write gate. Restrictive policies are AND'd with
*     every other policy, so a write is rejected unless the caller is an admin
*     (or the server context) — even if the author also wrote a permissive rule
*     such as "a user may edit their own row". Without this, a permissive owner
*     rule would let a user change their own `roles`.
*
* The server context is recognised as `rebase.uid() IS NULL` (`policy.serverContext()`)
* — the built-in flows that run without a user (signup, migrations) set no user
* GUC — which also lets the owner connection satisfy these policies even under
* FORCE RLS. A *user* request never reaches that state: an anonymous one carries
* `ANONYMOUS_USER_ID`, precisely so it cannot pass for the server here.
*
* **For a collection declaring `tenant`, additionally**
*  5. A **restrictive** tenancy gate for every operation. Same kind of thing as
*     the admin write gate and injected for the same reason: it is ANDed with
*     every other policy, so it narrows what the author's permissive rules
*     grant and can never widen them. See `./tenant.ts`.
*
* Opt out with `disableDefaultPolicies: true` to take full responsibility for
* the collection's RLS. The *restrictive* rules are not part of that opt-out:
* dropping a rule that can only remove access could express nothing but "let
* more people in", which is what the flag already does by removing the grants.
*/
var SERVER_OR_ADMIN_EXPR$1 = policy.or(policy.serverContext(), policy.rolesOverlap(["admin"]));
/** Write operations that must be admin-gated by default on auth collections. */
var DEFAULT_GUARDED_OPS = [
	"insert",
	"update",
	"delete"
];
/** Whether a collection is flagged as an authentication collection. */
function isAuthCollection$1(collection) {
	const auth = collection.auth;
	return auth === true || typeof auth === "object" && auth?.enabled === true;
}
/** The property marked as the row id (falls back to `id`). */
function getIdPropertyName$1(collection) {
	for (const [name, prop] of Object.entries(collection.properties ?? {})) if (prop && typeof prop === "object" && "isId" in prop && prop.isId) return name;
	return "id";
}
/**
* Returns the security rules that should be applied to a collection: the
* author's explicit `securityRules` plus the framework defaults described in
* the module doc (baseline server/admin read for all collections; self-read
* and the admin write gate for auth collections).
*
* Collections that opt out via `disableDefaultPolicies` are returned unchanged.
*/
/**
* The restrictive write gate for an auth collection.
*
* Restrictive, so it is ANDed with everything else: whatever an author's
* permissive rules allow, a write to this table still has to satisfy this too.
* It is the only thing standing between "users may edit their own row" and
* "users may grant themselves any role".
*/
function adminWriteGate(tableName) {
	return {
		name: `${tableName}_require_admin_write`,
		mode: "restrictive",
		operations: [...DEFAULT_GUARDED_OPS],
		condition: SERVER_OR_ADMIN_EXPR$1,
		check: SERVER_OR_ADMIN_EXPR$1
	};
}
/**
* The restrictive tenancy policy, as a list of zero or one.
*
* A list so the two call sites can splice it in without a conditional, and a
* separate function so it is obvious that it is injected on *both* paths —
* including the `disableDefaultPolicies` one, where it is the only permissive-
* looking thing that stays. See `./tenant.ts`.
*/
function tenantRule(collection) {
	const rule = buildTenantSecurityRule(collection);
	return rule ? [rule] : [];
}
function getEffectiveSecurityRules(collection) {
	const explicit = [...collection.securityRules ?? []];
	const tableName = getTableName(collection);
	const injected = [];
	if (isPostgresCollectionConfig(collection) && collection.disableDefaultPolicies) return [
		...explicit,
		...tenantRule(collection),
		...isAuthCollection$1(collection) ? [adminWriteGate(tableName)] : []
	];
	injected.push({
		name: `${tableName}_default_admin_read`,
		operations: ["select"],
		condition: SERVER_OR_ADMIN_EXPR$1
	});
	injected.push({
		name: `${tableName}_default_admin_write`,
		operations: [...DEFAULT_GUARDED_OPS],
		condition: SERVER_OR_ADMIN_EXPR$1,
		check: SERVER_OR_ADMIN_EXPR$1
	});
	if (isAuthCollection$1(collection)) {
		injected.push({
			name: `${tableName}_default_self_read`,
			operations: ["select"],
			condition: policy.compare(policy.field(getIdPropertyName$1(collection)), "eq", policy.authUid())
		});
		injected.push(adminWriteGate(tableName));
	}
	injected.push(...tenantRule(collection));
	return [...explicit, ...injected];
}
/**
* The framework defaults that {@link getEffectiveSecurityRules} would add to a
* collection, without the author's own rules.
*
* These policies appear in the database under names the author never wrote, and
* a permissive policy ORs with every other permissive policy — so someone
* reading their `securityRules` and then the real ACL sees more access than they
* declared. Dropping them by hand does nothing either: `db push` is declarative,
* so the next push asserts them again. Callers use this to say, in the generated
* DDL, which policies are injected and how to take them off.
*/
function getInjectedSecurityRules(collection) {
	if (isPostgresCollectionConfig(collection) && collection.disableDefaultPolicies) return [...tenantRule(collection), ...isAuthCollection$1(collection) ? [adminWriteGate(getTableName(collection))] : []];
	const explicitCount = (collection.securityRules ?? []).length;
	return getEffectiveSecurityRules(collection).slice(explicitCount);
}
/**
* Every policy name `rebase db push` would write for a collection.
*
* This is the answer to "did the codebase produce this live policy?", and it is
* more than `securityRules.map(r => r.name)` for two reasons:
*
*  - a rule without an explicit `name` compiles to `<table>_<op>_<hash>`, one
*    per operation, so comparing `rule.name` to `policyname` never matches it;
*  - the generator also injects the safe-by-default baseline
*    (`<table>_default_admin_*`), which is in no collection's `securityRules`.
*
* Every UI that flags drift has to get both right, and each one that derived it
* by hand got a different subset — which is how four policies *Rebase itself
* wrote* came to be badged as hand-written drift on every table in a project,
* with a button offering to import them back into the codebase that produced
* them. There is one derivation now, and this is it.
*/
function getGeneratedPolicyNames(collection) {
	return getPolicyNamesForRules(getEffectiveSecurityRules(collection), getTableName(collection));
}
//#endregion
//#region src/util/junction-policies.ts
var SERVER_OR_ADMIN_EXPR = policy.or(policy.serverContext(), policy.rolesOverlap(["admin"]));
/**
* Walk every collection's resolved relations and aggregate the junction tables
* they declare. Two collections may declare the same junction from opposite
* sides (posts→tags and tags→posts through `posts_tags`); both become
* `declaringSides` of one spec, so derived write grants consider both.
*/
function resolveJunctionSpecs(collections) {
	const specs = /* @__PURE__ */ new Map();
	for (const collection of collections) {
		const resolved = resolveCollectionRelations(collection);
		for (const relation of Object.values(resolved)) {
			if (!isManyToMany(relation)) continue;
			const targetCollection = relation.target();
			if (!targetCollection) continue;
			const rawName = relation.through.table;
			const table = rawName.includes(".") ? rawName.split(".").pop() : rawName;
			const schema = "public";
			const source = {
				collection,
				junctionColumn: relation.through.sourceColumn,
				relation
			};
			const target = {
				collection: targetCollection,
				junctionColumn: relation.through.targetColumn
			};
			const existing = specs.get(table);
			if (!existing) specs.set(table, {
				table,
				schema,
				endpoints: [source, target],
				declaringSides: [source],
				properties: mergeJunctionPayload({}, relation.through.properties, table, collection)
			});
			else {
				existing.properties = mergeJunctionPayload(existing.properties, relation.through.properties, table, collection);
				if (!existing.declaringSides.some((s) => s.collection === collection)) existing.declaringSides.push(source);
			}
		}
	}
	return specs;
}
/**
* Fold one side's `through.properties` into the junction's, refusing a
* disagreement rather than picking a winner.
*
* Both ends of a link may declare it — `posts.tags` and `tags.posts` are one
* junction — and each end may name the payload. Only one table gets created, so
* two descriptions of `role` that are not the same description are a question
* with no correct answer: whichever won, one of the two collections would be
* writing through a column it does not think it has. Compared structurally, so
* two sides that spell the same property twice (the normal case, and the one
* the docs recommend) are fine.
*/
function mergeJunctionPayload(into, incoming, table, collection) {
	if (!incoming || Object.keys(incoming).length === 0) return into;
	const merged = { ...into };
	for (const [key, property] of Object.entries(incoming)) {
		const already = merged[key];
		if (already && JSON.stringify(already) !== JSON.stringify(property)) throw new Error(`The junction table "${table}" is declared from more than one side, and they disagree about the payload column "${key}": "${collection.slug ?? collection.name}" describes it differently than another declaring collection does. One table is created, so both \`through.properties\` blocks have to describe the same column — or only one side should declare it.`);
		merged[key] = property;
	}
	return merged;
}
/**
* A synthetic CollectionConfig standing in for the junction during policy
* compilation and naming. Its two FK columns carry explicit `columnName`s so
* `outerField` operands resolve to the exact columns the CREATE TABLE emitted,
* whatever their casing.
*
* The payload columns are here too, exactly as authored. That is what lets one
* reading of a `Property` serve the junction as well as a collection: the
* schema planner plans these columns with the same function it plans a
* collection's with, and the write path validates a `_pivot` against them with
* the same validator a row's values go through. A second description of a
* payload column anywhere is a second description that can disagree.
*/
function getJunctionCollectionConfig(spec) {
	return buildJunctionCollectionConfig({
		table: spec.table,
		schema: spec.schema,
		keyColumns: spec.endpoints.map((endpoint) => endpoint.junctionColumn),
		properties: spec.properties
	});
}
/**
* The same synthetic collection, reached from a resolved relation rather than
* from a spec.
*
* The spec is built by walking every collection, which the schema planner does
* once at boot and no request path can afford. A read or a write already holds
* the relation, and a relation's `through` carries the table, both key columns
* and the payload — everything the config is made of. One builder underneath
* both, so the shape the planner emitted columns from is the shape the write
* path validates a `_pivot` against and the read path strips it with.
*/
function getJunctionConfigForRelation(through) {
	return buildJunctionCollectionConfig({
		table: through.table.includes(".") ? through.table.split(".").pop() : through.table,
		schema: "public",
		keyColumns: [through.sourceColumn, through.targetColumn],
		properties: through.properties
	});
}
function buildJunctionCollectionConfig(args) {
	const properties = {};
	for (const column of args.keyColumns) properties[column] = {
		type: "string",
		columnName: column
	};
	for (const [key, property] of Object.entries(args.properties)) {
		if (key === JUNCTION_PIVOT_KEY || key in properties) continue;
		properties[key] = property;
	}
	return {
		slug: args.table,
		name: args.table,
		table: args.table,
		schema: args.schema,
		properties
	};
}
/** The property marked as the row id (falls back to `id`). */
function getIdPropertyName(collection) {
	for (const [name, prop] of Object.entries(collection.properties ?? {})) if (prop && typeof prop === "object" && "isId" in prop && prop.isId) return name;
	return "id";
}
/** `EXISTS (SELECT 1 FROM endpoint WHERE endpoint.pk = junction.fk [AND extra])`. */
function existsEndpoint(endpoint, extra) {
	const correlation = policy.compare(policy.field(getIdPropertyName(endpoint.collection)), "eq", policy.outerField(endpoint.junctionColumn));
	return policy.existsIn({
		collection: endpoint.collection.slug,
		where: extra ? policy.and(correlation, extra) : correlation
	});
}
/**
* Whether a parent-rule expression keeps its meaning when moved inside the
* junction's `EXISTS` subquery — and the re-scoped copy if it does.
*
* Returns `null` when the rule cannot be embedded faithfully: `raw` SQL
* anywhere (its `{column}` placeholders would bind to the junction), or an
* `outerField` inside a nested `existsIn` (it would bind to the junction while
* the author meant the parent, and no operand can express "the middle scope").
* Top-level `outerField`s are rewritten to `field`, which is what they meant.
*/
function embedParentExpression(expr, depth = 0) {
	switch (expr.kind) {
		case "raw": return null;
		case "and":
		case "or": {
			const parts = [];
			for (const child of expr.operands) {
				const embedded = embedParentExpression(child, depth);
				if (!embedded) return null;
				parts.push(embedded);
			}
			return expr.kind === "and" ? policy.and(...parts) : policy.or(...parts);
		}
		case "not": {
			const embedded = embedParentExpression(expr.operand, depth);
			return embedded ? policy.not(embedded) : null;
		}
		case "existsIn": {
			const where = embedParentExpression(expr.where, depth + 1);
			return where ? policy.existsIn({
				collection: expr.collection,
				where
			}) : null;
		}
		case "compare": {
			const left = embedOperand(expr.left, depth);
			const right = embedOperand(expr.right, depth);
			if (!left || !right) return null;
			return {
				...expr,
				left,
				right
			};
		}
		default: return expr;
	}
}
/** Re-scope an operand, or return `null` if its binding cannot be preserved. */
function embedOperand(operand, depth) {
	if (operand.kind === "outerField") {
		if (depth === 0) return policy.field(operand.name);
		return null;
	}
	return operand;
}
/** Does the rule cover the `update` operation? */
function coversUpdate(rule) {
	return getPolicyOperations(rule).some((op) => op === "update" || op === "all");
}
/**
* The full derived policy set for a junction table: the locked server/admin
* baseline, the endpoint-visibility read grant, inherited write grants, and
* inherited restrictive gates. Returns `[]` when every declaring collection set
* `disableDefaultPolicies` — the junction is then the author's to police, and
* stays locked (RLS is still enabled) until they write policies for it.
*/
function getJunctionSecurityRules(spec) {
	if (spec.declaringSides.every((side) => isPostgresCollectionConfig(side.collection) && side.collection.disableDefaultPolicies)) return [];
	const rules = [];
	rules.push({
		name: `${spec.table}_default_admin_read`,
		operations: ["select"],
		condition: SERVER_OR_ADMIN_EXPR
	});
	rules.push({
		name: `${spec.table}_default_admin_write`,
		operations: [
			"insert",
			"update",
			"delete"
		],
		condition: SERVER_OR_ADMIN_EXPR,
		check: SERVER_OR_ADMIN_EXPR
	});
	rules.push({
		name: `${spec.table}_default_edge_read`,
		operations: ["select"],
		condition: policy.and(existsEndpoint(spec.endpoints[0]), existsEndpoint(spec.endpoints[1]))
	});
	const writeGrants = [];
	for (const side of spec.declaringSides) {
		const updateRules = ((isPostgresCollectionConfig(side.collection) ? side.collection.securityRules : void 0) ?? []).filter(coversUpdate);
		const permissive = updateRules.filter((r) => r.mode !== "restrictive");
		const restrictive = updateRules.filter((r) => r.mode === "restrictive");
		const embeddedGates = [];
		let gatesEmbeddable = true;
		for (const gate of restrictive) {
			const using = securityRuleToConditions(gate).usingExpr;
			const embedded = using ? embedParentExpression(using) : null;
			if (!embedded) {
				gatesEmbeddable = false;
				break;
			}
			embeddedGates.push(embedded);
		}
		if (!gatesEmbeddable) continue;
		const grants = [];
		for (const rule of permissive) {
			const using = securityRuleToConditions(rule).usingExpr;
			const embedded = using ? embedParentExpression(using) : null;
			if (embedded) grants.push(embedded);
		}
		if (grants.length === 0) continue;
		const condition = embeddedGates.length > 0 ? policy.and(policy.or(...grants), ...embeddedGates) : policy.or(...grants);
		writeGrants.push(existsEndpoint(side, condition));
	}
	if (writeGrants.length > 0) rules.push({
		name: `${spec.table}_default_edge_write`,
		operations: [
			"insert",
			"update",
			"delete"
		],
		condition: writeGrants.length === 1 ? writeGrants[0] : policy.or(...writeGrants),
		check: writeGrants.length === 1 ? writeGrants[0] : policy.or(...writeGrants)
	});
	return rules;
}
//#endregion
//#region src/util/conditions.ts
/**
* Access a nested property from an object via dot notation.
*/
function getIn$1(obj, path) {
	if (!obj || !path) return void 0;
	return path.split(".").reduce((acc, part) => acc && acc[part], obj);
}
var operationsRegistered = false;
/**
* Register custom JSON Logic operations for Rebase.
* Call this once at app initialization.
*/
function registerConditionOperations() {
	if (operationsRegistered) return;
	jsonLogic.add_operation("hasRole", function(roleId) {
		return this?.user?.roles?.includes(roleId) ?? false;
	});
	jsonLogic.add_operation("hasAnyRole", function(roleIds) {
		if (!this?.user?.roles || !Array.isArray(roleIds)) return false;
		return roleIds.some((role) => this.user.roles.includes(role));
	});
	jsonLogic.add_operation("isToday", (timestamp) => {
		if (!timestamp) return false;
		const date = new Date(timestamp);
		const today = /* @__PURE__ */ new Date();
		return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
	});
	jsonLogic.add_operation("isPast", (timestamp) => {
		if (!timestamp) return false;
		return timestamp < Date.now();
	});
	jsonLogic.add_operation("isFuture", (timestamp) => {
		if (!timestamp) return false;
		return timestamp > Date.now();
	});
	operationsRegistered = true;
}
/**
* Evaluate a condition against the given context.
*
* A condition may be stated as a literal instead of a rule — `hidden: true`
* rather than `hidden: { "==": [1, 1] }` — and a literal is already its own
* answer, so it is returned rather than handed to the evaluator.
*/
function evaluateCondition(rule, context) {
	if (typeof rule === "boolean") return rule;
	registerConditionOperations();
	return jsonLogic.apply(rule, context);
}
/**
* Convert a value to a format suitable for JSON Logic evaluation.
* Specifically handles Date objects by converting them to Unix timestamps.
*/
function serializeValueForConditions(value) {
	if (value === null || value === void 0) return value;
	if (value instanceof Date) return value.getTime();
	if (typeof value?.toMillis === "function") return value.toMillis();
	if (typeof value?.toDate === "function") return value.toDate().getTime();
	if (Array.isArray(value)) return value.map(serializeValueForConditions);
	if (typeof value === "object") {
		const result = {};
		for (const key of Object.keys(value)) result[key] = serializeValueForConditions(value[key]);
		return result;
	}
	return value;
}
/**
* Build a ConditionContext from the current property resolution context.
*/
function buildConditionContext(params) {
	const { propertyKey, values, previousValues, path, entityId, index, authController } = params;
	const user = authController.user;
	const serializedValues = serializeValueForConditions(values ?? {});
	return {
		values: serializedValues,
		previousValues: serializeValueForConditions(previousValues ?? values ?? {}),
		propertyValue: propertyKey ? getIn$1(serializedValues, propertyKey) : void 0,
		path,
		entityId,
		isNew: !entityId,
		index,
		user: {
			uid: user?.uid ?? "",
			email: user?.email ?? null,
			displayName: user?.displayName ?? null,
			photoURL: user?.photoURL ?? null,
			roles: (user?.roles ?? []).map((r) => typeof r === "string" ? r : r.id)
		},
		now: Date.now()
	};
}
/**
* Apply PropertyConditions to a resolved property, evaluating all JSON Logic rules.
*/
//#endregion
//#region src/util/pg-column-to-property.ts
/**
* Maps a PostgreSQL column data type to a Rebase property type.
*/
function pgTypeToRebaseProperty(column) {
	const { column_name, data_type, udt_name, is_nullable, column_default, character_maximum_length, enum_values } = column;
	const required = is_nullable === "NO";
	const prettifiedName = prettifyIdentifier(column_name);
	const isAutoId = column_default != null && (column_default.includes("nextval") || column_default.includes("gen_random_uuid") || column_default.includes("uuid_generate") || column_default.includes("identity"));
	if (data_type === "USER-DEFINED" && enum_values && enum_values.length > 0) return {
		type: "string",
		name: prettifiedName,
		enum: enum_values.map((v) => ({
			id: v,
			label: prettifyIdentifier(v)
		})),
		validation: required ? { required: true } : void 0
	};
	const dt = data_type.toLowerCase();
	switch (dt) {
		case "character varying":
		case "varchar":
		case "text":
		case "char":
		case "character":
		case "citext": {
			let colType = "varchar";
			if (dt === "text" || dt === "citext") colType = "text";
			if (dt === "char" || dt === "character") colType = "char";
			const declaredLength = colType === "text" ? null : character_maximum_length;
			const prop = {
				type: "string",
				name: prettifiedName,
				columnType: colType,
				validation: required || declaredLength ? {
					...required ? { required: true } : {},
					...declaredLength ? { max: declaredLength } : {}
				} : void 0
			};
			if (isAutoId) prop.isId = "manual";
			return prop;
		}
		case "uuid": {
			const prop = {
				type: "string",
				name: prettifiedName,
				validation: required ? { required: true } : void 0
			};
			if (isAutoId) prop.isId = "uuid";
			return prop;
		}
		case "integer":
		case "bigint":
		case "smallint": {
			const prop = {
				type: "number",
				name: prettifiedName,
				columnType: dt === "bigint" ? "bigint" : "integer",
				validation: {
					...required ? { required: true } : {},
					integer: true
				}
			};
			if (isAutoId) prop.isId = "increment";
			return prop;
		}
		case "serial":
		case "bigserial":
		case "smallserial": return {
			type: "number",
			name: prettifiedName,
			columnType: dt === "bigserial" ? "bigserial" : "serial",
			isId: "increment",
			validation: {
				...required ? { required: true } : {},
				integer: true
			}
		};
		case "numeric":
		case "decimal":
		case "real":
		case "double precision": {
			let colType = "numeric";
			if (dt === "real") colType = "real";
			if (dt === "double precision") colType = "double precision";
			return {
				type: "number",
				name: prettifiedName,
				columnType: colType,
				validation: required ? { required: true } : void 0
			};
		}
		case "boolean": return {
			type: "boolean",
			name: prettifiedName,
			validation: required ? { required: true } : void 0
		};
		case "timestamp with time zone":
		case "timestamp without time zone":
		case "timestamp":
		case "timestamptz":
		case "date":
		case "time with time zone":
		case "time without time zone":
		case "time": {
			let colType = "timestamp";
			if (dt.startsWith("date")) colType = "date";
			if (dt.startsWith("time ") || dt === "time") colType = "time";
			return {
				type: "date",
				name: prettifiedName,
				columnType: colType,
				validation: required ? { required: true } : void 0
			};
		}
		case "jsonb":
		case "json": return {
			type: "map",
			name: prettifiedName,
			columnType: dt === "jsonb" ? "jsonb" : "json",
			keyValue: true,
			properties: {}
		};
		case "array":
		case "ARRAY": {
			let innerType = "string";
			let colType = void 0;
			if (udt_name === "_text" || udt_name === "_varchar") {
				innerType = "string";
				colType = "text[]";
			} else if (udt_name === "_int4" || udt_name === "_int2" || udt_name === "_int8") {
				innerType = "number";
				colType = "integer[]";
			} else if (udt_name === "_bool") {
				innerType = "boolean";
				colType = "boolean[]";
			} else if (udt_name === "_numeric") {
				innerType = "number";
				colType = "numeric[]";
			}
			return {
				type: "array",
				name: prettifiedName,
				columnType: colType,
				of: { type: innerType }
			};
		}
		default: return {
			type: "string",
			name: prettifiedName,
			validation: required ? { required: true } : void 0
		};
	}
}
/**
* Builds a collection description from PostgreSQL table metadata.
* This is used when creating a new collection from an existing database table.
*/
function buildCollectionFromTableMetadata(tableName, metadata) {
	const properties = {};
	const propertiesOrder = [];
	const relations = [];
	const securityRules = [];
	for (const column of metadata.columns) {
		const property = pgTypeToRebaseProperty(column);
		if (property) {
			const propRecord = property;
			Object.keys(propRecord).forEach((key) => propRecord[key] === void 0 && delete propRecord[key]);
			const key = firstFreeKey([toWireKey(column.column_name), column.column_name], { has: (candidate) => candidate in properties });
			if (key !== column.column_name) propRecord.columnName = column.column_name;
			properties[key] = property;
			propertiesOrder.push(key);
		}
	}
	if (metadata.foreignKeys) for (const fk of metadata.foreignKeys) {
		const relName = toWireKey(fk.column_name.endsWith("_id") ? fk.column_name.substring(0, fk.column_name.length - 3) : fk.column_name);
		relations.push({
			id: fk.column_name,
			relationName: relName,
			target: fk.foreign_table_name,
			kind: "belongsTo",
			localKey: fk.column_name
		});
	}
	if (metadata.junctions) for (const junction of metadata.junctions) {
		const relName = junction.target_table_name;
		relations.push({
			id: junction.target_table_name + "_relation",
			relationName: relName,
			target: junction.target_table_name,
			kind: "manyToMany",
			through: {
				table: junction.junction_table_name,
				sourceColumn: junction.source_column_name,
				targetColumn: junction.target_column_name
			}
		});
	}
	if (metadata.policies) for (const policy of metadata.policies) {
		let operations = [];
		switch (policy.cmd) {
			case "ALL":
				operations = ["all"];
				break;
			case "SELECT":
				operations = ["select"];
				break;
			case "INSERT":
				operations = ["insert"];
				break;
			case "UPDATE":
				operations = ["update"];
				break;
			case "DELETE":
				operations = ["delete"];
				break;
		}
		const qual = policy.qual ? rewriteLegacyRlsFunctions(policy.qual) : void 0;
		const withCheck = policy.with_check ? rewriteLegacyRlsFunctions(policy.with_check) : void 0;
		if (qual) securityRules.push({
			name: policy.policy_name,
			operations,
			roles: policy.roles ?? [],
			using: qual,
			...withCheck ? { withCheck } : {}
		});
		else securityRules.push({
			name: policy.policy_name,
			operations,
			roles: policy.roles ?? []
		});
	}
	return {
		name: prettifyIdentifier(tableName),
		slug: tableName,
		table: tableName,
		properties,
		propertiesOrder,
		...relations.length > 0 ? { relations } : {},
		...securityRules.length > 0 ? { securityRules } : {}
	};
}
//#endregion
//#region src/util/string-column-length.ts
/**
* The length a bounded string column is declared with when the property does
* not say. Historical: it is what the DDL generator hardcoded, kept so that
* regenerating an existing schema does not silently redefine its columns.
*/
var DEFAULT_STRING_COLUMN_LENGTH = 255;
/**
* How wide a `varchar`/`char` column should be for a given property.
*
* One definition, three call sites, because they used to disagree. For the same
* `columnType: "varchar"` property the DDL generator emitted `VARCHAR(255)`
* while the Drizzle generator emitted a bare `varchar("col")` — which Postgres
* reads as *unbounded* — so which of the two you ran decided whether the column
* had a limit at all. Introspection then dropped the length entirely, so reading
* an existing `character varying(500)` column back and regenerating it produced
* a `VARCHAR(255)`: a silent narrowing of a column with data already in it.
*
* `validation.max` is the property's own statement about how long the value may
* be, so it is the only sensible source for the column's width — and it keeps
* the constraint the database enforces in step with the one the app enforces,
* rather than inventing a second, different limit underneath it.
*/
function resolveStringColumnLength(prop) {
	const max = prop.validation?.max;
	return typeof max === "number" && Number.isInteger(max) && max > 0 ? max : 255;
}
//#endregion
//#region src/util/internal-tables.ts
/**
* The tables Rebase creates for its own bookkeeping, and the SQL that keeps the
* end-user role away from them.
*
* ## Why this exists
*
* Authenticated requests run as {@link REBASE_USER_ROLE}, and the boot-time role
* provisioning grants that role `SELECT, INSERT, UPDATE, DELETE` on every table
* in the schemas a project uses — including `rebase`, because a project's own
* collections are allowed to live there (the scaffold puts `users` there). It
* also sets `ALTER DEFAULT PRIVILEGES`, so a table created *later* by the
* migrating role inherits the same grant.
*
* Every framework-internal table is created later: auth's tables come up during
* `initializeAuth`, `api_keys` during route mounting, `cron_logs` when the first
* job registers, `idempotency_keys` on the first request that carries a key. So
* they all inherited full DML for the end-user role — and none of them enables
* row-level security, because none of them is a collection with
* `securityRules`. Measured on a freshly provisioned database, `SET ROLE
* rebase_user` could read `rebase.refresh_tokens` (session token hashes),
* `rebase.mfa_factors` (`secret_encrypted`), `rebase.recovery_codes`, and
* `rebase.api_keys` (including its `admin` flag), and insert into
* `rebase.app_config`.
*
* Nothing routes a user-context query at those tables today, so this was not
* reachable over the API. That is the wrong thing to depend on: the documented
* model is that RLS is the authorization boundary, and these tables sat outside
* it. The boundary is now a privilege boundary instead — the role simply cannot
* address them.
*
* ## Why REVOKE rather than ENABLE ROW LEVEL SECURITY
*
* RLS with no policy denies every row, which is the same outcome, but it is the
* *weaker* statement: it leaves the grant in place, so a later policy — or a
* `FORCE` flag cleared by some future migration — reopens the table. There is no
* row of `refresh_tokens` any end user should ever reach, so the honest encoding
* is "this role has no privilege here at all". It also keeps the owner
* connection (which auth actually runs on) completely unaffected.
*
* ## Keeping it true
*
* `packages/rls-check` scans the `rebase` schema — it used to skip it as a
* "platform" schema — and its `rls-disabled` check fires on exactly the
* condition this module removes: RLS off *and* a DML grant to a reachable role.
* So a table added here without a revoke is caught by `pnpm rls:check`, not by
* someone re-reading this file.
*/
/**
* The Postgres role authenticated requests run as.
*
* Defined here rather than in the Postgres driver because both the driver (which
* provisions the role) and this module (which revokes on its behalf) need it,
* and a second spelling of a role name is a silent no-op waiting to happen.
*/
var REBASE_USER_ROLE = "rebase_user";
/**
* Framework-internal table names, unqualified.
*
* Deliberately NOT including `users`: the auth user table is also a collection,
* with `securityRules`, RLS enabled and policies applied. Users read their own
* row through it — revoking there would break sign-in. `revokeInternalTableSql`
* now skips any table with RLS enabled, so that exception is enforced rather
* than merely remembered — and so is the same hazard for every other name here,
* any of which a project may legitimately use for a collection of its own.
*
* `atlas_schema_revisions` is Atlas's migration ledger, which lands in `rebase`
* because `db migrate apply` passes `--revisions-schema rebase`.
*
* Every entry here must also be revoked by whatever creates it, and vice versa:
* the creation-time revoke fires once, on the boot that first makes the table,
* so it cannot help a database provisioned before that revoke existed. This
* list is what the boot-time sweep in `ensureAppRole` iterates, and the sweep
* is the only thing that can repair an already-granted table. A table revoked
* at creation but missing here is therefore permanently stranded on any
* database that predates its revoke.
*
* These names are unqualified, and the boot-time sweep applies them to every
* schema a project uses — so an entry here is a claim on that name in `public`
* as much as in `rebase`. `jobs` is Rebase's queue at `rebase.jobs` AND a
* perfectly ordinary collection name, and revoking `public.jobs` from a project
* that owns it leaves every read failing 42501 with correct policies applied
* and nothing in the RLS logs to explain it. The `relrowsecurity` guard in
* `revokeInternalTableSql` is what makes a common noun safe here — it is not a
* licence to claim more of them.
*/
var REBASE_INTERNAL_TABLES = [
	"user_identities",
	"refresh_tokens",
	"password_reset_tokens",
	"magic_link_tokens",
	"mfa_factors",
	"mfa_challenges",
	"recovery_codes",
	"app_config",
	"schema_meta",
	"api_keys",
	"cron_logs",
	"cron_claims",
	"jobs",
	"rate_limit_hits",
	"idempotency_keys",
	"entity_history",
	"branches",
	"metric_samples",
	"channel_messages",
	"channel_cursors",
	"channel_presence",
	"atlas_schema_revisions"
];
/** Postgres identifiers this module is willing to interpolate. */
var SAFE_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_$]*$/;
/**
* A single statement that takes every privilege on `schema.table` away from the
* end-user role.
*
* Wrapped in a `DO` block guarded on `pg_roles` for two reasons, both of which
* happen in practice:
*
*  - the role does not exist when the connection is unprivileged (Rebase then
*    relies on native RLS rather than a role switch), and a bare `REVOKE` on a
*    missing role is an error, not a no-op;
*  - the table may not exist yet — `cron_logs` never appears in a project with
*    no cron jobs — and `to_regclass` returning NULL has to be tolerated too.
*
* The third guard is the one that decides whether the *right* table is being
* revoked. The names in {@link REBASE_INTERNAL_TABLES} are unqualified, and the
* boot-time sweep in `ensureAppRole` applies all of them to every schema a
* project uses — including the schema its own collections live in. A project is
* free to call a collection `jobs`, `branches` or `api_keys`, and when it does,
* the sweep was revoking `rebase_user`'s DML on the project's table on every
* single boot. That is not a subtle degradation: the collection's whole API
* answers 500 `permission denied for table …` from then on, which is what
* happened to a public job board whose vacancies live in `public.jobs`.
*
* `relrowsecurity` separates the two cleanly, and it is the same fact this
* module already relies on. Framework-internal tables carry no RLS — that is the
* premise stated at the top of this file, and the reason a revoke is needed at
* all. Every collection table has it enabled, because that is how Rebase
* enforces `securityRules`. So "RLS is off" is exactly "this is not somebody's
* collection", and the guard also subsumes the hand-carved `users` exception:
* the auth user table is a collection, has RLS, and would now be skipped on its
* own merits rather than by being kept off a list.
*
* One command, so it is safe on handles that speak the extended query protocol
* and reject multi-statement strings.
*/
function revokeInternalTableSql(schema, table) {
	if (!SAFE_IDENTIFIER.test(schema)) throw new Error(`Refusing to build SQL with an unsafe schema name: ${JSON.stringify(schema)}`);
	if (!SAFE_IDENTIFIER.test(table)) throw new Error(`Refusing to build SQL with an unsafe table name: ${JSON.stringify(table)}`);
	const qualified = `"${schema}"."${table}"`;
	return `
        DO $rebase_revoke$
        BEGIN
            IF EXISTS (SELECT 1 FROM pg_roles WHERE rolname = '${REBASE_USER_ROLE}')
               AND to_regclass('${qualified}') IS NOT NULL
               AND NOT (SELECT relrowsecurity FROM pg_class WHERE oid = to_regclass('${qualified}')) THEN
                EXECUTE 'REVOKE ALL ON ${qualified} FROM ${REBASE_USER_ROLE}';
            END IF;
        END
        $rebase_revoke$;
    `.trim();
}
/**
* Revoke on every internal table in `schema`, one statement at a time.
*
* Best-effort per table: a connection that does not own one of them (a
* pre-provisioned database, a platform-managed ledger) cannot revoke on it, and
* that must not take down a boot. The caller decides how loud to be — `onError`
* exists so the driver can warn without this module importing a logger.
*/
async function revokeInternalTableAccess(execute, schema, options) {
	for (const table of options?.tables ?? REBASE_INTERNAL_TABLES) try {
		await execute(revokeInternalTableSql(schema, table));
	} catch (error) {
		options?.onError?.(table, error);
	}
}
//#endregion
//#region src/util/sql-rows.ts
/**
* Read the rows out of whatever a SQL driver actually returned.
*
* Two shapes reach this repository's stores through the same call, and which
* one arrives is a property of the driver rather than of the query:
* node-postgres hands back a `{ rows }` envelope, and the other paths — a bare
* `executeSql`, Drizzle's `db.execute()` on some builds — hand back an array.
* `@rebasepro/server`'s `SqlExec` type declares the array, so the envelope is
* off-contract every time it turns up, and it still turns up.
*
* Reading one shape only is how a store silently sees nothing: the rate limiter
* that did it counted every caller as being on their first request — a limiter
* that never limits, with no error anywhere to say so.
*
* Written inline it came to `result as unknown as { rows?: T[] } | T[]`, five
* times across three packages. That is the problem restated as an assertion: a
* union the caller has to re-test at runtime anyway, with `unknown` in the
* middle only because neither half overlaps what the signature promised. The
* `Array.isArray` below is that same test done once, and it *narrows* — so
* these branches are checked rather than claimed.
*
* The element type is the one claim that stays a claim: these are rows from
* hand-written SQL, and nothing at this layer can check a column list.
*/
function sqlRows(result) {
	if (Array.isArray(result)) return result;
	if (result !== null && typeof result === "object") {
		const rows = result.rows;
		if (Array.isArray(rows)) return rows;
	}
	return [];
}
/** The first row of {@link sqlRows}, or `undefined` when there were none. */
function firstSqlRow(result) {
	return sqlRows(result)[0];
}
//#endregion
//#region src/collections/default-collections.ts
/**
* Default users collection.
*
* Prepended to the developer's collections array by the admin and server.
* Slug-based dedup (Map keyed by slug, last-write-wins) lets developers
* override by defining their own collection with `slug: "users"`.
*
* Schema only — no `admin` block. This package is on the backend's dependency path,
* where that field does not exist: `@rebasepro/cms-types` adds it by declaration
* merging, and a BaaS install never installs that. The scaffolded
* `config/collections/users.ts` carries the presentation for projects that want this
* collection in their panel, which is also where it is editable.
*/
var defaultUsersCollection = defineCollection({
	name: "Users",
	singularName: "User",
	slug: "users",
	auth: true,
	table: "users",
	schema: "rebase",
	securityRules: [{
		operation: "select",
		roles: ["admin"]
	}, {
		operations: [
			"insert",
			"update",
			"delete"
		],
		roles: ["admin"]
	}],
	properties: {
		id: {
			name: "ID",
			type: "string",
			isId: "uuid"
		},
		email: {
			name: "Email",
			type: "string",
			validation: {
				required: true,
				unique: true
			}
		},
		displayName: {
			name: "Name",
			type: "string",
			columnName: "display_name",
			validation: { required: true }
		},
		photoURL: {
			name: "Photo URL",
			type: "string",
			columnName: "photo_url"
		},
		roles: {
			name: "Roles",
			type: "array",
			columnType: "text[]",
			of: {
				name: "Role",
				type: "string",
				enum: {
					admin: "Admin",
					editor: "Editor",
					viewer: "Viewer"
				}
			}
		},
		passwordHash: {
			name: "Password Hash",
			type: "string",
			columnName: "password_hash",
			excludeFromApi: true
		},
		emailVerified: {
			name: "Email Verified",
			type: "boolean",
			columnName: "email_verified",
			defaultValue: false
		},
		emailVerificationToken: {
			name: "Email Verification Token",
			type: "string",
			columnName: "email_verification_token",
			excludeFromApi: true
		},
		emailVerificationSentAt: {
			name: "Email Verification Sent At",
			type: "date",
			columnName: "email_verification_sent_at"
		},
		metadata: {
			name: "Metadata",
			type: "map",
			keyValue: true,
			properties: {},
			defaultValue: {}
		},
		createdAt: {
			name: "Created At",
			type: "date",
			columnName: "created_at",
			autoValue: "on_create"
		},
		updatedAt: {
			name: "Updated At",
			type: "date",
			columnName: "updated_at",
			autoValue: "on_update"
		}
	}
});
//#endregion
//#region src/collections/auth-secrets.ts
/** Is this collection the one the auth subsystem stores users in? */
function isAuthCollection(collection) {
	const auth = collection.auth;
	if (auth === true) return true;
	return typeof auth === "object" && auth !== null && "enabled" in auth && auth.enabled === true;
}
/**
* The column names, and property keys, the default users collection marks
* `excludeFromApi`.
*
* Both spellings, because a redeclaration is free to rename the property: one
* project's copy says `password_hash` where the default says `passwordHash`, and
* the column is the same either way. Read off {@link defaultUsersCollection}, so
* a secret added there is covered on the same commit.
*/
function defaultSecretNames() {
	const properties = defaultUsersCollection.properties;
	const names = /* @__PURE__ */ new Set();
	for (const [key, property] of Object.entries(properties)) {
		if (!property?.excludeFromApi) continue;
		names.add(key);
		if (property.columnName) names.add(property.columnName);
	}
	return names;
}
/**
* The keys of the properties on `collection` that hold an auth secret and do not
* carry `excludeFromApi`. Empty for any collection that is not the user store: a
* `password_hash` column elsewhere — a CRM importing hashes, say — is that
* project's data, not this rule's business.
*/
function authSecretsMissingExclusion(collection) {
	if (!collection.properties || !isAuthCollection(collection)) return [];
	const secrets = defaultSecretNames();
	const missing = [];
	for (const [key, property] of Object.entries(collection.properties)) {
		if (!property || property.excludeFromApi) continue;
		if (secrets.has(key) || secrets.has(property.columnName ?? key)) missing.push(key);
	}
	return missing;
}
//#endregion
//#region src/data/resolveDataSource.ts
/**
* Build a keyed registry from a list of {@link DataSourceDefinition}s.
* Later entries win on key collision.
*/
function createDataSourceRegistry(definitions) {
	const registry = {};
	for (const def of definitions ?? []) registry[def.key] = def;
	return registry;
}
/**
* Resolve the effective data source for a collection — the single source of
* truth shared by the frontend router, the backend driver registry, and the
* editor's capability lookups.
*
* Resolution order:
* 1. The routing **key** is `collection.dataSource`, else
*    {@link DEFAULT_DATA_SOURCE_KEY}.
* 2. If a definition is registered for that key, it provides `engine`,
*    `transport`, and `databaseId`.
* 3. Otherwise values are synthesized: `engine` from `collection.engine`
*    (or the key, or `"postgres"`), `transport` defaults to `"server"`,
*    and `databaseId` from the collection.
*
* `capabilities` are always derived from the resolved `engine`, so two
* data sources sharing an engine share capabilities.
*
* @param collection the collection (or any object carrying the routing fields)
* @param registry   optional registry of declared data sources
*/
function resolveDataSource(collection, registry) {
	const key = collection?.dataSource ?? DEFAULT_DATA_SOURCE_KEY;
	const def = registry?.[key];
	const engine = def?.engine ?? collection?.engine ?? (key !== DEFAULT_DATA_SOURCE_KEY ? key : "postgres");
	return {
		key,
		engine,
		transport: def?.transport ?? "server",
		databaseId: collection?.databaseId ?? def?.databaseId,
		capabilities: getDataSourceCapabilities(engine)
	};
}
/**
* Does a SQL toolchain own this collection's storage?
*
* "Owns the storage" means: something generates a table for it, pushes that
* table to a database, plans its RLS policies, and reports it as drifted when
* the two disagree. That is true of a Postgres collection and false of a
* Firestore or MongoDB one, whose documents live in a store Rebase never
* migrates — and the two were never told apart. Every stage of the SQL
* toolchain took "the collections" to mean *all* of them, so a Firestore
* collection declared next to the Postgres ones got a `pgTable` in the
* generated schema, a `CREATE TABLE` at boot, RLS policies, and a place in the
* `db push` include list — where its name shielding a same-named real table
* from Atlas's exclude list is the one that can lose data.
*
* The answer is the resolved engine's {@link DataSourceCapabilities}, not a
* name check: an engine registered through `registerDataSourceCapabilities`
* gets the same treatment as the built-in ones.
*
* Deliberately answers **true** for an engine nobody has heard of. Build-time
* tooling (the CLI, the schema generator) has no data-source registry to
* resolve a `dataSource` key against, so an unknown key resolves to an unknown
* engine — and the cost of the two mistakes is not symmetric. Wrongly
* including a collection generates a table nothing writes to; wrongly excluding
* one silently stops generating a table the app is serving from. Declare
* `engine` on a collection that is not SQL-backed and this is exact.
*/
function isRelationalCollection(collection, registry) {
	return getDataSourceCapabilities(collection?.engine ?? (collection?.dataSource ? resolveDataSource(collection, registry).engine : void 0)).supportsRelations;
}
/**
* The subset of `collections` a SQL toolchain owns — see
* {@link isRelationalCollection}.
*
* Every stage that generates SQL from collections starts by calling this, so
* the rule lives in one place rather than being re-decided per generator. It
* keeps the input order.
*/
function relationalCollections(collections, registry) {
	return collections.filter((collection) => isRelationalCollection(collection, registry));
}
//#endregion
//#region src/collections/CollectionRegistry.ts
var CollectionRegistry = class {
	/**
	* Declared data sources, used during normalization to resolve each
	* collection's engine (so `dataSource`-only collections get the right
	* capabilities). Empty by default.
	*/
	dataSources = {};
	/**
	* Global lifecycle callbacks applied to every collection.
	* Runs on all data paths (REST, WebSocket, `rebase.dataAsAdmin`).
	* Execution order: global → collection → property callbacks.
	*/
	_globalCallbacks;
	/**
	* Set global lifecycle callbacks that apply to every collection.
	* Typically called once during backend initialization.
	*/
	setGlobalCallbacks(callbacks) {
		this._globalCallbacks = callbacks;
	}
	/**
	* Get the currently registered global callbacks, if any.
	*/
	getGlobalCallbacks() {
		return this._globalCallbacks;
	}
	collectionsByTableName = /* @__PURE__ */ new Map();
	collectionsBySlug = /* @__PURE__ */ new Map();
	rootCollections = [];
	cachedCollectionsList = null;
	rawCollectionsByTableName = /* @__PURE__ */ new Map();
	rawCollectionsBySlug = /* @__PURE__ */ new Map();
	rawRootCollections = [];
	cachedRawCollectionsList = null;
	lastRawInputEntity = null;
	constructor(collections, dataSources) {
		if (dataSources) this.dataSources = dataSources;
		if (collections) this.registerMultiple(collections);
	}
	/**
	* Provide the declared data sources used to resolve each collection's
	* engine during normalization. Set this before registering collections.
	* Returns true if the registry changed (callers may re-register).
	*/
	setDataSources(dataSources) {
		if (deepEqual(this.dataSources, dataSources)) return false;
		this.dataSources = dataSources ?? {};
		return true;
	}
	reset() {
		this.collectionsByTableName.clear();
		this.collectionsBySlug.clear();
		this.rootCollections = [];
		this.cachedCollectionsList = null;
		this.rawCollectionsByTableName.clear();
		this.rawCollectionsBySlug.clear();
		this.rawRootCollections = [];
		this.cachedRawCollectionsList = null;
	}
	/**
	* Registers a collection and its subcollections recursively.
	* Returns true if the collections have changed, false otherwise.
	*
	* Idempotent: compares the raw input (before normalization) against a stored
	* entity. Only re-normalizes and re-registers when the raw input actually changed.
	* @param collections
	*/
	registerMultiple(collections) {
		const rawEntity = collections.map((c) => removeFunctions(c));
		if (this.lastRawInputEntity && deepEqual(this.lastRawInputEntity, rawEntity)) return false;
		this.reset();
		collections.forEach((c) => {
			if (c.slug) this.collectionsBySlug.set(c.slug, c);
			this.collectionsByTableName.set(getTableName(c), c);
		});
		const normalizedCollections = collections.map((c) => this.normalizeCollection({ ...c }));
		normalizedCollections.forEach((c, index) => {
			const raw = deepClone(collections[index]);
			this.rootCollections.push(c);
			this.rawRootCollections.push(raw);
			const normalized = this.normalizeCollection(c);
			this.collectionsByTableName.set(getTableName(normalized), normalized);
			this.rawCollectionsByTableName.set(getTableName(raw), raw);
			if (normalized.slug) this.collectionsBySlug.set(normalized.slug, normalized);
			if (raw.slug) this.rawCollectionsBySlug.set(raw.slug, raw);
		});
		normalizedCollections.forEach((c) => {
			const subcollections = getSubcollections(c);
			if (subcollections && subcollections.length > 0) subcollections.forEach((subCollection) => {
				if (!subCollection) return;
				this._registerRecursively(this.normalizeCollection({ ...subCollection }), deepClone(subCollection));
			});
		});
		this.lastRawInputEntity = rawEntity;
		return true;
	}
	register(collection, rawCollection) {
		const raw = rawCollection ? deepClone(rawCollection) : deepClone(collection);
		this.rootCollections.push(collection);
		this.rawRootCollections.push(raw);
		this._registerRecursively(collection, raw);
	}
	_registerRecursively(collection, rawCollection) {
		if (this.collectionsByTableName.has(getTableName(collection))) return;
		const normalizedCollection = this.normalizeCollection(collection);
		this.collectionsByTableName.set(getTableName(normalizedCollection), normalizedCollection);
		this.rawCollectionsByTableName.set(getTableName(rawCollection), rawCollection);
		if (normalizedCollection.slug) this.collectionsBySlug.set(normalizedCollection.slug, normalizedCollection);
		if (rawCollection.slug) this.rawCollectionsBySlug.set(rawCollection.slug, rawCollection);
		const subcollections = getSubcollections(normalizedCollection);
		if (subcollections && subcollections.length > 0) subcollections.forEach((subCollection) => {
			if (!subCollection) return;
			this._registerRecursively(this.normalizeCollection({ ...subCollection }), deepClone(subCollection));
		});
	}
	normalizeCollection(collection) {
		const result = { ...collection };
		{
			const resolved = resolveDataSource(result, this.dataSources);
			if (!result.dataSource) result.dataSource = resolved.key;
			if (!result.engine) result.engine = resolved.engine;
		}
		const properties = this.normalizeProperties(result.properties, result);
		result.properties = properties;
		for (const key of authSecretsMissingExclusion(result)) properties[key].excludeFromApi = true;
		return result;
	}
	normalizeProperties(properties, collection) {
		const newProperties = {};
		for (const key in properties) newProperties[key] = this.normalizeProperty(key, properties[key], collection);
		return newProperties;
	}
	normalizeProperty(key, property, collection) {
		const newProperty = { ...property };
		if (newProperty.type === "map" && newProperty.properties) newProperty.properties = this.normalizeProperties(newProperty.properties, collection);
		else if (newProperty.type === "array") {
			const arrayProp = newProperty;
			if (arrayProp.of) if (Array.isArray(arrayProp.of)) arrayProp.of = arrayProp.of.map((p, i) => this.normalizeProperty(`${key}[${i}]`, p, collection));
			else arrayProp.of = this.normalizeProperty(`${key}.of`, arrayProp.of, collection);
			else if (arrayProp.oneOf && arrayProp.oneOf.properties) arrayProp.oneOf.properties = this.normalizeProperties(arrayProp.oneOf.properties, collection);
		} else if ((newProperty.type === "string" || newProperty.type === "number") && newProperty.enum) {
			const stringOrNumberProperty = newProperty;
			if (typeof stringOrNumberProperty.enum === "object" && !Array.isArray(stringOrNumberProperty.enum)) stringOrNumberProperty.enum = enumToObjectEntries(stringOrNumberProperty.enum)?.filter((value) => value && (value.id || value.id === 0) && value.label) ?? [];
		} else if (newProperty.type === "relation") {
			const relationProperty = newProperty;
			if (relationProperty.relation) relationProperty.resolvedRelation = resolveRelation(relationProperty.relation, collection, key);
			else {
				const declared = resolveCollectionRelations(collection)[key];
				if (declared) relationProperty.resolvedRelation = declared;
				else console.warn(`Relation property '${key}' on '${collection.slug}' names no relation: it has no \`relation\` block, and the collection's \`relations\` array has no entry called '${key}'. The field will render no picker, generate no foreign key, and return nothing from \`include()\`.`);
			}
		}
		return newProperty;
	}
	get(path) {
		const bySlug = this.collectionsBySlug.get(path);
		if (bySlug) return bySlug;
		if (path.includes("-")) {
			const normalized = path.replace(/-/g, "_");
			const byNormalized = this.collectionsBySlug.get(normalized);
			if (byNormalized) return byNormalized;
		}
		return this.collectionsByTableName.get(path);
	}
	/**
	* Gets the pristine, un-normalized collection exactly as it was provided.
	* Useful for the AST editor so it doesn't accidentally serialize injected metadata back to disk.
	*/
	getRaw(path) {
		const bySlug = this.rawCollectionsBySlug.get(path);
		if (bySlug) return bySlug;
		if (path.includes("-")) {
			const normalized = path.replace(/-/g, "_");
			const byNormalized = this.rawCollectionsBySlug.get(normalized);
			if (byNormalized) return byNormalized;
		}
		return this.rawCollectionsByTableName.get(path);
	}
	/**
	* Get collection by resolving multi-segment paths through relations
	* e.g., "authors/70/posts" resolves to the posts collection
	*/
	getCollectionByPath(collectionPath) {
		if (!collectionPath.includes("/")) return this.get(collectionPath);
		const pathSegments = collectionPath.split("/").filter((p) => p);
		if (pathSegments.length < 3 || pathSegments.length % 2 === 0) throw new Error(`Invalid relation path: ${collectionPath}. Expected format: collection/id/relation or collection/id/relation/id/relation`);
		const rootCollectionPath = pathSegments[0];
		let currentCollection = this.get(rootCollectionPath);
		if (!currentCollection) throw new Error(`Root collection not found: ${rootCollectionPath}`);
		for (let i = 2; i < pathSegments.length; i += 2) {
			const relationKey = pathSegments[i];
			if (!getDataSourceCapabilities(currentCollection.engine).supportsRelations) throw new Error(`Relation path navigation requires a collection that supports relations, but '${currentCollection.slug}' uses engine '${currentCollection.engine}'`);
			const relation = findRelation(resolveCollectionRelations(currentCollection), relationKey);
			if (!relation) throw new Error(`Relation '${relationKey}' not found in collection '${currentCollection.slug}'`);
			const target = relation.target();
			currentCollection = this.collectionsByTableName.get(getTableName(target)) ?? this.normalizeCollection(target);
			if (i + 1 < pathSegments.length) {}
		}
		return currentCollection;
	}
	getCollections() {
		if (!this.cachedCollectionsList) this.cachedCollectionsList = Array.from(this.collectionsByTableName.values());
		return this.cachedCollectionsList;
	}
	getRawCollections() {
		if (!this.cachedRawCollectionsList) this.cachedRawCollectionsList = Array.from(this.rawCollectionsByTableName.values());
		return this.cachedRawCollectionsList;
	}
	/**
	* Resolves a multi-segment path like "products/123/locales" and returns
	* information about the collections and entity IDs along the path
	*/
	resolvePathToCollections(path) {
		const pathSegments = path.split("/").filter((p) => p);
		if (pathSegments.length === 0) throw new Error(`Invalid path: ${path}`);
		if (pathSegments.length % 2 !== 1) throw new Error(`Invalid collection path: ${path}. It must have an odd number of segments.`);
		const collections = [];
		const entityIds = [];
		let currentCollection = this.get(pathSegments[0]);
		if (!currentCollection) throw new Error(`Unknown collection path or slug: ${pathSegments[0]}`);
		collections.push(currentCollection);
		for (let i = 1; i < pathSegments.length; i += 2) {
			const entityId = pathSegments[i];
			entityIds.push(entityId);
			if (i + 1 < pathSegments.length) {
				const subcollectionSlug = pathSegments[i + 1];
				const subcollections = getSubcollections(currentCollection);
				if (!subcollections || subcollections.length === 0) throw new Error(`No subcollections found for ${currentCollection.slug} in path: ${path}`);
				const subcollection = subcollections.find((c) => c.slug === subcollectionSlug);
				if (!subcollection) throw new Error(`Subcollection '${subcollectionSlug}' not found in ${currentCollection.slug}`);
				currentCollection = this.normalizeCollection(subcollection);
				collections.push(currentCollection);
			}
		}
		return {
			collections,
			entityIds,
			finalCollection: currentCollection
		};
	}
};
//#endregion
//#region src/collections/engine-support.ts
/**
* Refuse a `beforeQuery` hook on a collection Postgres does not serve.
*
* `beforeQuery` narrows a read *before it is compiled*, which means compiling
* a declarative filter into the query the engine is about to run. Only
* `@rebasepro/server-postgres` does that today. On any other engine the hook
* would look configured and do nothing — and for a hook whose whole purpose is
* to restrict which rows come back, "nothing" means every row served to
* everybody.
*
* So it is a boot failure, named. The alternative — declaring the hook only on
* `PostgresCollectionConfig`, the way `search` is — is not available:
* `callbacks` is one engine-agnostic type shared by every collection, and
* splitting it would fork `afterRead` and the five write hooks along with it.
*
* Lives here rather than in either driver because both have to run it: the
* Postgres schema planner sees every collection in a mixed app, and a
* Mongo-only app never boots a Postgres planner at all.
*
* Called with *every* collection, before an engine filters any out.
*/
var assertBeforeQueryIsPostgresOnly = (collections) => {
	for (const collection of collections) {
		if (isPostgresCollectionConfig(collection)) continue;
		if (!collection.callbacks?.beforeQuery) continue;
		const engine = collection.engine ?? "non-postgres";
		throw new Error(`${collection.slug}.callbacks.beforeQuery: narrowing a read before it is compiled is a Postgres feature, and this collection is served by \`${engine}\`. Remove the hook — it would otherwise look configured while every read kept returning every row the engine allows. Redaction that works on every engine is \`afterRead\`.`);
	}
};
//#endregion
//#region src/collections/field-access.ts
/**
* The role that satisfies any non-empty list.
*
* The same arm every baseline policy carries: `security_rules` injects
* `rolesOverlap(['admin'])` into the default read and write policies, and
* `rebase.dataAsAdmin` is scoped with `{ uid: "service", roles: ["admin"] }`.
* Without this an author could declare `access: { read: ["hr"] }` and lock the
* administrator out of a column of their own database — and lock the Studio out
* of rendering it.
*/
var ADMIN_ROLE = "admin";
/**
* What a property's access rules actually are, with `excludeFromApi` expanded.
*
* Returns `undefined` when the property constrains nothing, so callers can skip
* the whole check for the overwhelmingly common case.
*/
function effectiveAccess(property) {
	if (!property) return void 0;
	if (property.excludeFromApi) return EXCLUDED_ACCESS;
	const access = property.access;
	if (!access) return void 0;
	if (access.read === void 0 && access.write === void 0) return void 0;
	return access;
}
/** The rule `excludeFromApi: true` expands to. Frozen: it is shared by every caller. */
var EXCLUDED_ACCESS = Object.freeze({
	read: Object.freeze([]),
	write: Object.freeze([])
});
/**
* Does a caller holding `roles` satisfy `allowed`?
*
* Three cases, and the middle one is the one worth stating out loud:
*
* - `allowed` omitted — the field carries no rule of its own, so the row's
*   policies have already answered. True.
* - `allowed` empty — nobody, at any privilege, through any API. Not the admin,
*   not the service key, not the trusted plane reading on a caller's behalf.
*   This is what `excludeFromApi` has always meant on the read side, and
*   collapsing the two spellings means the empty list has to keep meaning it.
* - `allowed` non-empty — one of the named roles, or `admin`, or no viewer at
*   all (the trusted server plane, which is not an API caller).
*/
function satisfies(allowed, viewer) {
	if (allowed === void 0) return true;
	if (allowed.length === 0) return false;
	if (!viewer) return true;
	const roles = viewer.roles;
	if (!roles || roles.length === 0) return false;
	return roles.includes("admin") || allowed.some((role) => roles.includes(role));
}
/** May this caller receive this field's value? */
function canReadField(property, viewer) {
	const access = effectiveAccess(property);
	return access ? satisfies(access.read, viewer) : true;
}
/** May this caller set this field's value? */
function canWriteField(property, viewer) {
	const access = effectiveAccess(property);
	return access ? satisfies(access.write, viewer) : true;
}
/**
* The names on this collection a caller may not touch, in the two spellings a
* caller can write them in.
*
* `declared` is the property keys, which is what has to leave a *known-fields*
* set. `refused` is those plus the physical column names behind them: a caller
* who knows the table can send `password_hash` as readily as `passwordHash`, and
* a rule that only knew the wire name would be one rename away from useless.
*
* `kind` picks which half of the rule is read; nothing else differs.
*/
function restrictedFieldNames(collection, viewer, kind) {
	const declared = [];
	const refused = /* @__PURE__ */ new Set();
	const allowed = kind === "read" ? canReadField : canWriteField;
	for (const [name, property] of Object.entries(collection.properties ?? {})) {
		if (allowed(property, viewer)) continue;
		declared.push(name);
		refused.add(name);
		const columnName = property.columnName;
		if (columnName) refused.add(columnName);
	}
	return {
		declared,
		refused
	};
}
/**
* True when nothing on this collection restricts a field, for either direction.
*
* Every read of every row runs through the strip, so the collection that has no
* rules — which is almost all of them — has to cost one property walk and no
* allocation.
*/
function hasFieldAccessRules(collection) {
	for (const property of Object.values(collection.properties ?? {})) if (effectiveAccess(property)) return true;
	return false;
}
//#endregion
//#region src/data/cursor.ts
/** A cursor that cannot be read at all — truncated, re-encoded, or invented. */
var CursorError = class CursorError extends Error {
	code = "INVALID_CURSOR";
	constructor(detail) {
		super(`Invalid \`after\` cursor: ${detail}. Pass back the \`meta.nextCursor\` from the previous page unchanged — it is opaque and must not be built by hand.`);
		this.name = "CursorError";
		Object.setPrototypeOf(this, CursorError.prototype);
	}
};
/**
* A cursor that reads fine but describes a different query.
*
* Separate from {@link CursorError} because the fix is different: this one is
* not a corrupt string, it is a correct cursor used against a sort it was not
* produced under. Seeking anyway would return rows in an order nobody asked
* for, and — worse — would look like it worked.
*/
var CursorMismatchError = class CursorMismatchError extends Error {
	code = "CURSOR_ORDER_MISMATCH";
	constructor(cursorKeys, queryKeys) {
		super(`The \`after\` cursor was produced by a query ordered by ${cursorKeys.map((k) => `"${k}"`).join(", ") || "(nothing)"}, but this query orders by ${queryKeys.map((k) => `"${k}"`).join(", ") || "(nothing)"}. A cursor only continues the listing it came from — keep \`orderBy\` identical across pages, or drop \`after\` to start over.`);
		this.name = "CursorMismatchError";
		Object.setPrototypeOf(this, CursorMismatchError.prototype);
	}
};
/**
* Tag for a value whose JSON round-trip would otherwise lose its type.
*
* A `timestamp` column comes back from the driver as a `Date`; JSON turns it
* into a string, and the string would then be compared against the column by
* whatever cast Postgres chose. Round-tripping it as a `Date` keeps the
* comparison the one the ORDER BY made.
*/
var DATE_TAG = "$date";
function encodeValue(value) {
	if (value instanceof Date) return { [DATE_TAG]: value.toISOString() };
	return value;
}
function decodeValue(value) {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		const tagged = value[DATE_TAG];
		if (typeof tagged === "string") {
			const date = new Date(tagged);
			return Number.isNaN(date.getTime()) ? tagged : date;
		}
	}
	return value;
}
/** base64url, without depending on Node's Buffer (this package runs in browsers). */
function toBase64Url(text) {
	const bytes = new TextEncoder().encode(text);
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromBase64Url(encoded) {
	const padded = encoded.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - encoded.length % 4) % 4);
	const binary = atob(padded);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return new TextDecoder().decode(bytes);
}
/**
* Encode "everything strictly after this row, in this order".
*
* @param orderBy the sort keys the listing ran under, in order of significance
* @param row     the last row served, which the next page picks up after
* @param id      that row's id — the tiebreaker every keyset comparison ends on
* @returns the opaque cursor, or `undefined` when no cursor can describe the
*   page. That is not a failure: a listing sorted by relevance has no stored
*   value to compare a later page against (scores are computed per query and
*   are not on the same scale between two of them), so it pages by offset and
*   `meta.nextCursor` is simply absent.
*/
function encodeCursor(orderBy, row, id) {
	if (id === void 0 || id === null) return void 0;
	const keys = orderBy ?? [];
	const values = {};
	for (const [field] of keys) {
		if (!(field in row)) return void 0;
		values[field] = encodeValue(row[field]);
	}
	return toBase64Url(JSON.stringify({
		k: keys,
		v: values,
		i: encodeValue(id)
	}));
}
/**
* Read a cursor produced by {@link encodeCursor}.
*
* @throws {CursorError} when the string is not a cursor this codec wrote.
*/
function decodeCursor(raw) {
	let parsed;
	try {
		parsed = JSON.parse(fromBase64Url(raw.trim()));
	} catch {
		throw new CursorError("it is not a cursor this API issued");
	}
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new CursorError("it does not decode to a cursor");
	const body = parsed;
	if (!Array.isArray(body.k)) throw new CursorError("it carries no sort keys");
	if (body.i === void 0) throw new CursorError("it carries no row id");
	const orderBy = [];
	for (const entry of body.k) {
		if (!Array.isArray(entry) || typeof entry[0] !== "string") throw new CursorError("one of its sort keys is malformed");
		const direction = entry[1] === "desc" ? "desc" : "asc";
		orderBy.push(entry[2] === "first" || entry[2] === "last" ? [
			entry[0],
			direction,
			entry[2]
		] : [entry[0], direction]);
	}
	const rawValues = body.v && typeof body.v === "object" && !Array.isArray(body.v) ? body.v : {};
	const values = {};
	for (const [field, value] of Object.entries(rawValues)) values[field] = decodeValue(value);
	return {
		orderBy,
		values,
		id: decodeValue(body.i)
	};
}
/**
* The `orderBy` a request should run under, given a cursor and whatever sort
* the request itself named.
*
* A request that names no sort **adopts the cursor's** — that is what makes
* `find({ after })` work without restating the `orderBy` from the previous
* call, and it cannot be wrong, since the cursor is the only sort in play.
* A request that names one must name the *same* one, key for key, direction for
* direction, nulls for nulls; anything else is {@link CursorMismatchError}.
*
* @throws {CursorMismatchError}
*/
function reconcileCursorOrder(cursor, requested) {
	if (!requested || requested.length === 0) return cursor.orderBy;
	const spell = (keys) => keys.map(([field, direction, nulls]) => `${field}:${direction}${nulls ? `:${nulls}` : ""}`);
	const cursorKeys = spell(cursor.orderBy);
	const queryKeys = spell(requested);
	if (cursorKeys.length !== queryKeys.length || cursorKeys.some((key, i) => key !== queryKeys[i])) throw new CursorMismatchError(cursorKeys, queryKeys);
	return requested;
}
/**
* The `startAfter` shape the driver contract takes, built from a cursor.
*
* The driver has always accepted `{ id, values }`; this is the one place that
* shape is produced, so the REST route and the WebSocket ingress cannot drift
* into two spellings of the same seek.
*/
function cursorToStartAfter(cursor) {
	return {
		id: cursor.id,
		values: cursor.values
	};
}
//#endregion
//#region src/data/sort-dialect.ts
/**
* Sort-order wire codec.
*
* This is the ONLY module that knows about the colon-delimited wire format
* (`"field:direction"`) used in HTTP query parameters, and about the JSON-array
* form that carries a multi-column sort over the same parameter.
* Everything else speaks {@link OrderByTuple} exclusively.
*
* Mirrors the filter architecture in `filter-dialect.ts`.
*
* @module
*/
/**
* Collapse the one-key and many-key spellings of a sort into the list form.
*
* `["a", "desc"]` and `[["a", "desc"]]` mean the same thing and normalize to
* the same value; the two are told apart by whether the first element is
* itself an array, which no field name ever is.
*
* This is also where a {@link RelationAggregateSort} object stops being an
* object. Above this function a sort key may be either spelling; below it,
* every key is a string — which is what `OrderByTuple`, the REST parameter, the
* driver contract and the cursor all already were. Doing it here means the one
* place that already collapses the two *shapes* of a sort also collapses the
* two *spellings* of a key, rather than every consumer learning about both.
*
* @returns The keys in order of significance, or `undefined` for no sort. An
*   empty list also returns `undefined` — "sort by nothing" is no sort, and
*   letting `[]` through would have every layer below re-deciding what it meant.
*/
function normalizeOrderBy(orderBy) {
	if (!orderBy || orderBy.length === 0) return void 0;
	const list = Array.isArray(orderBy[0]) ? orderBy : [orderBy];
	if (list.length === 0) return void 0;
	return list.map((entry, index) => toStrictTuple(entry, index));
}
/**
* The most significant sort key, for a caller that can only express one —
* a column header's arrow, a URL parameter, a driver that has not been taught
* the list form.
*/
function primaryOrderBy(orderBy) {
	return normalizeOrderBy(orderBy)?.[0];
}
/**
* Collapse the driver-level `{orderBy, order}` pair into the list form.
*
* The driver contract spells a single-column sort as a field name plus a
* separate direction, and a multi-column one as a list of tuples that leaves
* `order` meaningless. Every driver reads both through here so neither
* spelling has to be handled twice.
*
* An absent direction means ascending — the same thing a bare `?orderBy=name`
* has always meant over HTTP. The Postgres driver used to read the same pair as
* *descending* while Mongo read it as ascending, so one field name and no
* direction described two different queries depending on which database was
* underneath. Neither had a caller: every path in the workspace passes a
* direction, which is why the disagreement went unnoticed rather than being
* load-bearing.
*/
function normalizeDriverOrderBy(orderBy, order) {
	if (!orderBy) return void 0;
	if (typeof orderBy === "string") return [[orderBy, order === "desc" ? "desc" : "asc"]];
	return orderBy.length > 0 ? orderBy : void 0;
}
/** A sort whose *shape* is unusable, as opposed to one naming a field that does not exist. */
var OrderBySpecError = class extends Error {
	code = "INVALID_ORDER_BY";
	constructor(detail) {
		super(`Invalid \`orderBy\`: ${detail}. Expected a field name, or a list of [field, direction] pairs like [["roles","asc"],["created_at","desc"]]`);
		this.name = "OrderBySpecError";
	}
};
/**
* Validate an `orderBy` that arrived from outside this process — a WebSocket
* subscribe frame, a driver call from untyped JavaScript — and return it in the
* list form.
*
* Strict on purpose, in the same way the REST `parseOrderByParam` is: the
* failure mode for a shape nobody checks is not a crash but a *silently
* different query*. A malformed entry read as a field name resolves to no
* column, and under the lenient unknown-field mode the sort is then dropped and
* the rows come back in whatever order the database pleased — sorted, as far as
* the subscriber can tell, by whatever they asked for.
*/
function parseOrderBySpecStrict(raw, order) {
	if (raw === void 0 || raw === null || raw === "") return void 0;
	if (typeof raw === "string") return normalizeDriverOrderBy(raw, order);
	if (!Array.isArray(raw) || raw.length === 0) throw new OrderBySpecError(`${typeof raw} is not a field name or a list of sort keys`);
	if (typeof raw[0] === "string" || isRelationAggregateSort(raw[0])) return [toStrictTuple(raw, 0)];
	return raw.map(toStrictTuple);
}
/** `first`/`last`, or a refusal naming the entry — see {@link NullsPlacement}. */
function toStrictNulls(raw, index) {
	if (raw === void 0 || raw === null) return void 0;
	if (raw !== "first" && raw !== "last") throw new OrderBySpecError(`entry ${index} has nulls '${String(raw)}' — expected "first" or "last"`);
	return raw;
}
function toStrictTuple(raw, index) {
	if (!Array.isArray(raw)) throw new OrderBySpecError(`entry ${index} has no field name`);
	const key = isRelationAggregateSort(raw[0]) ? sortKeyToString(raw[0]) : raw[0];
	if (typeof key !== "string" || key.trim() === "") throw new OrderBySpecError(`entry ${index} has no field name`);
	const direction = raw[1];
	if (direction !== void 0 && direction !== "asc" && direction !== "desc") throw new OrderBySpecError(`entry ${index} has direction '${String(direction)}'`);
	const nulls = toStrictNulls(raw[2], index);
	return nulls ? [
		key,
		direction ?? "asc",
		nulls
	] : [key, direction ?? "asc"];
}
/**
* Serialize a sort to the wire.
*
* A single key keeps the `"field:direction"` shorthand it has always used —
* short, readable in a URL, and what every existing client and test expects.
* Several keys are emitted as the canonical JSON array the server already
* accepts, because the shorthand has no separator to spare: a comma-joined
* `"a:asc,b:desc"` parses as one field named `a` with the direction
* `"asc,b:desc"`, which the server refuses.
*
* **Runtime tolerance:** if the input is already a well-formed wire string
* (from an untyped JS caller), it is returned unchanged.
* This is undocumented tolerance, not public API — don't rely on it.
*
* @param orderBy - A canonical tuple or list of tuples, or at runtime
*   possibly a pre-serialized string (undocumented tolerance).
* @returns The wire-format string, or `undefined` if the input is falsy.
*
* @remarks
* Field names containing `:` are representable in the tuple form but
* **not** in the single-key wire encoding — this is an inherent limitation of
* the colon-delimited shorthand and is not resolved here.
*/
function serializeOrderBy(orderBy) {
	if (!orderBy) return void 0;
	if (typeof orderBy === "string") return orderBy;
	const list = normalizeOrderBy(orderBy);
	if (!list) return void 0;
	if (list.length === 1) {
		const [field, direction, nulls] = list[0];
		return nulls ? `${field}:${direction}:${nulls}` : `${field}:${direction}`;
	}
	return JSON.stringify(list.map(([field, direction, nulls]) => nulls ? {
		field,
		direction,
		nulls
	} : {
		field,
		direction
	}));
}
/**
* Deserialize a wire-format `"field:direction"` string into an {@link OrderByTuple}.
*
* Lenient parsing:
* - Bare field name (no colon): `"name"` → `["name", "asc"]`
* - Unknown direction: `"name:foo"` → `["name", "asc"]`
* - Empty / falsy input, or a blank field name: → `undefined`
*
* The leniency is this end's alone; the *server* refuses the same value. This
* used to say "matches existing server behaviour", and it stopped being true
* when `parseOrderByParam` grew a strict direction check: `?orderBy=name:foo`
* now answers `400 INVALID_ORDER_BY` ("entry 0 has direction 'foo'"). The split
* is deliberate — see {@link parseOrderBySpecStrict} — because a value this
* function is handed was produced by {@link serializeOrderBy} a moment earlier,
* and one that reaches the server came from a stranger.
*
* A blank field is `undefined` rather than `[" ", "asc"]`: whitespace is not a
* field name, and the tuple it used to produce could not be re-encoded — the
* only value in this codec that survived a decode and failed the next encode.
*
* Reads the single-key shorthand only. For a value that may carry several keys,
* use {@link deserializeOrderByList} — handed a JSON array this returns the
* whole array as one nonsensical field name.
*
* @param raw - The wire-format string from an HTTP query parameter.
* @returns The canonical tuple, or `undefined` if the input names no field.
*/
function deserializeOrderBy(raw) {
	if (!raw) return void 0;
	const idx = raw.indexOf(":");
	if (idx === -1) return raw.trim() === "" ? void 0 : [raw, "asc"];
	const field = raw.slice(0, idx);
	if (field.trim() === "") return void 0;
	const rest = raw.slice(idx + 1);
	const nullsIdx = rest.indexOf(":");
	const dir = nullsIdx === -1 ? rest : rest.slice(0, nullsIdx);
	const nulls = nullsIdx === -1 ? void 0 : rest.slice(nullsIdx + 1);
	const direction = dir === "desc" ? "desc" : "asc";
	return nulls === "first" || nulls === "last" ? [
		field,
		direction,
		nulls
	] : [field, direction];
}
/**
* Deserialize either wire spelling — the single-key shorthand or the JSON
* array — into the list form.
*
* Lenient in the same way {@link deserializeOrderBy} is: this is the client end
* of the codec, where the value was produced by {@link serializeOrderBy} a
* moment earlier. The *server* end parses the same shapes strictly, in
* `parseOrderByParam`, because there the value came from a stranger and a
* direction it cannot read has to be refused rather than quietly turned into
* `"asc"`.
*/
function deserializeOrderByList(raw) {
	if (!raw) return void 0;
	const trimmed = raw.trim();
	if (trimmed.startsWith("[")) try {
		const parsed = JSON.parse(trimmed);
		if (Array.isArray(parsed)) {
			const list = parsed.map((entry) => {
				if (typeof entry === "string") return deserializeOrderBy(entry);
				if (entry && typeof entry === "object" && typeof entry.field === "string") {
					const direction = entry.direction === "desc" ? "desc" : "asc";
					return entry.nulls === "first" || entry.nulls === "last" ? [
						entry.field,
						direction,
						entry.nulls
					] : [entry.field, direction];
				}
			}).filter((entry) => entry !== void 0);
			return list.length > 0 ? list : void 0;
		}
	} catch {}
	const single = deserializeOrderBy(trimmed);
	return single ? [single] : void 0;
}
//#endregion
//#region src/data/include-spec.ts
/** An `include` that cannot be read, as opposed to one naming a relation that does not exist. */
var IncludeSpecError = class IncludeSpecError extends Error {
	code;
	constructor(detail, code = "INVALID_INCLUDE") {
		super(`Invalid \`include\`: ${detail}`);
		this.name = "IncludeSpecError";
		this.code = code;
		Object.setPrototypeOf(this, IncludeSpecError.prototype);
	}
};
var emptyNode = () => ({ children: {} });
function ensureNode(tree, key) {
	return tree[key] ??= emptyNode();
}
/**
* Merge one dotted path (`"comments.author"`) into a tree.
*
* Merging rather than assigning is what makes `include=comments,comments.author`
* mean the same thing as `include=comments.author`: the second path deepens the
* node the first created instead of replacing it and losing its options.
*/
function addPath(tree, path) {
	const segments = path.split(".").map((s) => s.trim()).filter(Boolean);
	if (segments.length === 0) return;
	if (segments.length > MAX_INCLUDE_DEPTH) throw new IncludeSpecError(`"${path}" nests ${segments.length} relations deep; the limit is ${MAX_INCLUDE_DEPTH}. Each hop is another query, and an unbounded one walks a self-referencing relation forever.`, "INCLUDE_TOO_DEEP");
	let level = tree;
	for (const segment of segments) level = ensureNode(level, segment).children;
}
function normalizeOptions(key, options, depth) {
	if (depth > MAX_INCLUDE_DEPTH) throw new IncludeSpecError(`"${key}" nests more than ${MAX_INCLUDE_DEPTH} relations deep.`, "INCLUDE_TOO_DEEP");
	if (options.limit !== void 0 && (!Number.isInteger(options.limit) || options.limit < 1)) throw new IncludeSpecError(`"${key}" has limit ${JSON.stringify(options.limit)} — expected a whole number of 1 or more.`);
	const node = { children: {} };
	if (options.limit !== void 0) node.limit = options.limit;
	if (options.where) node.where = options.where;
	if (options.logical) node.logical = options.logical;
	if (options.fields && options.fields.length > 0) node.fields = [...options.fields];
	const orderBy = typeof options.orderBy === "string" ? deserializeOrderByList(options.orderBy) : normalizeOrderBy(options.orderBy);
	if (orderBy) node.orderBy = orderBy;
	if (options.include) {
		const nested = normalizeIncludeAt(options.include, depth + 1);
		if (nested.wildcard) throw new IncludeSpecError(`"${key}" asks for \`*\` inside a nested include. Name the relations you need.`);
		node.children = nested.tree;
	}
	return node;
}
function normalizeIncludeAt(spec, depth) {
	if (Array.isArray(spec)) {
		const tree = {};
		let wildcard = false;
		for (const raw of spec) {
			if (typeof raw !== "string") throw new IncludeSpecError(`${typeof raw} is not a relation name`);
			const name = raw.trim();
			if (!name) continue;
			if (name === "*") {
				wildcard = true;
				continue;
			}
			addPath(tree, name);
		}
		return {
			wildcard,
			tree
		};
	}
	if (typeof spec !== "object" || spec === null) throw new IncludeSpecError(`${typeof spec} is not a list of relations or an include tree`);
	const tree = {};
	let wildcard = false;
	for (const [key, value] of Object.entries(spec)) {
		if (key === "*") {
			if (value) wildcard = true;
			continue;
		}
		if (value === true) {
			ensureNode(tree, key);
			continue;
		}
		if (value === false || value === void 0 || value === null) continue;
		if (typeof value !== "object" || Array.isArray(value)) throw new IncludeSpecError(`"${key}" must be \`true\` or an options object`);
		tree[key] = normalizeOptions(key, value, depth);
	}
	return {
		wildcard,
		tree
	};
}
/**
* Collapse any {@link IncludeSpec} spelling into one tree.
*
* `["author", "comments.author"]` and
* `{ author: true, comments: { include: { author: true } } }` normalize to the
* same value — which is the whole point: the REST parameter can only carry the
* flat spelling, the SDK prefers the tree, and the driver should never learn
* about either.
*
* @throws {IncludeSpecError} for a shape that is not an include at all, or one
*   that nests past {@link MAX_INCLUDE_DEPTH}.
*/
function normalizeInclude(spec) {
	if (spec === void 0 || spec === null) return void 0;
	const normalized = normalizeIncludeAt(spec, 1);
	if (!normalized.wildcard && Object.keys(normalized.tree).length === 0) return void 0;
	return normalized;
}
/**
* Every relation name a tree names, as dotted paths — `["comments",
* "comments.author"]`.
*
* Used to report which names an `include` asked for when one of them is not a
* relation, and to serialize a tree that carries no per-relation options back
* to the flat wire spelling.
*/
function includePaths(tree, prefix = "") {
	const out = [];
	for (const [key, node] of Object.entries(tree)) {
		const path = prefix ? `${prefix}.${key}` : key;
		out.push(path);
		out.push(...includePaths(node.children, path));
	}
	return out;
}
/**
* The relation names an `include` asks for at the top level.
*
* `["author", "comments.author"]` and `{author: true, comments: {...}}` both
* answer `["author", "comments"]` — a *hop*, not a path, because the only
* consumer is `?fields=`, which names keys on the row being returned and a
* nested relation is not one of those.
*
* Derived rather than passed: `include` has four spellings and three of them
* are not a `string[]`, so every consumer that wants the plain names either
* calls this or reimplements the flattening.
*/
function topLevelIncludeNames(spec) {
	const normalized = normalizeInclude(spec);
	if (!normalized) return [];
	return Object.keys(normalized.tree);
}
/** Whether any node in the tree carries per-relation options. */
function hasOptions(tree) {
	return Object.values(tree).some((node) => node.limit !== void 0 || node.where !== void 0 || node.logical !== void 0 || node.orderBy !== void 0 || node.fields !== void 0 || hasOptions(node.children));
}
/**
* Serialize an {@link IncludeSpec} for the REST `?include=` parameter.
*
* Two spellings, and which one is used is decided by the request rather than
* chosen:
*
* - **Comma-separated dotted paths** — `include=author,comments.author`. What a
*   plain include is, what a human types, and what every existing client sends.
* - **JSON**, when any relation carries options — `include={"comments":{"limit":5,
*   "include":{"author":true}}}`. The flat spelling has nowhere to put a
*   `limit`, and inventing a punctuation for it (`comments(limit:5)`) would be a
*   third grammar to learn beside the two this API already has.
*
* The server accepts both on every list and get route, and tells them apart the
* same way this does: a value starting with `{` is JSON.
*/
function serializeInclude(spec) {
	const normalized = normalizeInclude(spec);
	if (!normalized) return void 0;
	if (normalized.wildcard && Object.keys(normalized.tree).length === 0) return "*";
	if (!hasOptions(normalized.tree)) {
		const paths = includePaths(normalized.tree);
		const leaves = paths.filter((path) => !paths.some((other) => other.startsWith(`${path}.`)));
		const all = normalized.wildcard ? ["*", ...leaves] : leaves;
		return all.length > 0 ? all.join(",") : void 0;
	}
	return JSON.stringify(toWireTree(normalized));
}
/**
* A normalized tree, back in the {@link IncludeSpec} spelling a caller writes.
*
* The round trip is what lets a builder accumulate `include` calls: normalize
* each, merge, and hand the result back as a spec the next layer can normalize
* again. Idempotent, so doing it twice changes nothing.
*/
function denormalizeInclude(normalized) {
	return toWireTree(normalized);
}
function mergeTrees(into, from) {
	for (const [key, node] of Object.entries(from)) {
		const existing = into[key];
		if (!existing) {
			into[key] = node;
			continue;
		}
		if (node.limit !== void 0) existing.limit = node.limit;
		if (node.where !== void 0) existing.where = node.where;
		if (node.logical !== void 0) existing.logical = node.logical;
		if (node.orderBy !== void 0) existing.orderBy = node.orderBy;
		if (node.fields !== void 0) existing.fields = node.fields;
		existing.children = mergeTrees(existing.children, node.children);
	}
	return into;
}
/**
* Combine several `include` requests into one.
*
* Repeated `.include(...)` calls on a query builder are additive: each names
* more of the graph to load, and a later one must not discard what an earlier
* one asked for. Assigning instead of merging is why `.include("author")
* .include("tags")` used to load only tags.
*/
function mergeIncludeSpecs(existing, additions) {
	const merged = {
		wildcard: false,
		tree: {}
	};
	const absorb = (spec) => {
		const normalized = normalizeInclude(spec);
		if (!normalized) return;
		merged.wildcard ||= normalized.wildcard;
		mergeTrees(merged.tree, normalized.tree);
	};
	absorb(existing);
	const names = additions.filter((a) => typeof a === "string");
	if (names.length > 0) absorb(names);
	for (const addition of additions) if (typeof addition !== "string") absorb(addition);
	if (!merged.wildcard && Object.keys(merged.tree).length === 0) return void 0;
	return denormalizeInclude(merged);
}
function toWireTree(normalized) {
	const emit = (tree) => {
		const out = {};
		for (const [key, node] of Object.entries(tree)) {
			const options = {};
			if (node.limit !== void 0) options.limit = node.limit;
			if (node.where) options.where = node.where;
			if (node.logical) options.logical = node.logical;
			if (node.orderBy) options.orderBy = node.orderBy;
			if (node.fields) options.fields = node.fields;
			const children = emit(node.children);
			if (Object.keys(children).length > 0) options.include = children;
			out[key] = Object.keys(options).length > 0 ? options : true;
		}
		return out;
	};
	const tree = emit(normalized.tree);
	if (normalized.wildcard) tree["*"] = true;
	return tree;
}
/**
* Read the REST `?include=` parameter, in either spelling.
*
* @throws {IncludeSpecError} for malformed JSON or a tree that nests too deep.
*/
function deserializeInclude(raw) {
	if (raw === void 0 || raw === null) return void 0;
	const text = raw.trim();
	if (!text) return void 0;
	if (text.startsWith("{")) {
		let parsed;
		try {
			parsed = JSON.parse(text);
		} catch {
			throw new IncludeSpecError("the parametrised form must be a JSON object, e.g. {\"comments\":{\"limit\":5,\"include\":{\"author\":true}}}");
		}
		if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new IncludeSpecError("the parametrised form must be a JSON object");
		return parsed;
	}
	return text.split(",").map((s) => s.trim()).filter(Boolean);
}
//#endregion
//#region src/data/query_builder.ts
function or(...conditions) {
	return {
		type: "or",
		conditions
	};
}
function and(...conditions) {
	return {
		type: "and",
		conditions
	};
}
/**
* Negate a group: `not(a)` is `NOT a`, and `not(a, b)` is `NOT (a AND b)`.
*
* The conjunction, not the disjunction — one rule, stated on
* {@link LogicalCondition} and applied identically by the wire codec, the REST
* `?not=` parameter and every driver compiler. Groups nest, so De Morgan's
* other half is `not(or(a, b))`.
*
* It compiles to a real SQL `NOT (...)` rather than to inverted operators,
* which matters more than it looks: SQL is three-valued, so `NOT (a AND b)` and
* `(NOT a) OR (NOT b)` stop agreeing the moment a NULL is involved, and only
* one of them is the query the caller wrote. It also means a negation includes
* rows whose column is NULL — which is what `NOT` means.
*/
function not(...conditions) {
	return {
		type: "not",
		conditions
	};
}
function cond(column, operator, value) {
	return {
		column,
		operator,
		value
	};
}
var QueryBuilder = class {
	collection;
	params = { where: {} };
	constructor(collection) {
		this.collection = collection;
	}
	where(columnOrCondition, operator, value) {
		if (typeof columnOrCondition === "object" && columnOrCondition !== null && "type" in columnOrCondition) {
			const next = columnOrCondition;
			this.params.logical = this.params.logical ? {
				type: "and",
				conditions: [this.params.logical, next]
			} : next;
			return this;
		}
		if (!this.params.where) this.params.where = {};
		const column = columnOrCondition;
		const condition = [operator, value];
		const existing = this.params.where[column];
		if (existing === void 0) this.params.where[column] = condition;
		else if (Array.isArray(existing) && existing.length > 0 && Array.isArray(existing[0])) this.params.where[column].push(condition);
		else {
			let firstCondition;
			if (Array.isArray(existing) && existing.length === 2 && typeof existing[0] === "string") firstCondition = existing;
			else firstCondition = ["==", existing];
			this.params.where[column] = [firstCondition, condition];
		}
		return this;
	}
	/**
	* Order the results by a specific column.
	*
	* Called again, this adds a tie-breaker rather than replacing the sort:
	* keys apply in the order they were added.
	*
	* @example
	* client.collection('users').orderBy('createdAt', 'desc').find()
	* @example
	* client.collection('users').orderBy('roles').orderBy('createdAt', 'desc').find()
	*/
	orderBy(column, direction = "asc") {
		const existing = normalizeOrderBy(this.params.orderBy) ?? [];
		this.params.orderBy = [...existing, [column, direction]];
		return this;
	}
	/**
	* Limit the number of results returned.
	*/
	limit(count) {
		this.params.limit = count;
		return this;
	}
	/**
	* Skip the first N results.
	*/
	offset(count) {
		this.params.offset = count;
		return this;
	}
	/**
	* Set a free-text search string if supported by the backend.
	*/
	search(searchString, options) {
		this.params.searchString = searchString;
		if (options?.explain !== void 0) this.params.searchExplain = options.explain;
		return this;
	}
	/**
	* Order rows by nearest-neighbour distance to `vector`, closest first.
	*
	* Postgres only, over a property declared as `type: "vector"`. Rows come
	* back with a `_distance`; `where` filters before the ordering.
	*/
	vectorSearch(property, vector, options) {
		this.params.vectorSearch = {
			property,
			vector,
			...options?.distance !== void 0 && { distance: options.distance },
			...options?.threshold !== void 0 && { threshold: options.threshold }
		};
		return this;
	}
	/**
	* Include related entities in the response.
	* Relations will be populated with full entity data instead of just IDs.
	*
	* @param relations - Relation names to include, or "*" for all.
	* @example
	* // Include specific relations
	* client.data.posts.include("tags", "author").find()
	*
	* // Include all relations
	* client.data.posts.include("*").find()
	*/
	include(...relations) {
		this.params.include = relations;
		return this;
	}
	/**
	* Execute the find query and return the results.
	*/
	async find() {
		return this.collection.find(this.params);
	}
	/**
	* Listen to realtime updates matching this query.
	*/
	listen(onUpdate, onError) {
		if (!this.collection.listen) throw new Error("Listen is only available when RebaseClient is configured with a websocketUrl.");
		return this.collection.listen(this.params, onUpdate, onError);
	}
};
//#endregion
//#region src/data/paginate.ts
/**
* The pagination engine behind `iterate()` / `findAll()`.
*
* It lives here, above both transports, on purpose: the HTTP client and the
* in-process accessor implement the same `SDKCollectionClient` contract, and a
* helper written twice is a helper that drifts. Both call into this file, so
* "the SDK paginates like *this*" has exactly one definition.
*
* Everything below is expressed in terms of a single `find(params)` function,
* which is all either transport has to supply.
*/
/** Rows requested per page when the caller does not say. */
var DEFAULT_PAGE_SIZE = 200;
/** Rows `findAll()` will materialise before it refuses to continue. */
var DEFAULT_FIND_ALL_MAX_ROWS = 1e4;
/**
* Requests one walk may make before it gives up on the server ever saying
* `hasMore: false`. At the default page size that is two million rows — far
* past any legitimate walk, and short of running forever.
*/
var DEFAULT_MAX_PAGES = 1e4;
/**
* Thrown when a walk stops for a reason the caller needs to know about.
*
* Every one of these is a case where the alternative would be silent: a
* truncated array that looks complete, or a loop that never returns. Check
* {@link code} to tell them apart.
*/
var RebasePaginationError = class RebasePaginationError extends Error {
	code;
	constructor(code, message) {
		super(message);
		this.name = "RebasePaginationError";
		this.code = code;
		Object.setPrototypeOf(this, RebasePaginationError.prototype);
	}
};
/**
* Resolve `limit`/`offset`/`page` into the window a read will actually use.
*
* Lives here, next to the walk, for the reason at the top of this file: every
* transport has to mean the same thing by "page two". Four of them did not —
* the REST layer strode by {@link DEFAULT_LIST_LIMIT}, the local-first
* evaluator by {@link DEFAULT_PAGE_SIZE}, the in-process accessor by 20, and
* the published type documented a fourth number. Pages that overlap or skip
* rows are the mildest of those outcomes.
*
* `page` wins over `offset`, as {@link FindParams} documents. `driverOffset`
* is the value to hand a driver: it stays `undefined` when the caller named no
* offset, because keyset pagination seeks with a `where` clause and must not
* look like it is paging by offset.
*/
function resolveFindWindow(params) {
	const limit = params?.limit ?? DEFAULT_LIST_LIMIT;
	const offset = params?.page != null ? Math.max(0, (params.page - 1) * limit) : params?.offset ?? 0;
	return {
		limit,
		offset,
		driverOffset: params?.page != null ? offset : params?.offset
	};
}
function normalizePageSize(raw) {
	if (raw === void 0 || !Number.isFinite(raw)) return 200;
	return Math.max(1, Math.floor(raw));
}
function normalizeMaxPages(raw) {
	if (raw === void 0) return DEFAULT_MAX_PAGES;
	if (raw === Number.POSITIVE_INFINITY) return raw;
	if (!Number.isFinite(raw)) return DEFAULT_MAX_PAGES;
	return Math.max(1, Math.floor(raw));
}
function normalizeMaxRows(raw) {
	if (raw === void 0) return DEFAULT_FIND_ALL_MAX_ROWS;
	if (raw === Number.POSITIVE_INFINITY) return raw;
	if (!Number.isFinite(raw)) return DEFAULT_FIND_ALL_MAX_ROWS;
	return Math.max(0, Math.floor(raw));
}
/**
* Walk every row a query matches, yielding one row at a time and fetching the
* next page only when the consumer asks for it.
*
* See {@link SDKCollectionClient.iterate} for the caller-facing contract,
* including the offset-drift caveat and the `cursor` alternative.
*
* @param find   the transport's single-page read
* @param params `find()` parameters minus the window, plus the walk options
* @param label  the collection name, so an error says which walk failed
*/
async function* paginateFind(find, params, label = "collection") {
	const { pageSize, cursor, maxPages, ...rest } = params ?? {};
	const findParams = { ...rest };
	const size = normalizePageSize(pageSize);
	const pageCap = normalizeMaxPages(maxPages);
	const seekRequested = cursor !== void 0 && cursor !== null;
	if (seekRequested) {
		const field = typeof cursor === "string" ? cursor : cursor.field;
		const requested = typeof cursor === "object" && cursor !== null ? cursor.direction : void 0;
		if (!normalizeOrderBy(findParams.orderBy)) findParams.orderBy = [field, requested ?? "asc"];
	}
	let offset = 0;
	let pages = 0;
	let after;
	for (;;) {
		if (pages >= pageCap) throw new RebasePaginationError("max-pages", `Iterating "${label}" made ${pages} requests without the server reporting the end of the collection. Stopping rather than looping forever — raise \`maxPages\` if the walk is genuinely this long, or check that the backend sets \`meta.hasMore\`.`);
		const pageParams = {
			...findParams,
			limit: size
		};
		if (seekRequested) {
			if (after) pageParams.after = after;
		} else pageParams.offset = offset;
		const page = await find(pageParams);
		pages += 1;
		const rows = page?.data ?? [];
		if (rows.length === 0) return;
		for (const row of rows) yield row;
		if (page?.meta?.hasMore !== true) return;
		if (seekRequested) {
			const next = page.meta.nextCursor;
			if (!next) throw new RebasePaginationError("cursor-missing", `Cannot seek past the last row of "${label}": the server reported another page but issued no cursor for it. An ordering with no stored value to compare against — relevance (\`_score\`) — cannot key a cursor. Drop \`cursor\` to page by offset.`);
			if (next === after) throw new RebasePaginationError("cursor-stalled", `Iterating "${label}" is stuck: two pages in a row ended on the same cursor, so the walk cannot advance. Continuing would loop forever. Page by offset instead, or report this — a cursor that does not move is a server-side bug.`);
			after = next;
		} else offset += rows.length;
	}
}
/**
* {@link paginateFind}, collected into an array under a ceiling.
*
* See {@link SDKCollectionClient.findAll}.
*/
async function collectAllPages(find, params, label = "collection") {
	const { maxRows, ...rest } = params ?? {};
	const cap = normalizeMaxRows(maxRows);
	const out = [];
	for await (const row of paginateFind(find, rest, label)) {
		out.push(row);
		if (out.length > cap) throw new RebasePaginationError("max-rows", `findAll("${label}") matched more than ${cap} rows. Returning the first ${cap} would look like the whole answer and quietly not be one, so this throws instead. Raise \`maxRows\` if you meant to load them all, or stream with \`iterate()\`.`);
	}
	return out;
}
/**
* Build the `iterate` / `findAll` pair for one collection from its `find`.
*
* Both transports call this, which is what keeps the two implementations from
* being two implementations.
*/
function createPaginationHelpers(find, label) {
	return {
		iterate: (params) => paginateFind(find, params, label),
		findAll: (params) => collectAllPages(find, params, label)
	};
}
//#endregion
//#region src/data/filter-dialect.ts
/**
* REST wire-format adapter for the unified filter system.
*
* This module is the ONLY code in the entire codebase that knows about
* PostgREST-style dot-syntax strings (`eq.active`, `gt.18`, `in.(a,b)`).
* Everything else speaks `FilterValues` exclusively.
*
* Wire-format values are always strings — the wire format carries no type
* metadata, so type coercion is the responsibility of the server-side data
* driver which has access to the collection schema.
*
* Structural characters inside a value are backslash-escaped: `,` → `\,`,
* `(` → `\(`, `)` → `\)`, and a literal backslash as `\\`. Decoding is
* deliberately conservative — only those four sequences are decoded, so a
* backslash that arrives unescaped from an older client survives intact.
*
* @module
*/
/**
* Serialize a JS value to its querystring representation.
* `null` is serialized as the literal string `"null"`.
* Relation values (`EntityRelation` instances or `{ __type: "relation", id, path }`
* objects) are serialized as their raw id — the wire format only carries the
* value to compare against the FK column.
*/
function stringifyValue(value) {
	if (value === null) return "null";
	const relation = normalizeToEntityRelation(value);
	if (relation) return String(relation.id);
	return String(value);
}
/**
* Characters that carry structure in the wire format and must therefore be
* escaped inside a value: the separator, the group delimiters, and the escape
* character itself.
*
* Parentheses are here because `and(...)`/`or(...)` groups are parsed by
* tracking paren depth. A value containing one is not merely ambiguous, it
* moves where the parser thinks the group ends.
*/
var WIRE_SPECIALS = /[\\,()]/g;
/**
* Escape a value for the wire format: `\` → `\\`, `,` → `\,`, `(` → `\(`,
* `)` → `\)`.
*/
/**
* The wire spelling of an empty list.
*
* A lone backslash: unproducible by {@link escapeWireValue}, which doubles
* every backslash it emits, so it cannot collide with any real item.
*/
var EMPTY_LIST_TOKEN = "\\";
function escapeWireValue(value) {
	return value.replace(WIRE_SPECIALS, (ch) => `\\${ch}`);
}
/**
* Unescape a wire-format value.
*
* **Conservative**, and deliberately so: only the four sequences
* {@link escapeWireValue} actually produces are decoded. A backslash followed
* by anything else is left exactly as it is.
*
* This used to consume the backslash before *any* character, which is
* indistinguishable for anything this codec emitted — it only ever emits those
* four — but not for input arriving from elsewhere. A client on an older
* release sends a Windows path or a LIKE pattern with a literal `C:\x`
* unescaped, and greedy unescaping silently turned it into `C:x`, changing
* which rows matched. Decoding only what the encoder can produce makes the two
* directions agree across versions.
*/
function unescapeWireValue(value) {
	let result = "";
	for (let i = 0; i < value.length; i++) {
		const next = value[i + 1];
		if (value[i] === "\\" && (next === "\\" || next === "," || next === "(" || next === ")")) {
			result += next;
			i++;
			continue;
		}
		result += value[i];
	}
	return result;
}
/**
* Split a parenthesized list string on unescaped commas.
* Input is the content between `(` and `)`.
*
* @example
* splitListItems("admin,editor")          // ["admin", "editor"]
* splitListItems("hello\\, world,foo")    // ["hello, world", "foo"]
*/
function splitListItems(inner) {
	const items = [];
	let current = "";
	for (let i = 0; i < inner.length; i++) if (inner[i] === "\\" && i + 1 < inner.length) {
		current += inner[i] + inner[i + 1];
		i++;
	} else if (inner[i] === ",") {
		items.push(unescapeWireValue(current));
		current = "";
	} else current += inner[i];
	items.push(unescapeWireValue(current));
	return items;
}
/**
* Split a group body on commas at paren depth 0, honouring escapes.
*
* The escape-awareness is the point. The splitter used to track only paren
* depth, so a comma inside a scalar value ended a condition:
* `or(name.eq.Doe, John,age.gte.18)` parsed as *three* conditions, the middle
* one a fabricated `" John" == true`. On an `or` that widens the result set,
* and nothing anywhere reports an error — the query simply stops meaning what
* the caller wrote.
*/
function splitGroupItems(inner) {
	const parts = [];
	let depth = 0;
	let start = 0;
	for (let i = 0; i < inner.length; i++) {
		const ch = inner[i];
		if (ch === "\\" && i + 1 < inner.length) {
			i++;
			continue;
		}
		if (ch === "(") depth++;
		else if (ch === ")") depth--;
		else if (ch === "," && depth === 0) {
			parts.push(inner.slice(start, i));
			start = i + 1;
		}
	}
	parts.push(inner.slice(start));
	return parts;
}
/**
* Operator tables as `Map`s, because the key comes off the wire.
*
* Indexed as plain objects, every `Object.prototype` member answered: a query
* string of `?f=valueOf.x` found a truthy "operator" — the inherited function —
* and `deserializeTuple` returned it *as the operator*, so a function object
* travelled on into the compilers in place of a `WhereFilterOp`. The guard one
* line below (`if (!canonicalOp)`) reads as though it rejects anything unknown,
* and does not: `Object.prototype` is not unknown to a plain object.
*
* Same shape as the prototype-key defects swept out of `setIn`, `getIn`,
* `mergeDeep`, `unflattenObject` and `FOREIGN_CONVENTION_UIDS`.
*/
var REST_OP_LOOKUP = new Map(Object.entries(REST_TO_CANONICAL));
var CANONICAL_OP_LOOKUP = new Map(Object.entries(CANONICAL_TO_REST));
/** The operator spellings a rejection lists back to the caller. */
var VALID_OPERATOR_LIST = ALL_WHERE_FILTER_OPS.join(", ");
/**
* A filter condition named an operator this dialect does not have.
*
* ## Why this throws, rather than returning a typed rejection
*
* `deserializeFilter` is the *shared* codec: the REST ingress
* (`packages/server/src/api/rest/query-parser.ts`), the browser SDK and the
* admin panel (`buildRebaseData.ts`) all decode through it. Two constraints
* follow.
*
* - It cannot throw the server's `ApiError`. `@rebasepro/common` does not
*   depend on `@rebasepro/server` (the dependency runs the other way), and a
*   browser client has no error handler to render an `ApiError` with. So the
*   rejection is this plain `Error` subclass, whose `message` reads correctly
*   wherever it surfaces — a rejected promise in an app, a 400 body over HTTP.
* - It cannot be a returned rejection *value*. Every caller assigns the result
*   straight into a query it is about to run; a sentinel that none of them
*   check would be ignored, which is exactly the silently-wrong-filter failure
*   this exists to stop. Throwing is also what this file already does for the
*   sibling cases — `serializeTuple` on an unknown canonical operator,
*   `deserializeLogicalCondition` past the nesting bound — and the REST parser
*   already converts the latter into a 400.
*
* `statusCode`, `code` and `details` are carried as fields because the server's
* Hono error handler duck-types those off any thrown error: a decode path that
* forgets to convert still answers 400 with the canonical envelope instead of a
* 500 that says "An unexpected error occurred". `query-parser.ts` converts
* explicitly all the same — that is the path the contract is stated on, and an
* incidental 400 is not a contract.
*/
var UnknownFilterOperatorError = class extends Error {
	/** The field the condition was written against. */
	field;
	/** The operator string as it arrived, verbatim. */
	operator;
	/** Every operator this dialect accepts, in canonical spelling. */
	validOperators = ALL_WHERE_FILTER_OPS;
	/** See the class docblock: read by the server's error handler. */
	statusCode = 400;
	code = "UNKNOWN_FILTER_OPERATOR";
	details;
	constructor(field, operator) {
		super(`Unknown filter operator '${operator}' on field '${field}'. Valid operators: ${VALID_OPERATOR_LIST}`);
		this.name = "UnknownFilterOperatorError";
		this.field = field;
		this.operator = operator;
		this.details = {
			field,
			operator,
			validOperators: ALL_WHERE_FILTER_OPS
		};
	}
};
/**
* Two to three characters of ASCII punctuation and nothing else — the shape
* every symbolic operator has (`==`, `>=`, `<>`, `~~`, `!!`, `>>`, `===`), and
* one a column value effectively never has.
*
* Two characters minimum on purpose. A *single* punctuation character is a
* perfectly ordinary value — `{ grade: ["-", "+"] }` is a two-item list, not a
* condition — and the only single-character operator anyone actually mistypes
* is `=`, which is named separately below. `<` and `>` need no special case:
* they are real operators and resolve.
*/
var SYMBOLIC_OPERATOR = /^[^\p{L}\p{N}\s]{2,3}$/u;
/** Lowercase, strip everything that is not a letter or digit. */
function normalizeOperatorName(op) {
	return op.toLowerCase().replace(/[^a-z0-9]/g, "");
}
/**
* Every real operator name with its case and separators removed, so a
* respelling of one — `arrayContains`, `not_in`, `NOT-LIKE`, `isNull` — is
* recognised as an attempt at an operator rather than read as a value.
*
* These are rejected rather than accepted: admitting a second spelling of an
* operator would leave two wire spellings of one thing, and the rejection
* message names the one that works.
*/
var RESPELLED_OPERATORS = new Set([...ALL_WHERE_FILTER_OPS, ...Object.keys(REST_TO_CANONICAL)].map(normalizeOperatorName));
/**
* Operator names *other* query dialects use, which this one does not have.
*
* This list is curated, and deliberately so. For a word-shaped string there is
* no rule that separates "an operator the caller guessed" from "a value that
* happens to be a word": `{ tags: ["a", "b"] }` has to keep meaning a two-item
* `in` list, so the codec cannot simply refuse every unrecognised word in
* position 0. The line is therefore drawn by name, and only around names whose
* use as an operator is far more likely than their use as one of two sibling
* values. `contains` is the motivating case — the first thing a developer
* reaches for, and until now it compiled to `title IN ('contains', 'Hell')`.
*
* Genuinely ambiguous single words (`any`, `all`, `exists`, `search`, `not`)
* are left off: as operators they are rare, and as enum values they are common.
* Everywhere else the tie goes to *rejecting*, because a 400 naming the
* supported set costs the caller one round trip, and the alternative — which is
* what every name on this list used to produce — is a query that runs, returns
* rows, and is wrong.
*/
var NEAR_MISS_OPERATORS = /* @__PURE__ */ new Set([
	"contains",
	"notcontains",
	"doesnotcontain",
	"doesnotcontains",
	"includes",
	"notincludes",
	"startswith",
	"notstartswith",
	"beginswith",
	"startingwith",
	"endswith",
	"notendswith",
	"matches",
	"notmatches",
	"regex",
	"regexp",
	"between",
	"notbetween",
	"equals",
	"notequals",
	"equalto",
	"isequalto",
	"isnotequalto",
	"greaterthan",
	"greaterthanorequal",
	"greaterthanorequalto",
	"lessthan",
	"lessthanorequal",
	"lessthanorequalto",
	"isempty",
	"isnotempty",
	"oneof",
	"noneof",
	"anyof",
	"allof",
	"null",
	"isnullorempty"
]);
/**
* Was this string *meant* as an operator?
*
* Only consulted after {@link toCanonicalOp} has already failed to resolve it,
* so a `true` here is always a rejection.
*/
function isOperatorShaped(op) {
	if (op === "=") return true;
	if (SYMBOLIC_OPERATOR.test(op)) return true;
	const normalized = normalizeOperatorName(op);
	if (!normalized) return false;
	return RESPELLED_OPERATORS.has(normalized) || NEAR_MISS_OPERATORS.has(normalized);
}
/**
* Read a `[op, value]` tuple, if that is what this is.
*
* Three outcomes, and the middle one is the defect this function exists for:
*
* - the operator resolves (canonical *or* REST spelling) → the canonical tuple;
* - the operator does not resolve but was plainly meant as one → throw;
* - it does not look like an operator at all → `undefined`, and the caller
*   falls back to reading the array as a list of values.
*
* The old test was `toCanonicalOp(raw[0]) === raw[0]`, i.e. canonical spelling
* only, with *everything else* — including every REST short-code — dropping
* through to `["in", raw]`. So the operator string itself became a value in a
* membership test: `["!!", "Hello"]` compiled to `title IN ('!!','Hello')`,
* which matches, and the caller got back rows their filter was written to
* exclude. `["eq", "active"]` had the same shape of failure.
*/
function readTuple(field, raw) {
	if (!Array.isArray(raw) || raw.length !== 2) return void 0;
	const [op, value] = raw;
	if (typeof op !== "string") return void 0;
	const canonical = toCanonicalOp(op);
	if (canonical) return [canonical, value];
	if (op.includes(".")) return void 0;
	if (isOperatorShaped(op)) throw new UnknownFilterOperatorError(field, op);
}
/**
* Encode the `<op>.<value>` half of a wire condition.
*
* This is the single leaf encoder. Both wire positions that carry a condition
* — a top-level query parameter (`?status=eq.active`) and a leaf inside an
* `and(...)`/`or(...)` group (`or(status.eq.active,…)`) — go through it, so a
* rule expressed here holds in both. The group serializer used to carry its
* own copy, and the copy had drifted on every rule that matters: `null` went
* out as the four-character string, the empty list as `()`, and an operator
* this dialect does not have was silently rewritten to `eq` — a filter that
* ran, returned rows, and answered a different question than the one asked.
*
* `escapeScalar` is the one thing the two positions legitimately disagree
* about. A scalar in a query parameter owns the whole value and needs no
* escaping; a scalar inside a group sits between the same commas a list item
* does, so a comma in it would end the condition early.
*/
function serializeOperatorAndValue(op, value, { escapeScalar, where }) {
	if (typeof op !== "string") throw new TypeError(`${where}: operator must be a string, got ${typeof op}`);
	const restOp = CANONICAL_OP_LOOKUP.get(op);
	if (!restOp) throw new TypeError(`${where}: unknown operator "${op}". Valid operators: ${Object.keys(CANONICAL_TO_REST).join(", ")}`);
	if (value === null && (op === "==" || op === "!=")) return op === "==" ? "isnull.null" : "notnull.null";
	if (NULL_OPS.has(op)) return `${restOp}.null`;
	if (Array.isArray(value)) {
		if (value.length === 0) return `${restOp}.(${EMPTY_LIST_TOKEN})`;
		return `${restOp}.(${value.map((v) => escapeWireValue(stringifyValue(v))).join(",")})`;
	}
	const scalar = stringifyValue(value);
	return `${restOp}.${escapeScalar ? escapeWireValue(scalar) : scalar}`;
}
/**
* Serialize a single canonical condition tuple to a PostgREST dot-string.
*
* Throws `TypeError` if the input is not a valid `[WhereFilterOp, unknown]` tuple.
*
* @example
* serializeTuple(["==", "active"])           // "eq.active"
* serializeTuple(["in", ["admin","editor"]]) // "in.(admin,editor)"
* serializeTuple([">=", 18])                 // "gte.18"
*/
function serializeTuple(tuple) {
	if (!Array.isArray(tuple) || tuple.length !== 2) throw new TypeError(`serializeTuple: expected a [WhereFilterOp, value] tuple, got ${JSON.stringify(tuple)}`);
	const [op, value] = tuple;
	return serializeOperatorAndValue(op, value, {
		escapeScalar: false,
		where: "serializeTuple"
	});
}
/**
* Convert `FilterValues` (or `WireFilterValues`) to a PostgREST-style
* querystring record.
*
* - Canonical `[WhereFilterOp, value]` tuples are serialized strictly.
* - Pre-serialized PostgREST strings (e.g. `"eq.published"`) are passed through.
* - Single conditions produce a string value.
* - Multiple conditions on the same field produce a string array (repeated params).
*
* @example
* serializeFilter({ status: ["==", "active"] })
* // → { status: "eq.active" }
*
* serializeFilter({ age: [[">=", 18], ["<", 65]] })
* // → { age: ["gte.18", "lt.65"] }
*
* // Pre-serialized strings pass through unchanged:
* serializeFilter({ status: "eq.published" })
* // → { status: "eq.published" }
*/
function serializeFilter(filter) {
	const result = {};
	for (const [field, condition] of Object.entries(filter)) {
		if (condition === void 0) continue;
		if (typeof condition === "string") {
			result[field] = condition;
			continue;
		}
		if (Array.isArray(condition) && condition.length > 0 && Array.isArray(condition[0])) result[field] = condition.map(serializeTuple);
		else result[field] = serializeTuple(condition);
	}
	return result;
}
/**
* The spellings a null-testing operator's operand may take.
*
* The serializer writes `isnull.null`; a hand-written `isnull.true` means the
* same thing and has always been accepted. Anything else after the operator is
* not an operand it has — `notnull.reason` is a *value* — see
* {@link deserializeSingle}.
*/
var NULL_OPERANDS = /* @__PURE__ */ new Set([
	"null",
	"true",
	"false",
	""
]);
/**
* Parse a single PostgREST dot-string into a `[WhereFilterOp, unknown]` tuple.
*
* All values are returned as strings — the wire format carries no type
* metadata, so coercion is the data driver's responsibility.
*
* ## When a leading segment is an operator, and when it is part of the value
*
* `?status=in.progress` and `?status=in.(a,b)` differ by one character and mean
* entirely different things, and the reading here decides which. The rule, in
* full:
*
* > A dot-string is read as `operator.operand` **only** when its first segment
* > names a known REST operator **and** what follows is a well-formed operand
* > *for that operator's arity*. Otherwise the whole string is the value.
*
* Arity, per operator family:
*
* - **List** operators (`in`, `nin`, `csa` — `LIST_OPS`) take a parenthesised
*   list and nothing else. `in.(draft,review)` is the operator; `in.progress`
*   is the *value* `"in.progress"`, because there is no list there and so no
*   `in` filter that could have been written. That case used to compile to
*   `status IN ('progress')` — a filter the caller never wrote, quietly
*   matching the wrong rows and, on a status field, hiding every row they were
*   looking for.
* - **Null** operators (`isnull`, `notnull` — {@link NULL_OPS}) take no
*   operand: only {@link NULL_OPERANDS}. `notnull.reason` is the value
*   `"notnull.reason"`, not "reason is not null".
* - **Everything else** takes one scalar, and any remainder is one — including
*   the empty string, so `eq.` really is "equals the empty string".
*
* ### The one ambiguity that remains, and how to write past it
*
* A scalar operator's operand is unconstrained, so `?status=like.that` is a
* `LIKE 'that'` and no rule at this layer can tell it from the literal value
* `"like.that"` — both are well-formed encodings, and picking either by guess
* would break the other. Two spellings say "value" unambiguously, and both
* round-trip:
*
* - `?status=eq.like.that` — name the operator. The *first* segment is consumed
*   as the operator and everything after it is the value, dots and all. This is
*   what `serializeFilter` emits, which is why the SDK never meets the
*   ambiguity at all.
* - `?where={"status":["==","like.that"]}` — the JSON dialect's tuple form.
*
* Values that merely *contain* dots (`user@host.com`, `1.2.3`) were never
* ambiguous: their first segment names no operator to begin with.
*/
function deserializeSingle(raw) {
	const dotIndex = raw.indexOf(".");
	if (dotIndex === -1) return ["==", raw];
	const prefix = raw.substring(0, dotIndex);
	const rest = raw.substring(dotIndex + 1);
	const canonicalOp = REST_OP_LOOKUP.get(prefix);
	if (!canonicalOp) return ["==", raw];
	if (NULL_OPS.has(canonicalOp)) {
		if (!NULL_OPERANDS.has(rest)) return ["==", raw];
		return [canonicalOp, null];
	}
	if (rest.startsWith("(") && rest.endsWith(")")) {
		const inner = rest.slice(1, -1);
		return [canonicalOp, inner === EMPTY_LIST_TOKEN ? [] : splitListItems(inner)];
	}
	if (LIST_OPS.has(canonicalOp)) return ["==", raw];
	return [canonicalOp, rest];
}
/**
* Convert a PostgREST-style querystring record to `FilterValues`.
*
* - String values are parsed as single conditions.
* - String arrays (repeated query params) become multiple conditions on the same field.
*
* @example
* deserializeFilter({ status: "eq.active" })
* // → { status: ["==", "active"] }
*
* deserializeFilter({ age: ["gte.18", "lt.65"] })
* // → { age: [[">=", "18"], ["<", "65"]] }
*
* @throws {UnknownFilterOperatorError} when a condition names an operator this
* dialect does not have. See that class for why a rejection here is a throw.
*/
function deserializeFilter(query) {
	const result = {};
	for (const [field, raw] of Object.entries(query)) {
		if (raw === void 0) continue;
		const tuple = readTuple(field, raw);
		if (tuple) {
			result[field] = tuple;
			continue;
		}
		if (Array.isArray(raw)) {
			if (raw.length === 0) continue;
			if (Array.isArray(raw[0])) {
				const tuples = raw.map((item) => readTuple(field, item));
				if (tuples.every((t) => t !== void 0)) {
					result[field] = tuples;
					continue;
				}
			}
			if (raw.length === 1) result[field] = typeof raw[0] === "string" ? deserializeSingle(raw[0]) : ["==", raw[0]];
			else if (typeof raw[0] === "string" && raw[0].includes(".")) result[field] = raw.map((r) => typeof r === "string" ? deserializeSingle(r) : ["==", r]);
			else result[field] = ["in", raw];
		} else if (typeof raw === "string") result[field] = deserializeSingle(raw);
		else result[field] = ["==", raw];
	}
	return result;
}
/**
* Serialize a `LogicalCondition` or `FilterCondition` to its wire-format string.
*
* Leaf encoding is {@link serializeOperatorAndValue}, the same function
* `serializeTuple` uses, so `null`, the empty list and an unknown operator
* behave identically inside a group and in a query parameter.
*
* @throws {TypeError} when a leaf names an operator this dialect does not have.
* It used to fall back to `eq`, which turned `age >= 18` into `age = 18` with
* no diagnostic anywhere.
*
* @example
* serializeLogicalCondition({ column: "status", operator: "==", value: "active" })
* // → "status.eq.active"
*
* serializeLogicalCondition({ column: "deleted_at", operator: "==", value: null })
* // → "deleted_at.isnull.null"
*
* serializeLogicalCondition({ type: "or", conditions: [...] })
* // → "or(status.eq.active,status.eq.pending)"
*/
function serializeLogicalCondition(cond) {
	if ("type" in cond) {
		const inner = (cond.conditions ?? []).map(serializeLogicalCondition).join(",");
		return `${cond.type}(${inner})`;
	}
	return `${escapeWireValue(cond.column)}.${serializeOperatorAndValue(cond.operator, cond.value, {
		escapeScalar: true,
		where: "serializeLogicalCondition"
	})}`;
}
/**
* Split a leaf condition into `column`, operator token and value.
*
* The naive reading — column is everything before the first dot, operator is
* everything up to the second — cannot express a relation path. A filter on
* `author.name` serializes to `author.name.eq.bob` and came back as the column
* `author` with the operator `name`, which resolves to nothing, so the
* fallback made it `author == "eq.bob"`: a condition that runs and matches
* nothing, on a column the caller never named.
*
* So the operator is found rather than assumed: it is the first dot-separated
* segment after the column that resolves to a real operator. Everything before
* it is the column, everything after is the value. `version.eq.1.2.3` reads as
* `version == "1.2.3"` because the scan stops at the first match — the `eq` at
* offset 1, not a later segment — and `metadata->>x.eq.5` never had dots in the
* column to begin with.
*
* Returns `undefined` when no segment resolves — `status.active`, an equality
* written without an operator, which the caller handles.
*/
function splitLeafCondition(str) {
	let limit = str.length;
	for (let i = 0; i < str.length; i++) {
		if (str[i] === "\\") {
			i++;
			continue;
		}
		if (str[i] === "(") {
			limit = i;
			break;
		}
	}
	const dots = [];
	for (let i = 0; i < limit; i++) {
		if (str[i] === "\\") {
			i++;
			continue;
		}
		if (str[i] === ".") dots.push(i);
	}
	for (let i = 1; i < dots.length; i++) {
		const operator = toCanonicalOp(str.substring(dots[i - 1] + 1, dots[i]));
		if (!operator) continue;
		return {
			column: unescapeWireValue(str.substring(0, dots[i - 1])),
			operator,
			value: str.substring(dots[i] + 1)
		};
	}
}
/**
* Parse a logical condition wire-format string back into a
* `LogicalCondition` or `FilterCondition`.
*
* @example
* deserializeLogicalCondition("status.eq.active")
* // → { column: "status", operator: "==", value: "active" }
*
* deserializeLogicalCondition("or(status.eq.active,age.gte.18)")
* // → { type: "or", conditions: [...] }
*/
/**
* How deeply `or(...)`/`and(...)` groups may nest.
*
* This parser recurses once per level, on a value that arrives in a query
* string. Unbounded, twenty thousand levels reached `RangeError: Maximum call
* stack size exceeded`, which a caller sees as a 500 about the call stack
* rather than a 400 about their filter. Node's 16 KB header cap keeps a GET
* below that in practice, but "the HTTP layer happens to stop it" is not a
* bound this parser should rely on.
*
* Thirty-two is far past anything a real filter expresses; the deepest in this
* repository's own tests is three.
*/
var MAX_LOGICAL_NESTING_DEPTH = 32;
function deserializeLogicalCondition(str, nesting = 0) {
	if (nesting > 32) throw new Error(`Filter groups nest more than 32 levels deep. Flatten the condition — \`or(a,or(b,c))\` is \`or(a,b,c)\`.`);
	const logicalMatch = str.match(/^(and|or|not)\((.+)\)$/);
	if (logicalMatch) {
		const type = logicalMatch[1];
		const innerStr = logicalMatch[2];
		return {
			type,
			conditions: splitGroupItems(innerStr).map((part) => deserializeLogicalCondition(part, nesting + 1))
		};
	}
	const leaf = splitLeafCondition(str);
	if (!leaf) {
		const firstDot = str.indexOf(".");
		if (firstDot === -1) return {
			column: unescapeWireValue(str),
			operator: "==",
			value: true
		};
		return {
			column: unescapeWireValue(str.substring(0, firstDot)),
			operator: "==",
			value: unescapeWireValue(str.substring(firstDot + 1))
		};
	}
	const { column, operator, value: valueStr } = leaf;
	if (NULL_OPS.has(operator)) return {
		column,
		operator,
		value: null
	};
	if (valueStr.startsWith("(") && valueStr.endsWith(")")) {
		const inner = valueStr.slice(1, -1);
		return {
			column,
			operator,
			value: inner === EMPTY_LIST_TOKEN ? [] : splitListItems(inner)
		};
	}
	return {
		column,
		operator,
		value: unescapeWireValue(valueStr)
	};
}
//#endregion
//#region src/data/buildRebaseData.ts
/**
* What a client says when its data source cannot subscribe.
*
* Named rather than inlined so the sentence a caller sees does not depend on
* which of the two adapters below happened to build the client.
*/
var noRealtime = (slug) => `Realtime is not available for "${slug}": its data source does not support subscriptions.`;
/** What a client says when its data source cannot count. */
var noCount = (slug) => `Counting is not available for "${slug}": its data source does not support it.`;
/**
* Derive the response key an aggregate comes back under.
*
* `sum(total)` → `sum_total`, `count()` → `count`. Written once, here, because
* the REST parser derives the same alias from `?select=sum(total)` and the two
* have to agree — a caller reading `row.sum_total` off an SDK result and off an
* HTTP response is reading the same key or the SDK is broken.
*/
function aggregateAlias(fn, field) {
	return field ? `${fn}_${field}` : fn;
}
function toDriverAggregate(select) {
	const field = select.field;
	return {
		fn: select.fn,
		field,
		alias: aggregateAlias(select.fn, field)
	};
}
/**
* What a client says when its data source cannot aggregate.
*
* A stub rather than a fallback that fetches and reduces in JavaScript: that
* would be wrong under a `limit` and unaffordable without one, and it would look
* like it had worked.
*/
var noAggregate = (slug) => `Aggregates are not available for "${slug}": its data source does not implement them.`;
function createPrimaryKeyResolver(options) {
	const cache = /* @__PURE__ */ new Map();
	const warned = /* @__PURE__ */ new Set();
	return function primaryKeysFor(slug) {
		const cached = cache.get(slug);
		if (cached) return cached;
		const collection = options?.resolveCollection?.(slug);
		if (!collection) return [];
		const keys = resolvePrimaryKeys(collection);
		if (keys.length > 0) {
			cache.set(slug, keys);
			return keys;
		}
		if (!warned.has(slug)) {
			warned.add(slug);
			console.warn(`[rebase] Collection '${slug}' declares no primary key, so its rows have no address: detail links, caching and relations will not work for it. Mark the key property with \`isId\` in its collection config — the server logs which column to mark at boot, if its schema knows the key.`);
		}
		return keys;
	};
}
/**
* Build the admin's view model out of the row the wire serves.
*
* The wire has ONE shape, for every consumer: flat columns, typed the way the
* database typed them, and a relation rendered as the target's own columns (or
* only its foreign key, when nothing asked for it). That is the REST contract,
* what `find()` returns, what `listen()` pushes, and what the generated types
* describe.
*
* The admin renders neither of those directly. Its date field requires a real
* `Date` and rejects a string outright; its relation cells read `.data.values`
* off a relation ref. Those requirements are the *admin's*, so they are met
* here — in the browser, from the collection config the panel already has —
* rather than by asking the server for a second wire shape.
*
* That second shape is what this replaces. Until 2026-09-09 the realtime wire
* carried the view model and every other read carried flat rows, so `find()`
* and `listen()` answered one query two ways; unifying the wire without doing
* this conversion is what left every date cell reading "Invalid date value"
* and every relation cell "Unexpected value".
*
* Values already in view-model form pass through untouched: a driver that
* still sends `{ __type: "date" }` or a relation ref (the client revives both)
* is served by the same walk.
*/
function toViewModelValues(values, properties, collection, resolveCollection) {
	if (!properties) return values;
	const relations = collection ? resolveCollectionRelations(collection) : {};
	let out;
	const write = (key, value) => {
		out = out ?? { ...values };
		out[key] = value;
	};
	for (const [key, rawProperty] of Object.entries(properties)) {
		const property = rawProperty;
		if (!property) continue;
		if (!(key in values)) {
			const fkRelation = relations[key];
			const column = fkRelation && "localKey" in fkRelation ? fkRelation.localKey : void 0;
			const fk = column !== void 0 ? values[column] ?? values[toWireKey(column)] : void 0;
			const fkTarget = fkRelation?.targetSlug;
			if (fkTarget && (typeof fk === "string" || typeof fk === "number")) write(key, new EntityRelation(fk, fkTarget));
			continue;
		}
		const value = values[key];
		if (value === null || value === void 0) continue;
		const relation = relations[key];
		if (relation && (property.type === "relation" || property.of?.type === "relation" || property.type === "array")) {
			const target = relation.targetSlug;
			if (!target) continue;
			const targetProperties = resolveCollection?.(target)?.properties;
			const targetCollection = resolveCollection?.(target);
			const toRef = (item) => {
				if (item instanceof EntityRelation) return item;
				if (typeof item === "object" && item !== null && "__type" in item) return item;
				if (typeof item === "object" && item !== null) {
					const row = item;
					const keys = targetCollection ? resolvePrimaryKeys(targetCollection) : [];
					const id = keys.length > 0 ? buildCompositeId(row, keys) : row.id;
					if (id === void 0 || id === null || id === "") return item;
					return new EntityRelation(id, target, {
						id,
						path: target,
						values: toViewModelValues(row, targetProperties, targetCollection, resolveCollection)
					});
				}
				if (typeof item === "string" || typeof item === "number") return new EntityRelation(item, target);
				return item;
			};
			write(key, Array.isArray(value) ? value.map(toRef) : toRef(value));
			continue;
		}
		if (property.type === "date" && !(value instanceof Date)) {
			if (typeof value === "string" || typeof value === "number") {
				const date = new Date(value);
				write(key, isNaN(date.getTime()) ? null : date);
			}
			continue;
		}
		if (property.type === "map" && property.properties && typeof value === "object" && !Array.isArray(value)) write(key, toViewModelValues(value, property.properties, void 0, resolveCollection));
	}
	return out ?? values;
}
/**
* Give a flat row the Entity view-model the admin renders.
*
* The address is *derived here* — it is not a column, and the row it came from
* does not contain one. Rows carry exactly what the table has, with the types
* Postgres returned; the id is this layer's invention, and this is the only
* place it is minted.
*
* `primaryKeys` empty falls back to a literal `id` on the row: drivers other
* than postgres still serve rows with one, and this keeps them working.
*/
function rowToEntity(row, slug, primaryKeys = [], toViewModel) {
	const { _matches, ...values } = row;
	return {
		id: primaryKeys.length > 0 ? buildCompositeId(row, primaryKeys) : row.id,
		path: slug,
		values: toViewModel ? toViewModel(values) : values,
		..._matches ? { searchMatches: _matches } : {}
	};
}
/**
* The relation envelope `toFlatRow` writes where a relation was:
* `{ id, path, __type: "relation", data: { id, path, values } }`. It is the
* admin's view-model, and the only pipeline that produces one is postgres'.
*/
function isRelationEnvelope(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value) && value.__type === "relation";
}
/** The target's own columns, as `toRestRow` would have inlined them. */
function inlineEnvelope(envelope) {
	return envelope.data?.values ?? {};
}
/**
* Replace every relation envelope on a row with the target's flat columns.
*
* The SDK serves one relation shape — the inlined one (see
* {@link RestFetchService}) — and Postgres now serves it on every read, so
* against that driver this walk finds nothing to do. It stays for the drivers
* whose own `fetchCollection` still answers with refs: a developer reading
* through this accessor gets one shape whichever driver is underneath.
*
* Only applied where the REST pipeline is the contract (see `find`); a driver
* without a `restFetchService` keeps whatever it returns.
*
* Note this is NOT how the admin gets its view model — that is built in the
* browser by {@link toViewModelValues}, from the same flat row.
*/
function inlineRelationRefs(row) {
	let out;
	for (const [key, value] of Object.entries(row)) if (isRelationEnvelope(value)) {
		out = out ?? { ...row };
		out[key] = inlineEnvelope(value);
	} else if (Array.isArray(value) && value.some(isRelationEnvelope)) {
		out = out ?? { ...row };
		out[key] = value.map((item) => isRelationEnvelope(item) ? inlineEnvelope(item) : item);
	}
	return out ?? row;
}
function createDriverAccessor(driver, slug, getPks = () => [], toViewModel) {
	const accessor = {
		async find(params) {
			const filter = params?.where ? deserializeFilter(params.where) : void 0;
			const { limit, offset, driverOffset } = resolveFindWindow(params);
			const cursor = params?.after ? decodeCursor(params.after) : void 0;
			const orderBy = cursor ? reconcileCursorOrder(cursor, normalizeOrderBy(params?.orderBy)) : normalizeOrderBy(params?.orderBy);
			const startAfter = cursor ? cursorToStartAfter(cursor) : void 0;
			const probeLimit = startAfter ? limit + 1 : limit;
			const fetchService = driver.restFetchService;
			const fetched = fetchService ? await fetchService.fetchCollectionForRest(slug, {
				filter,
				logical: params?.logical,
				limit: probeLimit,
				offset: startAfter ? void 0 : driverOffset,
				startAfter,
				orderBy,
				searchString: params?.searchString,
				fields: params?.fields,
				distinct: params?.distinct
			}, params?.include) : await driver.fetchCollection({
				path: slug,
				limit: probeLimit,
				offset: startAfter ? void 0 : driverOffset,
				startAfter,
				filter,
				logical: params?.logical,
				orderBy,
				searchString: params?.searchString,
				include: params?.include,
				fields: params?.fields,
				distinct: params?.distinct
			});
			const seeking = startAfter !== void 0;
			const rows = seeking ? fetched.slice(0, limit) : fetched;
			let total = rows.length + offset;
			let hasMore = seeking ? fetched.length > limit : rows.length >= limit;
			if (driver.count) {
				total = await driver.count({
					path: slug,
					filter,
					logical: params?.logical,
					searchString: params?.searchString
				});
				if (!seeking) hasMore = offset + rows.length < total;
			}
			const last = rows[rows.length - 1];
			const nextCursor = hasMore && last && driver.restFetchService?.cursorFor ? driver.restFetchService.cursorFor(slug, last, orderBy) : void 0;
			return {
				data: rows.map((row) => rowToEntity(row, slug, getPks(), toViewModel)),
				meta: {
					total,
					limit,
					offset,
					hasMore,
					...nextCursor && { nextCursor }
				}
			};
		},
		async findById(id) {
			const fetchService = driver.restFetchService;
			const row = fetchService ? await fetchService.fetchOneForRest(slug, id) : await driver.fetchOne({
				path: slug,
				id
			});
			return row ? rowToEntity(row, slug, getPks(), toViewModel) : void 0;
		},
		aggregate: driver.restFetchService?.aggregate ? async (params) => driver.restFetchService.aggregate(slug, {
			aggregates: params.select.map(toDriverAggregate),
			groupBy: params.groupBy,
			filter: params.where ? deserializeFilter(params.where) : void 0,
			logical: params.logical,
			searchString: params.searchString,
			limit: params.limit
		}) : void 0,
		async create(data, id) {
			return rowToEntity(await driver.save({
				path: slug,
				values: data,
				id,
				status: "new"
			}), slug, getPks(), toViewModel);
		},
		createMany: driver.saveMany ? async (data, options) => {
			return (await driver.saveMany({
				path: slug,
				rows: data,
				upsert: options?.upsert,
				onConflict: options?.onConflict
			})).map((row) => rowToEntity(row, slug, getPks(), toViewModel));
		} : void 0,
		async update(id, data) {
			return rowToEntity(await driver.save({
				path: slug,
				values: data,
				id,
				status: "existing"
			}), slug, getPks(), toViewModel);
		},
		async delete(id) {
			return driver.delete({ row: {
				id,
				path: slug
			} });
		},
		updateMany: driver.updateMany ? async (updates) => {
			return (await driver.updateMany({
				path: slug,
				updates: updates.map((u) => ({
					id: u.id,
					values: u.data
				}))
			})).map((row) => rowToEntity(row, slug, getPks(), toViewModel));
		} : void 0,
		deleteMany: driver.deleteMany ? async (ids) => {
			await driver.deleteMany({
				path: slug,
				ids
			});
		} : void 0,
		count: driver.count ? async (params) => {
			const filter = params?.where ? deserializeFilter(params.where) : void 0;
			return driver.count({
				path: slug,
				filter,
				logical: params?.logical,
				searchString: params?.searchString
			});
		} : void 0,
		listen: driver.listenCollection ? (params, onUpdate, onError) => {
			const { limit, offset, driverOffset } = resolveFindWindow(params);
			const normalize = driver.restFetchService ? inlineRelationRefs : (row) => row;
			return driver.listenCollection({
				path: slug,
				limit,
				offset: driverOffset,
				filter: params?.where,
				logical: params?.logical,
				orderBy: normalizeOrderBy(params?.orderBy),
				searchString: params?.searchString,
				searchExplain: params?.searchExplain,
				vectorSearch: params?.vectorSearch,
				onUpdate: (entities) => {
					onUpdate({
						data: entities.map((row) => rowToEntity(normalize(row), slug, getPks(), toViewModel)),
						meta: {
							total: offset + entities.length,
							limit,
							offset,
							hasMore: entities.length >= limit
						}
					});
				},
				onError
			});
		} : void 0,
		listenById: driver.listenOne ? (id, onUpdate, onError) => {
			const normalize = driver.restFetchService ? inlineRelationRefs : (row) => row;
			return driver.listenOne({
				path: slug,
				id,
				onUpdate: (entity) => onUpdate(entity ? rowToEntity(normalize(entity), slug, getPks(), toViewModel) : void 0),
				onError
			});
		} : void 0,
		where(columnOrCondition, operator, value) {
			const builder = new QueryBuilder(accessor);
			if (typeof columnOrCondition === "object") return builder.where(columnOrCondition);
			return builder.where(columnOrCondition, operator, value);
		},
		orderBy(column, ascending) {
			return new QueryBuilder(accessor).orderBy(column, ascending);
		},
		limit(count) {
			return new QueryBuilder(accessor).limit(count);
		},
		offset(count) {
			return new QueryBuilder(accessor).offset(count);
		},
		search(searchString, options) {
			return new QueryBuilder(accessor).search(searchString, options);
		},
		vectorSearch(property, vector, options) {
			return new QueryBuilder(accessor).vectorSearch(property, vector, options);
		},
		include(...relations) {
			return new QueryBuilder(accessor).include(...relations);
		}
	};
	return accessor;
}
/**
* Build a `RebaseData` object from a `DataDriver` using JavaScript Proxy.
*
* This is the key bridge: any property access like `data.products` returns
* a `CollectionAccessor` backed by the underlying DataDriver, without
* needing per-collection code generation.
*
* @example
* const data = buildRebaseData(driver);
* await data.products.create({ name: "Camera", price: 299 });
* const { data: items } = await data.products.find({ where: { status: ["==", "published"] } });
*/
/**
* The view-model converter for one collection, or `undefined` when there is no
* collection config to build it from.
*
* Absent is the honest answer for every consumer that is not the admin: the
* flat SDK derives itself from this same layer (`buildSdkData`) and must keep
* the wire's own types, and it registers no collection resolver.
*/
function createViewModelConverter(options) {
	if (!options?.resolveCollection) return () => void 0;
	return function converterFor(slug) {
		return (values) => {
			const collection = options.resolveCollection?.(slug);
			if (!collection) return values;
			return toViewModelValues(values, collection.properties, collection, options.resolveCollection);
		};
	};
}
function buildRebaseData(driver, options) {
	const cache = /* @__PURE__ */ new Map();
	const primaryKeysFor = createPrimaryKeyResolver(options);
	const viewModelFor = createViewModelConverter(options);
	function getAccessor(slug) {
		let accessor = cache.get(slug);
		if (!accessor) {
			accessor = createDriverAccessor(driver, slug, () => primaryKeysFor(slug), viewModelFor(slug));
			cache.set(slug, accessor);
		}
		return accessor;
	}
	return new Proxy({ collection: getAccessor }, { get(_target, prop) {
		if (prop === "collection") return getAccessor;
		if (typeof prop === "symbol") return void 0;
		if (prop === "then" || prop === "toJSON" || prop === "$$typeof") return void 0;
		return getAccessor(toSnakeCase(prop));
	} });
}
/**
* Unwrap a Entity back into the flat row it was built from. `rowToEntity` keeps
* the row untouched under `.values` and derives `.id` alongside it, so dropping
* the wrapper is the whole operation — the address was never part of the row.
*/
function entityToRow(entity) {
	return entity.values;
}
/**
* Fluent query builder for the flat SDK data layer. Mirrors {@link QueryBuilder}
* but resolves to `FindResult<M>` (flat rows) instead of Entity-wrapped
* `FindResponse<M>`.
*/
var SdkQueryBuilder = class {
	client;
	params = { where: {} };
	constructor(client) {
		this.client = client;
	}
	where(columnOrCondition, operator, value) {
		if (typeof columnOrCondition === "object" && columnOrCondition !== null && "type" in columnOrCondition) {
			const next = columnOrCondition;
			this.params.logical = this.params.logical ? {
				type: "and",
				conditions: [this.params.logical, next]
			} : next;
			return this;
		}
		if (!this.params.where) this.params.where = {};
		const column = columnOrCondition;
		const condition = [operator, value];
		const existing = this.params.where[column];
		if (existing === void 0) this.params.where[column] = condition;
		else if (Array.isArray(existing) && existing.length > 0 && Array.isArray(existing[0])) this.params.where[column].push(condition);
		else {
			let firstCondition;
			if (Array.isArray(existing) && existing.length === 2 && typeof existing[0] === "string") firstCondition = existing;
			else firstCondition = ["==", existing];
			this.params.where[column] = [firstCondition, condition];
		}
		return this;
	}
	/** Called again, this adds a tie-breaker rather than replacing the sort. */
	orderBy(column, direction = "asc", nulls) {
		const existing = normalizeOrderBy(this.params.orderBy) ?? [];
		const key = sortKeyToString(column);
		this.params.orderBy = [...existing, nulls ? [
			key,
			direction,
			nulls
		] : [key, direction]];
		return this;
	}
	limit(count) {
		this.params.limit = count;
		return this;
	}
	offset(count) {
		this.params.offset = count;
		return this;
	}
	search(searchString, options) {
		this.params.searchString = searchString;
		if (options?.explain !== void 0) this.params.searchExplain = options.explain;
		return this;
	}
	vectorSearch(property, vector, options) {
		this.params.vectorSearch = {
			property,
			vector,
			...options?.distance !== void 0 && { distance: options.distance },
			...options?.threshold !== void 0 && { threshold: options.threshold }
		};
		return this;
	}
	/**
	* Load relations. Merges rather than replaces, so `.include("author")` then
	* `.include({ comments: { limit: 5 } })` asks for both — a builder call that
	* silently discarded an earlier one is the same defect `where` had.
	*/
	include(...relations) {
		this.params.include = mergeIncludeSpecs(this.params.include, relations);
		return this;
	}
	fields(...columns) {
		this.params.fields = [...this.params.fields ?? [], ...columns];
		return this;
	}
	distinct(enabled = true) {
		this.params.distinct = enabled;
		return this;
	}
	after(cursor) {
		this.params.after = cursor;
		return this;
	}
	async find() {
		return this.client.find(this.params);
	}
	/** Aggregate the matching rows. See {@link SDKCollectionClient.aggregate}. */
	async aggregate(params) {
		return this.client.aggregate({
			...params,
			where: this.params.where,
			logical: this.params.logical,
			searchString: this.params.searchString
		});
	}
	/**
	* Page through everything this query matches, one row at a time.
	*
	* `.limit()` on the builder becomes the page size, so the ceiling on a
	* single `find()` is not a ceiling on what the query can read.
	*/
	iterate(options) {
		return this.client.iterate({
			...this.params,
			...this.params.limit !== void 0 && { pageSize: this.params.limit },
			...options
		});
	}
	/** Collect everything this query matches into one array. */
	findAll(options) {
		return this.client.findAll({
			...this.params,
			...this.params.limit !== void 0 && { pageSize: this.params.limit },
			...options
		});
	}
	/**
	* Count the records matching this query.
	*
	* This used to answer `0` when the client had no `count` — a number, from a
	* source that had not counted anything, indistinguishable from an empty
	* collection. It now does what the client does, which on a source that
	* cannot count is throw and say so.
	*/
	async count() {
		return this.client.count(this.params);
	}
	listen(onUpdate, onError) {
		return this.client.listen(this.params, onUpdate, onError);
	}
};
/**
* Wrap a Entity-shaped {@link CollectionAccessor} into a flat
* {@link SDKCollectionClient}. Every returned record is unwrapped to a flat row
* so the backend SDK is byte-for-byte the same shape as the frontend client.
*/
function toSdkCollectionClient(snap, slug = "collection") {
	const client = {
		async find(params) {
			const res = await snap.find(params);
			return {
				data: res.data.map(entityToRow),
				meta: res.meta
			};
		},
		iterate(params) {
			return paginateFind((p) => client.find(p), params, slug);
		},
		findAll(params) {
			return collectAllPages((p) => client.find(p), params, slug);
		},
		async findById(id) {
			const s = await snap.findById(id);
			return s ? entityToRow(s) : void 0;
		},
		async get(id) {
			const s = await snap.findById(id);
			if (!s) throw new RebaseApiError(`No record with id ${JSON.stringify(String(id))} in "${slug}".`, {
				status: 404,
				code: "NOT_FOUND"
			});
			return entityToRow(s);
		},
		async create(data, id) {
			return entityToRow(await snap.create(data, id));
		},
		async createMany(data, options) {
			if (!Array.isArray(data)) throw new TypeError("createMany expects an array of records.");
			if (data.length === 0) return [];
			if (!snap.createMany) throw new Error("Bulk writes are not supported by this collection's data source. Fall back to create() per record.");
			return (await snap.createMany(data, options)).map(entityToRow);
		},
		/**
		* One row through the bulk path, because the bulk path is where the
		* conflict target lives.
		*
		* `CollectionAccessor` has no single-row upsert and adding one would
		* mean a second way to say the same thing to the same driver method —
		* `saveMany` already takes `upsert` and `onConflict`, and a batch of
		* one is exactly an upsert of one.
		*/
		async upsert(data, options) {
			if (!snap.createMany) throw new Error("Upsert is not supported by this collection's data source: it needs a bulk write, which this driver does not implement. Fall back to create() or update().");
			const row = (await snap.createMany([data], {
				upsert: true,
				onConflict: options?.onConflict
			}))[0];
			if (!row) throw new Error(`Upsert into "${slug}" returned no row.`);
			return entityToRow(row);
		},
		async update(id, data) {
			return entityToRow(await snap.update(id, data));
		},
		async updateMany(updates) {
			if (!Array.isArray(updates)) throw new TypeError("updateMany expects an array of { id, data } entries.");
			if (updates.length === 0) return [];
			if (!snap.updateMany) throw new Error("Bulk updates are not supported by this collection's data source. Fall back to update() per record.");
			return (await snap.updateMany(updates.map((u) => ({
				id: u.id,
				data: u.data
			})))).map(entityToRow);
		},
		delete(id) {
			return snap.delete(id);
		},
		async deleteMany(ids) {
			if (!Array.isArray(ids)) throw new TypeError("deleteMany expects an array of ids.");
			if (ids.length === 0) return;
			if (!snap.deleteMany) throw new Error("Bulk deletes are not supported by this collection's data source. Fall back to delete() per record.");
			await snap.deleteMany(ids);
		},
		count: snap.count ? (params) => snap.count(params) : unsupportedMethod(noCount(slug)),
		listen: snap.listen ? (params, onUpdate, onError) => snap.listen(params, (res) => onUpdate({
			data: res.data.map(entityToRow),
			meta: res.meta
		}), onError) : unsupportedMethod(noRealtime(slug)),
		listenById: snap.listenById ? (id, onUpdate, onError) => snap.listenById(id, (s) => onUpdate(s ? entityToRow(s) : void 0), onError) : unsupportedMethod(noRealtime(slug)),
		where(columnOrCondition, operator, value) {
			const builder = new SdkQueryBuilder(client);
			if (typeof columnOrCondition === "object") return builder.where(columnOrCondition);
			return builder.where(columnOrCondition, operator, value);
		},
		orderBy: (column, direction, nulls) => new SdkQueryBuilder(client).orderBy(column, direction, nulls),
		limit: (count) => new SdkQueryBuilder(client).limit(count),
		offset: (count) => new SdkQueryBuilder(client).offset(count),
		search: (searchString) => new SdkQueryBuilder(client).search(searchString),
		vectorSearch: (property, vector, options) => new SdkQueryBuilder(client).vectorSearch(property, vector, options),
		include: (...relations) => new SdkQueryBuilder(client).include(...relations),
		fields: (...columns) => new SdkQueryBuilder(client).fields(...columns),
		distinct: (enabled) => new SdkQueryBuilder(client).distinct(enabled),
		after: (cursor) => new SdkQueryBuilder(client).after(cursor),
		aggregate: snap.aggregate ? (params) => snap.aggregate(params) : unsupportedMethod(noAggregate(slug))
	};
	return client;
}
/**
* Wrap a flat {@link SDKCollectionClient} into a Entity-shaped
* {@link CollectionAccessor}. Every returned row is re-wrapped into the
* `{ id, path, values }` view-model the admin panel renders.
*/
function toEntityAccessor(sdk, slug, getPks = () => [], toViewModel) {
	const accessor = {
		async find(params) {
			const res = await sdk.find(params);
			return {
				data: res.data.map((row) => rowToEntity(row, slug, getPks(), toViewModel)),
				meta: res.meta
			};
		},
		async findById(id) {
			const row = await sdk.findById(id);
			return row ? rowToEntity(row, slug, getPks(), toViewModel) : void 0;
		},
		async create(data, id) {
			return rowToEntity(await sdk.create(data, id), slug, getPks(), toViewModel);
		},
		createMany: sdk.createMany ? async (data, options) => {
			return (await sdk.createMany(data, options)).map((row) => rowToEntity(row, slug, getPks(), toViewModel));
		} : void 0,
		async update(id, data) {
			const row = await sdk.update(id, data);
			if (!row) throw new Error(`Update returned no data for id ${id}`);
			return rowToEntity(row, slug, getPks(), toViewModel);
		},
		delete(id) {
			return sdk.delete(id);
		},
		count: isUnsupported(sdk.count) ? void 0 : (params) => sdk.count(params),
		aggregate: isUnsupported(sdk.aggregate) ? void 0 : (params) => sdk.aggregate(params),
		listen: isUnsupported(sdk.listen) ? void 0 : (params, onUpdate, onError) => sdk.listen(params, (res) => onUpdate({
			data: res.data.map((row) => rowToEntity(row, slug, getPks(), toViewModel)),
			meta: res.meta
		}), onError),
		listenById: isUnsupported(sdk.listenById) ? void 0 : (id, onUpdate, onError) => sdk.listenById(id, (row) => onUpdate(row ? rowToEntity(row, slug, getPks(), toViewModel) : void 0), onError),
		where(columnOrCondition, operator, value) {
			const builder = new QueryBuilder(accessor);
			if (typeof columnOrCondition === "object") return builder.where(columnOrCondition);
			return builder.where(columnOrCondition, operator, value);
		},
		orderBy: (column, direction) => new QueryBuilder(accessor).orderBy(column, direction),
		limit: (count) => new QueryBuilder(accessor).limit(count),
		offset: (count) => new QueryBuilder(accessor).offset(count),
		search: (searchString) => new QueryBuilder(accessor).search(searchString),
		vectorSearch: (property, vector, options) => new QueryBuilder(accessor).vectorSearch(property, vector, options),
		include: (...relations) => new QueryBuilder(accessor).include(...relations)
	};
	return accessor;
}
/**
* Wrap a flat {@link RebaseSdkData} into a Entity-shaped {@link RebaseData}.
*
* This is the **admin boundary**: the SDK client (`client.data`) returns flat
* rows, but the admin renders the `Entity` view-model (`entity.values.*`).
* `core/Rebase.tsx` wraps `client.data` through this before handing it to the
* admin `RebaseDataContext` — without it the admin renders rows with only their
* `id`.
*/
/**
* Only the by-slug accessor is asked for, so only that is required.
*
* Taking a whole `RebaseSdkData` meant taking `RebaseSdkData<unknown>`, whose
* dynamic branch is an index signature — and no `RebaseSdkData<DB>` satisfies
* it, because its own `collection` method is not a `SDKCollectionClient`. So a
* caller holding a *typed* client could not pass it to a function that reads
* one method off it, and that method is identical on every instantiation.
*/
function wrapAsEntityData(sdkData, options) {
	const cache = /* @__PURE__ */ new Map();
	const primaryKeysFor = createPrimaryKeyResolver(options);
	const viewModelFor = createViewModelConverter(options);
	function getAccessor(slug) {
		let accessor = cache.get(slug);
		if (!accessor) {
			accessor = toEntityAccessor(sdkData.collection(slug), slug, () => primaryKeysFor(slug), viewModelFor(slug));
			cache.set(slug, accessor);
		}
		return accessor;
	}
	return new Proxy({ collection: getAccessor }, { get(_target, prop) {
		if (prop === "collection") return getAccessor;
		if (typeof prop === "symbol") return void 0;
		if (prop === "then" || prop === "toJSON" || prop === "$$typeof") return void 0;
		return getAccessor(toSnakeCase(prop));
	} });
}
/**
* Wrap a Entity-shaped {@link RebaseData} into a flat {@link RebaseSdkData}.
*
* Every collection accessor is adapted to return flat rows. Use this to derive
* the flat SDK data layer (`context.data`) from an existing Entity data layer
* — e.g. the admin routes its Entity data via `useData()` and exposes the
* same routing as flat `context.data` for callbacks by wrapping it here.
*/
function wrapAsSdkData(entityData) {
	const cache = /* @__PURE__ */ new Map();
	function getAccessor(slug) {
		let accessor = cache.get(slug);
		if (!accessor) {
			accessor = toSdkCollectionClient(entityData.collection(slug), slug);
			cache.set(slug, accessor);
		}
		return accessor;
	}
	return new Proxy({ collection: getAccessor }, { get(_target, prop) {
		if (prop === "collection") return getAccessor;
		if (typeof prop === "symbol") return void 0;
		if (prop === "then" || prop === "toJSON" || prop === "$$typeof") return void 0;
		return getAccessor(toSnakeCase(prop));
	} });
}
/**
* Build a flat {@link RebaseSdkData} from a `DataDriver`.
*
* This is the developer-facing SDK data layer used by backend framework
* callbacks & scripts (`context.data` / `rebase.dataAsAdmin`). It returns flat rows —
* identical in shape to the frontend SDK client, down to how a relation is
* served: a foreign key stays a foreign key, and a relation named in `include`
* arrives as the target's own columns. The `{ __type: "relation" }` envelope is
* the admin's view-model and never reaches here.
*
* The admin uses {@link buildRebaseData} (Entity) over its own driver.
*/
function buildSdkData(driver) {
	return wrapAsSdkData(buildRebaseData(driver));
}
//#endregion
//#region src/data/buildRoutedRebaseData.ts
/**
* Build a {@link RebaseData} that routes each collection to the right
* backend based on its resolved data source.
*
* `.collection(path)` (and dynamic `data.products`-style access) resolves the
* collection's data-source key via `resolveKey` and delegates to the matching
* entry in `sources`, falling back to `defaultData` when there is no match.
* Because routing keys off the *path being accessed*, a reference widget
* inside a Firestore form that points at a Postgres collection is still
* served by Postgres — routing follows the target, not the ancestor.
*
* When `sources` is empty this returns `defaultData` untouched, so the
* single-driver setup keeps identical behaviour and identity (important for
* effect dependencies that key off the data instance).
*
* @example
* const data = buildRoutedRebaseData({
*     defaultData: client.data,
*     sources: { analytics: buildRebaseData(firestoreDriver) },
*     resolveKey: (path) => resolveDataSource(registry.getCollection(path), defs).key
* });
* await data.products.find();        // → default (server / Postgres)
* await data.events.find();          // → Firestore, if `events.dataSource === "analytics"`
*/
function buildRoutedRebaseData({ defaultData, sources, resolveKey }) {
	if (!sources || Object.keys(sources).length === 0) return defaultData;
	function resolve(slugOrPath) {
		const key = resolveKey(slugOrPath);
		if (key && sources[key]) return sources[key];
		return defaultData;
	}
	function getAccessor(slugOrPath) {
		return resolve(slugOrPath).collection(slugOrPath);
	}
	return new Proxy({ collection: getAccessor }, { get(_target, prop) {
		if (prop === "collection") return getAccessor;
		if (typeof prop === "symbol") return void 0;
		if (prop === "then" || prop === "toJSON" || prop === "$$typeof") return void 0;
		return getAccessor(toSnakeCase(prop));
	} });
}
//#endregion
//#region src/data/filter-conditions.ts
/**
* Read one field's filter as the list of conditions it stands for.
*
* Accepts both declared shapes and normalises them to a list:
*
* ```ts
* toFilterTuples(["==", "active"])          // [["==", "active"]]
* toFilterTuples([[">=", 18], ["<", 65]])   // [[">=", 18], ["<", 65]]
* ```
*
* A falsy, non-array or empty param has no conditions in it — the empty list,
* so a caller iterating adds nothing rather than compiling a tuple of
* `undefined`s and logging about an operator nobody sent.
*/
function toFilterTuples(filterParam) {
	if (!filterParam || !Array.isArray(filterParam) || filterParam.length === 0) return [];
	if (Array.isArray(filterParam[0])) return filterParam;
	return [filterParam];
}
//#endregion
//#region src/table-classification.ts
/**
* Table Classification
*
* Shared constants and pure functions for classifying database tables.
* Used by both the server-side PostgresBackendDriver and the Studio RLS editor.
*/
/** Schemas that are always considered Rebase-internal. */
var REBASE_INTERNAL_SCHEMAS = ["rebase", "auth"];
/** Table-name prefixes that mark a table as Rebase-internal regardless of schema. */
var REBASE_INTERNAL_PREFIXES = [
	"_rebase_",
	"_auth_",
	"drizzle_"
];
/**
* Synchronously classify a table based on naming conventions.
*
* @param tableName  - The unqualified name of the table.
* @param schemaName - The schema the table belongs to (e.g. `"public"`, `"rebase"`).
* @returns `"rebase-internal"` when the table belongs to a reserved schema or
*          carries a reserved prefix; `"user"` otherwise.
*
* @remarks
* Junction-table detection requires an async database query and is therefore
* **not** handled by this function. Use {@link detectJunctionTables} to obtain
* the set of junction tables, then reclassify as needed.
*/
/** The schema Rebase puts both its own tables and the tenant's collections in. */
var REBASE_SCHEMA = "rebase";
/**
* Rebase's own tables, by unqualified name.
*
* Reused from `util/internal-tables`, which maintains it for the privilege
* revoke — rather than a second list here, which would be a second thing to
* keep true. It already excludes `users` for the reason that matters to this
* function too: the auth user table is also a collection, with RLS and
* policies, and hiding it would hide the one table almost every policy
* references.
*
* That list is deliberately willing to claim common nouns — `jobs`, `branches`,
* `api_keys` — because its own caller re-checks each against `relrowsecurity`.
* This function has no such signal, so the layer above supplies it: a table the
* project MAPS to a collection is classified as the customer's before this is
* consulted at all.
*/
var INTERNAL_TABLE_NAMES = new Set(REBASE_INTERNAL_TABLES);
function classifyTable(tableName, schemaName) {
	if (REBASE_INTERNAL_PREFIXES.some((prefix) => tableName.startsWith(prefix))) return "rebase-internal";
	if (schemaName === REBASE_SCHEMA) return INTERNAL_TABLE_NAMES.has(tableName) ? "rebase-internal" : "user";
	if (REBASE_INTERNAL_SCHEMAS.includes(schemaName)) return "rebase-internal";
	return "user";
}
/**
* Convenience predicate that checks whether a table is Rebase-internal.
*
* @param tableName  - The unqualified name of the table.
* @param schemaName - The schema the table belongs to.
* @returns `true` if the table is classified as `"rebase-internal"`.
*/
function isRebaseInternalTable(tableName, schemaName) {
	return classifyTable(tableName, schemaName) === "rebase-internal";
}
/** SQL query that detects junction tables in the `public` schema. */
var JUNCTION_TABLES_SQL = `
  SELECT t.table_name
  FROM information_schema.tables t
  WHERE t.table_schema = 'public'
    AND t.table_type = 'BASE TABLE'
    AND NOT EXISTS (
      SELECT 1
      FROM information_schema.columns c
      WHERE c.table_schema = t.table_schema
        AND c.table_name = t.table_name
        AND c.column_name NOT IN (
          SELECT kcu.column_name
          FROM information_schema.key_column_usage kcu
          JOIN information_schema.table_constraints tc
            ON tc.constraint_name = kcu.constraint_name
            AND tc.table_schema = kcu.table_schema
          WHERE tc.constraint_type = 'FOREIGN KEY'
            AND kcu.table_schema = t.table_schema
            AND kcu.table_name = t.table_name
        )
    )
`;
/**
* Asynchronously detect junction (link) tables in the `public` schema.
*
* A junction table is defined as a table where **every** column participates in
* at least one foreign-key constraint.
*
* @param executeSql - A callback that executes a raw SQL string and returns the
*                     resulting rows.
* @returns A `Set` containing the names of all detected junction tables.
*/
async function detectJunctionTables(executeSql) {
	const rows = await executeSql(JUNCTION_TABLES_SQL);
	const junctionTables = /* @__PURE__ */ new Set();
	for (const row of rows) if (typeof row.table_name === "string") junctionTables.add(row.table_name);
	return junctionTables;
}
//#endregion
export { ADMIN_ROLE, CALLBACK_REJECTED, COLLECTION_PATH_SEPARATOR, COMPOSITE_ID_SEPARATOR, CollectionRegistry, CursorError, CursorMismatchError, DEFAULT_FIND_ALL_MAX_ROWS, DEFAULT_MAX_PAGES, DEFAULT_ONE_OF_TYPE, DEFAULT_ONE_OF_VALUE, DEFAULT_PAGE_SIZE, DEFAULT_STRING_COLUMN_LENGTH, IncludeSpecError, JUNCTION_TABLES_SQL, MAX_LOGICAL_NESTING_DEPTH, OrderBySpecError, QueryBuilder, REBASE_INTERNAL_PREFIXES, REBASE_INTERNAL_SCHEMAS, REBASE_INTERNAL_TABLES, REBASE_USER_ROLE, RebasePaginationError, TENANT_INDEX_REASON, UnknownFilterOperatorError, aggregateAlias, and, applyDefaultValuesOnCreate, assertBeforeQueryIsPostgresOnly, authSecretsMissingExclusion, buildCollectionFromTableMetadata, buildCompositeId, buildConditionContext, buildPropertyCallbacks, buildRebaseData, buildRoutedRebaseData, buildSdkData, buildTenantSecurityRule, callbackRefusal, canCreateEntity, canDeleteEntity, canEditEntity, canReadCollection, canReadField, canWriteField, checkOperation, classifyTable, collectAllPages, cond, createDataSourceRegistry, createPaginationHelpers, createRelationRef, createRelationRefWithData, cursorToStartAfter, decodeCursor, defaultUsersCollection, defineCollection, denormalizeInclude, deserializeFilter, deserializeInclude, deserializeLogicalCondition, deserializeOrderBy, deserializeOrderByList, detectJunctionTables, effectiveAccess, embedParentExpression, encodeCursor, enumToObjectEntries, evaluateCondition, evaluatePolicy, fieldKeyForColumn, findAnonymousGrants, findRelation, firstSqlRow, fullPathToCollectionSegments, getArrayResolvedProperties, getChildViewDeclaringProperties, getChildViewRelationPropertyKeys, getColumnName, getDeclaredPrimaryKeys, getDefaultValueFor, getDefaultValueFortype, getDefaultValuesFor, getEffectiveSecurityRules, getEntityChildViews, getEnumVarName, getGeneratedPolicyNames, getInjectedSecurityRules, getJunctionCollectionConfig, getJunctionConfigForRelation, getJunctionSecurityRules, getLabelOrConfigFrom, getPrimaryKeys, getReferenceFrom, getRelationFrom, getRelationTargetPath, getSubcollections, getTableName, getTableVarName, getTenantConfig, hasFieldAccessRules, includePaths, isAddressableId, isJunctionBackedRelation, isPropertyBuilder, isRebaseInternalTable, isRelationRequired, isRelationalCollection, mergeIncludeSpecs, normalizeDriverOrderBy, normalizeEmail, normalizeInclude, normalizeOrderBy, normalizeToEntityRelation, not, or, paginateFind, parseIdValues, parseOrderBySpecStrict, policyToPostgres, primaryOrderBy, reconcileCursorOrder, registerConditionOperations, relationDeclaringProperty, relationalCollections, requireCallbackClient, requireCallbackCollection, resolveArrayProperties, resolveCollectionRelations, resolveDataSource, resolveEnumValues, resolveFindWindow, resolveJunctionSpecs, resolvePrimaryKeys, resolveProperties, resolveProperty, resolvePropertyEnum, resolveRelation, resolveRelationProperty, resolveStorageFilenameString, resolveStoragePathString, resolveStorageSource, resolveStringColumnLength, resolveTenantWrite, restrictedFieldNames, revokeInternalTableAccess, revokeInternalTableSql, sanitizeData, securityRuleToConditions, segmentsToStrippedPath, serializeFilter, serializeInclude, serializeLogicalCondition, serializeOrderBy, sortCollectionsBySlug, sortProperties, sqlRows, sqlToPolicy, stripCollectionPath, tenantBypassRoles, tenantPolicyName, tenantScopeExpression, toCallbackError, toFilterTuples, topLevelIncludeNames, traverseValueProperty, traverseValuesProperties, updateDateAutoValues, updateUserAutoValues, wrapAsEntityData, wrapAsSdkData };

//# sourceMappingURL=index.es.js.map